import { pageLocation } from "@/services/pageLocation";
import {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useLayoutEffect,
  type CSSProperties,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosDelete, axiosGet, axiosPost } from "../services/axiosService";
import { variableParser } from "../services/parserService";
import { BotComponent, UserComponent } from "./ChatComponent";
import env from "../services/environment";
import "../services/firebaseService";
import "../style/preview.css";
import { BotIconOld, WhiteBotIcon, OutgrowBotIcon } from "./Icons";
import { deleteCookies, setCookie, getCookie } from "../services/cookieService";
import { io } from "socket.io-client";
import { nanoid } from "nanoid";
import { useCalendlyEventListener } from "react-calendly";
import getTextColor from "@/utils/getTextColor"; // cool
import { useRTLDirection } from "@/hooks/useRTLDirection";
import { produce } from "immer";
import _ from "lodash";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "@/style/swipper.css";
import trackError, { trackFrontendError } from "@/utils/track-error";
import {
  ACCEPT_MESSAGE,
  COMPOSE_MESSAGE_PLACEHOLDER,
  DECLINE_MESSAGE,
  EMAIL_VALIDATION_MESSAGE,
  ERROR_529,
  ERROR_CONNECTION_ISSUE,
  QUESTION_LIMIT_REACHED_MESSAGE,
  RETRY_LABEL,
  // SOMETHING_WENT_WRONG_MESSAGE,
  TAKING_LONGER_THAN_EXPECTED_MESSAGE,
  WAIT_BEFORE_TRYING_MESSAGE,
} from "@/language.constants";
import { removeCustomTags, stripHTML } from "@/utils/renderUtility";
import ProtectedBranding from "./ProtectedBranding";
import { useReasoningManager } from "@/hooks/useReasoningManager";
import deriveShade from "@/utils/deriveShade";
import { useParentWindowWidth } from "@/hooks/useParentWindowWidth";
import getColorWithAlpha from "@/utils/getColorWithAlpha";
import { COMPARE_MODE } from "@/utils/compareMode";
import { applyImageFallback } from "@/utils/brandingImageFallback";
import axios from "axios";
import { Tooltip } from "./common/Tooltip";
import { sanitizeBrandingCss } from "@/utils/sanitizeBrandingCss";

declare global {
  interface Window {
    loadGTM: () => void;
    loadGTMDirect: () => void;
    gtmLoaded: boolean;
    google_tag_manager: any;
    dataLayer: any[];
    cbbSettings: {
      base_url: string;
      app_id: string;
      app_type: string;
    };
    cbbConsentHandlers: {
      accept: () => void;
      decline: () => void;
      loadGTM: () => void;
    };
  }
}

const PREDEFINED_VARIABLES = [
  "location",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
];
const STACKED_COMPOSER_INSET = 15;
const TEXTAREA_SCROLLBAR_WIDTH = 5;
const TEXTAREA_SCROLLBAR_GAP = 5;
const TEXTAREA_OVERFLOW_INSET =
  TEXTAREA_SCROLLBAR_WIDTH + TEXTAREA_SCROLLBAR_GAP;
const environmentData = env();
const socket = io(environmentData.SERVER_API_URL + "/live-calc", {
  transports: ["websocket"],
  autoConnect: false,
});

// Chat count is stored in localStorage so it survives inside a cross-site embed
// iframe, where third-party cookie writes are blocked. Cookie is read as a
// fallback for sessions that counted before this change.
const getAiChatCount = (botId: string) => {
  let stored: string | null = null;
  try {
    stored = window.localStorage?.getItem(`chat_${botId}`) ?? null;
  } catch {
    stored = null;
  }

  const count = Number(stored ?? getCookie(`chat_${botId}`));
  return Number.isFinite(count) ? count : 0;
};

const setAiChatCount = (botId: string, count: number) => {
  try {
    window.localStorage?.setItem(`chat_${botId}`, String(count));
  } catch {
    // localStorage can throw in private mode or when storage is blocked
  }

  const expires = new Date(Date.now());
  expires.setUTCHours(expires.getUTCHours() + 1, 0, 0, 0);
  document.cookie = `chat_${botId}=${count}; expires=${expires.toUTCString()}; path=/; SameSite=None; Secure`;
};

const hasReachedQuestionLimit = (aiChatCount: number, usageLimit: unknown) => {
  const normalizedUsageLimit = Number(usageLimit);
  return (
    Number.isFinite(normalizedUsageLimit) && aiChatCount >= normalizedUsageLimit
  );
};

type FrontendQueryTransportError = Error & {
  statusCode?: number;
  requestUrl?: string;
  code?: string;
};
const STANDALONE_CUSTOM_CSS_ID = "cbb-standalone-custom-css";

const SELECT_MODEL_DROPDOWN_MESSAGE = "Please select a model in the dropdown.";

function getErrorText(error: unknown) {
  if (typeof error === "string") return error;

  const errorData = (error as any)?.response?.data;
  if (typeof errorData === "string") return errorData;
  if (errorData) {
    try {
      return JSON.stringify(errorData);
    } catch {
      return "";
    }
  }

  if (error instanceof Error) return error.message;

  try {
    return JSON.stringify(error);
  } catch {
    return "";
  }
}

function isMissingModelValidationError(error: unknown) {
  const errorText = getErrorText(error);
  return (
    errorText.includes('"model" must be a string') ||
    errorText.includes("'model' must be a string")
  );
}

function isMissingModelSelection(model: unknown) {
  return typeof model !== "string" || model.trim().length === 0;
}

const Preview = forwardRef(function (props: any, ref) {
  const {
    embed,
    environment,
    consent,
    setConsent,
    setReadPdf,
    setReadPdfText,
    locationData = {},
    embedData,
    contactData = null,
    setContactData,
    roomId,
    setRoomId,
    showBackBtn,
    isPreviewModeChat,
    creditId,
    variableData,
    fromOutgrow,
    isTrial,
  } = props;
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const { parentWidth, isMobileParent } = useParentWindowWidth();
  const debug = useRef<boolean>(searchParams.has("debug"));
  const steps = useRef<boolean>(searchParams.has("steps"));
  const compareInstanceId = useRef<string>(searchParams.get("_i") || "");

  const saveAnswerPost = (url: string, body: any) => {
    let finalUrl = url;
    if (COMPARE_MODE) {
      finalUrl += "&isPreview=true";
    }
    return axiosPost(finalUrl, body);
  };

  const getActiveRoomId = () => roomId ?? roomID.current ?? "";

  const senderId = useMemo(() => {
    const temp = getCookie("senderId")
      ? getCookie("senderId")
      : String(Math.floor(Math.random() * 1000000));
    return temp;
  }, []);
  // const botData = props?.botData ?? {};tmp
  const [botData, setBotData] = useState<any>(props?.botData ?? {});
  const [userCredential, setUserCredential] = useState({
    id: senderId,
    email: "",
    phone: "",
    name: "",
    salesForce: "",
  });

  const [chatConfig, setChatConfig] = useState<any>(props?.botData?.chatConfig);
  const rtl = useRTLDirection(chatConfig);
  const [questions, setQuestions] = useState<any>([]);
  const [isReplyValid, setIsReplyValid] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [botCustomId, setBotCustomId] = useState("");
  const [botId, setBotId] = useState("");
  const [botName, setBotName] = useState<any>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<any>(null);
  const [buttonVariables, setButtonVariables] = useState<any>([]);
  const [islast, setIsLast] = useState<boolean>(false);
  const [isAnswerSaved, setIsAnswerSaved] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [botType, setBotType] = useState<string>("");
  const [localContactData, setLocalContactData] = useState(contactData);
  const [hasUserSentMessage, setHasUserSentMessage] = useState<boolean>(false);
  const hasUserMessagedRef = useRef<boolean>(false); // Track if user has sent at least one message
  const hasSeededStaticMessagesRef = useRef(false);
  const [message, setMessage] = useState<any>([]);
  // Bumped once when the compare restore window closes, so the outgoing-history
  // effect re-runs and flushes the current message state (a ref flip alone
  // would not re-trigger the effect).
  const [compareRestoreWindowClosedTick, setCompareRestoreWindowClosedTick] =
    useState(0);
  useEffect(() => {
    if (contactData) setLocalContactData(contactData);
  }, [contactData]);

  const [newReply, setNewReply] = useState("");
  const suppressCompareOutgoingRef = useRef(false);
  const suppressCompareClickRef = useRef(false);
  const pendingCompareBroadcastRef = useRef<{
    text: string;
    imageUrl?: string;
  } | null>(null);
  const [compareBroadcastTrigger, setCompareBroadcastTrigger] = useState(0);
  const compareConfigRef = useRef<{
    modelConfig?: {
      modelName?: string;
      temperature?: number;
      thinkingConfig?: { enable?: boolean; mode?: string; level?: string };
      providerOptions?: Record<string, any>;
    };
    customPrompt?: string;
    toolOverridesById?: Array<{ toolId: string; prompt?: string }>;
    calendlyOverridesById?: Array<{ calendlyId: string; prompt?: string }>;
    customGoals?: Record<string, any>;
  } | null>(null);

  const isApplyingRemoteCompareInputRef = useRef(false);
  const compareInputPostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isRestoringCompareHistoryRef = useRef(false);
  const compareHistoryPostTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const compareRestoreWindowOpenRef = useRef(COMPARE_MODE);
  const compareRestoreWindowTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const postCompareMessage = (payload: Record<string, any>) => {
    if (!COMPARE_MODE) return;
    window.parent.postMessage(
      {
        source: "compare",
        instanceId: compareInstanceId.current,
        ...payload,
      },
      "*",
    );
  };

  const broadcastCompareClickAction = (action: Record<string, any>) => {
    if (suppressCompareClickRef.current) return;
    postCompareMessage({ type: "COMPARE_CLICK_ACTION", action });
  };
  const newReplyRef = useRef("");
  const newReplyLast = useRef(newReply);
  const handleUserReplyRef = useRef<(...args: any[]) => Promise<void> | void>(
    () => {},
  );
  const previewRunnerActiveRef = useRef(false);
  const previewRunnerIsPreviewOverrideRef = useRef<boolean | null>(null);
  const lastSourceQuery = useRef("");
  const [responseLoader, setResponseLoader] = useState<boolean>(false);
  const prevResponseLoaderRef = useRef<boolean>(false);

  // Fire CREDIT_CONSUMED to parent whenever the response loader turns off
  useEffect(() => {
    const wasLoading = prevResponseLoaderRef.current;
    prevResponseLoaderRef.current = responseLoader;

    // true → false: loader just turned off, fire the event
    if (wasLoading && !responseLoader && !COMPARE_MODE) {
      console.log("[CREDIT_CONSUMED] postMessage fired to parent");
      window.parent.postMessage({ type: "CREDIT_CONSUMED" }, "*");
    }
  }, [responseLoader]);

  // Track when to show credits: ONLY after user sends a message AND gets a bot response (and loader stops)
  useEffect(() => {
    const totalCredits = Number(
      localContactData?.fieldData?.totalCreditsPurchased || 0,
    );

    // Don't show credits if no credits are configured
    if (totalCredits <= 0) {
      return;
    }

    // Don't show credits while bot is loading/typing
    if (responseLoader) {
      return;
    }

    // Check if there's at least one user message (author === 1)
    const hasUserMsg = message.some(
      (msg: any) => msg.author === 1 && !msg.isStaticPreview,
    );

    if (hasUserMsg) {
      hasUserMessagedRef.current = true;

      // Find the index of the first user message
      const firstUserMsgIndex = message.findIndex(
        (msg: any) => msg.author === 1 && !msg.isStaticPreview,
      );

      // Check if there's a bot response (author === 0) AFTER the first user message
      const hasBotResponseAfterUserMsg = message
        .slice(firstUserMsgIndex + 1)
        .some((msg: any) => msg.author === 0);

      // Only show credits if bot has responded to user's message AND loader has stopped
      if (hasBotResponseAfterUserMsg) {
        setHasUserSentMessage(true);
      }
    }
  }, [
    message,
    localContactData?.fieldData?.totalCreditsPurchased,
    responseLoader,
  ]);
  const hasSessionConversation = useMemo(() => {
    const firstUserMsgIndex = message.findIndex(
      (msg: any) => msg.author === 1 && !msg.isStaticPreview,
    );
    if (firstUserMsgIndex === -1) return false;

    return message
      .slice(firstUserMsgIndex + 1)
      .some((msg: any) => msg.author === 0 && !msg.isLoader);
  }, [message]);
  const [textColor, setTextColor] = useState<any>("");
  const [starterQues, setStarterQues] = useState<any>();
  const [tempStore, setTempStore] = useState<any>();
  const [activeCat, setActiveCat] = useState<any>();
  const [arr, setArr] = useState<any>([]);
  const [arr2, setArr2] = useState<any>([]);
  const [arr3, setArr3] = useState<any>([]);
  const [checkMsg, setCheckMsg] = useState<any>(false);
  const [checkIndex, setCheckIndex] = useState<any>(false);
  const [checkValidate, setCheckValidate] = useState<any>(false);
  const person = 1;
  const ai = 0;
  const [disableInput, setDisableInput] = useState<any>();
  const [isMultiline, setIsMultiline] = useState(false);
  const [hasTextareaScrollbar, setHasTextareaScrollbar] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [hasUserScrolledTextarea, setHasUserScrolledTextarea] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioData, setAudioData] = useState<any>([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [canRetryVoicePermission, setCanRetryVoicePermission] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState<{
    previewUrl: string;
    fileUrl: string;
    name: string;
    isUploading: boolean;
    error?: string;
  } | null>(null);
  const audioRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const pendingImageUploadId = useRef(0);
  const recordingTimerRef = useRef<any | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeTimer = useRef<any>();
  const nudgeTimer3 = useRef<any>();
  const responseTimer = useRef<any>();
  const nudgeTimer2 = useRef<any>();
  const finalMsg = useRef<any>();
  const isSkip = useRef<boolean>(true);
  // Starter welcome currently on screen (see syncStarterWelcome).
  const starterWelcomeRef = useRef<string>("");
  const regenerate = useRef<any>();
  const scrollRef = useRef<HTMLSpanElement>(null);
  const scrollToButtomRef = useRef<HTMLDivElement>(null);
  const [suggestedQuesAns, setSuggestedQuesAns] = useState<boolean>(false);
  finalMsg.current = message[message.length - 1];
  const [startLiveChat, setStartLiveChat] = useState(false);
  const [liveChatNode, setLiveChatNode] = useState<any>();
  const [inhouseAssigneeName, setInhouseAssigneeName] = useState("");
  const [dataSource, setDataSource] = useState<any[]>([]);
  const dataSourceRef = useRef<any[]>([]);
  const handoffData = useRef<{
    isConnected: boolean;
    handoffType: string;
    configId: string;
    payload: Record<string, any>;
  }>({
    isConnected: false,
    handoffType: "",
    configId: "",
    payload: {},
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Stores the true single-line scrollHeight (measured on first paint) so we
  // don't compare against a hardcoded value that ignores CSS padding.
  const singleLineHeightRef = useRef<number>(0);
  // Stores the textarea's clientWidth at the moment text first wraps into multiline.
  // Used by Effect 1 to simulate the narrow layout when checking whether to revert
  // back to single-line mode as the user deletes characters.
  const narrowWidthRef = useRef<number>(0);
  const narrowPaddingLeftRef = useRef<string>("0px");
  const narrowPaddingRightRef = useRef<string>("0px");
  const [calendlyConfig, setCalendlyConfig] = useState<any>({});
  const roomID = useRef<any>();
  const chatHandlerRef = useRef<string>("ai");
  const socketRoomId = useRef(nanoid());
  const [scrapedData, setScrapedData] = useState<Record<string, string>>({});
  const queryRequestSignal = useRef<AbortController>();
  const showRetryHandlerRef = useRef<(data: any) => void>();
  const [chatSummary, setChatSummary] = useState("");
  const [nextChatSummaryStart, setNextChatSummaryStart] = useState(0);
  const [chatSummaryPending, setChatSummaryPending] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const [usingToolLoader, setUsingToolLoader] = useState(false);
  const [showGoToBottom, setShowGoToBottom] = useState(false);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const initialLoadComplete = useRef(false);
  const hasUserScrolled = useRef(false);
  const isAtBottom = useRef(true);
  const bottomObserver = useRef<IntersectionObserver | null>(null);
  const queryRequestId = useRef("");
  const isStreamingRef = useRef(false); // Track if /query streaming is in progress
  const hasReceivedStreamChunk = useRef(false);
  const wasLastRequestAborted = useRef(false); // Track if last /query was cancelled by user
  const pendingToolData = useRef<{ toolCalls: any[]; toolResults: any[] }>({
    toolCalls: [],
    toolResults: [],
  });
  const botComponentRef = useRef<any>();
  const showCalendlyBtn = useRef(false);
  const [responseCacheOptions, setResponseCacheOptions] = useState<{
    id: string;
    preserved: boolean;
    answered?: boolean;
  } | null>(null);
  const creditConsumedNotifiedRef = useRef(false);
  const isMobile =
    typeof window !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const responsive = window.innerWidth <= 768;
  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, []);
  const [borderRadius, setBorderRadius] = useState(100);
  const formattedLocation = useMemo(() => {
    function capitalize(str: string): string {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    if (locationData?.country) {
      return `${capitalize(locationData.city)},${capitalize(locationData.region)},${capitalize(locationData.country)}`;
    }
    return "Not Detected";
  }, [locationData]);
  const reportFrontendQueryError = useCallback(
    ({
      query,
      errorType,
      error,
      statusCode,
      requestUrl = `${environmentData.SERVER_API_URL}/ai/query`,
    }: {
      query: string;
      errorType:
        | "network"
        | "http"
        | "timeout"
        | "empty_response"
        | "fallback_failed";
      error: unknown;
      statusCode?: number;
      requestUrl?: string;
    }) => {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error(
              typeof error === "string" ? error : "Unknown frontend error",
            );
      let rawError: string | undefined;

      if (error && typeof error === "object") {
        try {
          rawError = JSON.stringify(error, Object.getOwnPropertyNames(error));
        } catch (serializationError) {
          console.error(
            "Failed to serialize frontend query error",
            serializationError,
          );
        }
      }

      trackFrontendError({
        source: "frontend",
        surface: "livebot",
        category: "query",
        query,
        errorType,
        tags: ["Frontend ERROR", "frontend", "livebot"],
        request: {
          method: "POST",
          url: requestUrl,
          statusCode,
          queryRequestId: queryRequestId.current,
          hostname: pageLocation().hostname,
          pathname: pageLocation().pathname,
        },
        context: {
          botId: botData?._id,
          botCustomId: botData?.botCustomId ?? botCustomId,
          namespace: botData?.botCustomId ?? botCustomId,
          companyId: botData?.companyId,
          contactId: localContactData?._id ?? contactData?._id,
          userResponseId: roomId ?? roomID.current,
          queryRequestId: queryRequestId.current,
          socketRoomId: socketRoomId.current,
          modelName: botData?.botModel,
          streaming: true,
          includeSource: botData?.showDataSource,
          isPreview: isPreviewModeChat,
          userId: botData?.userId,
          hostname: pageLocation().hostname,
          pathname: pageLocation().pathname,
          fromOutgrow,
          location: formattedLocation,
        },
        error: {
          name: normalizedError.name,
          message: normalizedError.message,
          stack: normalizedError.stack,
          code:
            typeof (normalizedError as any)?.code === "string"
              ? (normalizedError as any).code
              : statusCode !== undefined
                ? String(statusCode)
                : undefined,
          cause:
            typeof normalizedError.cause === "string"
              ? normalizedError.cause
              : null,
          raw: rawError,
        },
      });
    },
    [
      botCustomId,
      botData?._id,
      botData?.botCustomId,
      botData?.botModel,
      botData?.companyId,
      botData?.showDataSource,
      botData?.userId,
      contactData?._id,
      formattedLocation,
      fromOutgrow,
      isPreviewModeChat,
      localContactData?._id,
      roomId,
    ],
  );
  const customGoalsTriggered = useRef(false);
  const customGoalsMsg = useRef("");
  const errorTimeoutRef = useRef<any>(null);
  const pendingErrorMessage = useRef<string>("");

  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownRemainingTime, setCooldownRemainingTime] = useState(0);
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [socketErrorDetail, setSocketErrorDetail] = useState("");
  const [showSocketErrorDetail, setShowSocketErrorDetail] = useState(false);
  const [isCustomProviderError, setIsCustomProviderError] = useState(false);
  const reasoning = useReasoningManager();
  const shouldShowDetailedSocketError = useMemo(() => {
    if (isPreviewModeChat) return true;
    if (typeof window === "undefined") return false;
    return (
      pageLocation().hostname.toLowerCase() === "live-release.outgrow.chat" ||
      pageLocation().hostname.toLowerCase() === "ai.localhost"
    );
  }, [isPreviewModeChat]);
  const defaultConnectionIssueMessage =
    ERROR_CONNECTION_ISSUE[botData?.aiBotConfig?.language?.toLowerCase()] ||
    ERROR_CONNECTION_ISSUE.english;

  const defaultQuestionLimitReachedMessage =
    QUESTION_LIMIT_REACHED_MESSAGE[
      botData?.aiBotConfig?.language?.toLowerCase()
    ] || QUESTION_LIMIT_REACHED_MESSAGE.english;
  const hasSocketErrorDetail =
    isPreviewModeChat && socketErrorDetail.trim().length > 0;
  const previewSocketErrorMessage = hasSocketErrorDetail
    ? defaultConnectionIssueMessage
    : shouldShowDetailedSocketError && cooldownMessage
      ? cooldownMessage
      : defaultConnectionIssueMessage;

  useEffect(() => {
    if (embed || typeof document === "undefined") return;

    const customCSS = chatConfig?.standaloneCustomCSS || "";

    const existingStyle = document.getElementById(STANDALONE_CUSTOM_CSS_ID);
    if (existingStyle) {
      existingStyle.remove();
    }

    if (!customCSS.trim()) return;

    const style = document.createElement("style");
    style.id = STANDALONE_CUSTOM_CSS_ID;
    style.innerHTML = customCSS;
    document.head.appendChild(style);

    setTimeout(() => sanitizeBrandingCss(STANDALONE_CUSTOM_CSS_ID), 30);

    return () => {
      style.remove();
    };
  }, [embed, chatConfig?.standaloneCustomCSS]);

  const loadGTMNow = useCallback(() => {
    console.log("=== LOADING GTM ===");

    const GTM_ID = "GTM-PS96TMC8";

    // Check if already loaded
    if (
      window.gtmLoaded ||
      document.querySelector(`script[src*="${GTM_ID}"]`)
    ) {
      console.log("GTM already loaded");
      return;
    }

    // Initialize dataLayer FIRST
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    console.log("DataLayer initialized:", window.dataLayer);

    // Create and load GTM script
    const gtmScript = document.createElement("script");
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    // Add load event listeners
    gtmScript.onload = () => {
      console.log("GTM script loaded successfully");
      window.gtmLoaded = true;

      // Verify script is in DOM
      const scriptInDom = document.querySelector(`script[src*="${GTM_ID}"]`);
      console.log("Script in DOM:", !!scriptInDom);

      // Check if GTM object exists
      setTimeout(() => {
        console.log(
          "Google Tag Manager object:",
          typeof window.google_tag_manager,
        );
        console.log("DataLayer after load:", window.dataLayer);
      }, 1000);
    };

    gtmScript.onerror = (error) => {
      console.error("Failed to load GTM script:", error);
    };

    // Add to head
    document.head.appendChild(gtmScript);
    console.log("GTM script element created and added to head");

    // Verify it was added
    setTimeout(() => {
      const addedScript: any = document.querySelector(
        `script[src*="${GTM_ID}"]`,
      );
      console.log("Script added to DOM:", !!addedScript);
      if (addedScript) {
        console.log("Script src:", addedScript.src);
      }
    }, 100);

    // Add noscript fallback
    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);

    console.log("GTM noscript fallback added");
  }, []);

  const useGTMLoader = (botData: any) => {
    const gtmLoadedRef = useRef(false);

    const checkPrivacyAndLoadGTM = useCallback(() => {
      try {
        const localStorageValue =
          window.localStorage?.getItem("privacy_policy");
        const cookieValue = document.cookie
          .split("; ")
          .find((row) => row.startsWith("privacy_policy="))
          ?.split("=")[1];

        if (localStorageValue === "true" || cookieValue === "true") {
          // GTM loading is now handled by the embed script
        }
      } catch (error) {
        console.error("Error checking privacy policy:", error);
      }
    }, []);

    return { checkPrivacyAndLoadGTM };
  };

  const lastUserMsgIndex = useMemo(() => {
    return message?.reduce((acc, val, idx) => {
      if (val?.author === 1) {
        acc = idx;
      }

      return acc;
    }, message?.length - 1);
  }, [message]);

  useImperativeHandle(ref, () => {
    return {
      sendAssistantMessage: (message) => {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: ai,
            timestamp: new Date(),
            text: message,
            aiResponse: true,
          },
        ]);
      },
      sendStaticUserMessage: (message) => {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: message,
          },
        ]);
      },
      sendStaticMessages: (
        messages: Array<{ text: string; role: "assistant" | "user" }>,
      ) => {
        if (hasSeededStaticMessagesRef.current || !messages.length) return;

        hasSeededStaticMessagesRef.current = true;
        const timestamp = new Date();
        setMessage((prevItems) => [
          ...prevItems,
          ...messages.map((item) => ({
            author: item.role === "assistant" ? ai : person,
            timestamp,
            text: item.text,
            aiResponse: item.role === "assistant",
            isStaticPreview: true,
          })),
        ]);
      },
      sendUserMessage: (message) => handleUserReply(0, false, message, false),
    };
  }, [botCustomId, roomId, botData]);

  const { checkPrivacyAndLoadGTM } = useGTMLoader(botData);

  useEffect(() => {
    checkPrivacyAndLoadGTM();
  }, [checkPrivacyAndLoadGTM]);

  // useEffect(() => {
  //   if (scrollToButtomRef.current && message.length === 0) {
  //     const timer = setTimeout(() => {
  //       if (scrollToButtomRef.current) {
  //         scrollToButtomRef.current.scrollTop = 0;
  //       }
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [message.length]);

  const userData = useMemo(() => {
    let data: Record<string, any> = {
      location: locationData.location ?? "",
    };

    data = mergeData(data, contactData?.fieldData);
    data = mergeData(data, embedData);
    data = mergeData(data, scrapedData);
    // data = mergeData(data, locationData);
    return data;
  }, [locationData, embedData, contactData, scrapedData]);

  const [isAiResError, setIsAiResError] = useState(false);
  const [isAiRateLimitError, setIsAiRateLimitError] = useState(false);
  const [isQuestionLimitReached, setIsQuestionLimitReached] = useState(false);
  const isChatInputLocked = isQuestionLimitReached;
  const isChatInputDisabled = Boolean(
    disableInput || isChatInputLocked || overLimit,
  );
  const [editable, setEditable] = useState(false);

  // The builder can change the starter welcome after the chat has loaded (e.g.
  // Build → Starter questions → Page-aware preview). Starter questions only show
  // under a message whose text matches aiStarterQues.initialMsg, so the welcome
  // on screen follows it, as long as the visitor hasn't written anything yet.
  const syncStarterWelcome = (nextWelcome: string) => {
    const previousWelcome = starterWelcomeRef.current;
    starterWelcomeRef.current = nextWelcome;
    if (previousWelcome === nextWelcome) return;
    setMessage((items: any[]) => {
      if (items.some((item) => item?.author === person)) return items;
      const index = previousWelcome
        ? items.findIndex(
            (item) => item?.author === ai && item?.text === previousWelcome,
          )
        : -1;
      if (index !== -1) {
        const next = [...items];
        next[index] = { ...next[index], text: nextWelcome };
        return next;
      }
      // No starter welcome shown yet (the bot had no starter questions when it
      // loaded): show it the way the chat does on load.
      if (
        items.some((item) => item?.author === ai && item?.text === nextWelcome)
      )
        return items;
      if (!items.length) {
        setCheckMsg(true);
        return items;
      }
      return [
        ...items,
        { author: ai, timestamp: new Date(), text: nextWelcome },
      ];
    });
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "BOT_DATA_UPDATE") {
        const updatedBotData = event.data.botData;
        if (!updatedBotData || typeof updatedBotData !== "object") return;

        setBotData(updatedBotData);
        setChatConfig(updatedBotData.chatConfig);
        if (
          updatedBotData.aiStarterQues?.initialMsg &&
          updatedBotData.aiStarterQues?.data.length
        ) {
          setTempStore(updatedBotData.aiStarterQues.data);
          setStarterQues(updatedBotData.aiStarterQues.data);
          syncStarterWelcome(updatedBotData.aiStarterQues.initialMsg);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!isQuestionLimitReached) return;

    const unlockIfLimitAllowsAnotherQuestion = () => {
      if (
        !hasReachedQuestionLimit(
          getAiChatCount(botId),
          botData?.aiBotConfig?.usageLimit,
        )
      ) {
        setIsQuestionLimitReached(false);
        setIsAiRateLimitError(false);
      }
    };

    unlockIfLimitAllowsAnotherQuestion();
    const interval = window.setInterval(
      unlockIfLimitAllowsAnotherQuestion,
      10_000,
    );
    return () => window.clearInterval(interval);
  }, [botData?.aiBotConfig?.usageLimit, botId, isQuestionLimitReached]);

  useEffect(() => {
    handleUserReplyRef.current = handleUserReply;
  }, [handleUserReply]);

  useEffect(() => {
    return () => {
      if (pendingImage?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pendingImage.previewUrl);
      }
    };
  }, [pendingImage?.previewUrl]);

  useEffect(() => {
    window.parent.postMessage({ type: "LIVEBOT_STATIC_MESSAGES_READY" }, "*");
  }, []);

  useEffect(() => {
    window.parent.postMessage({ type: "livebot:preview:ready" }, "*");

    const handlePreviewMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== "object") return;
      const data = event.data as {
        type?: string;
        sequence?: number;
        payload?: { active?: boolean; text?: string; isPreview?: boolean };
      };
      if (data.type === "livebot:preview:run-state") {
        previewRunnerActiveRef.current = Boolean(data.payload?.active);
        if (!data.payload?.active) {
          previewRunnerIsPreviewOverrideRef.current = null;
        }
        return;
      }
      if (data.type !== "livebot:preview:user-reply") return;
      const messageText =
        typeof data.payload?.text === "string" ? data.payload.text : "";
      const isPreviewOverride =
        typeof data.payload?.isPreview === "boolean"
          ? data.payload.isPreview
          : null;

      Promise.resolve()
        .then(() => {
          if (messageText.trim().length === 0) return;
          previewRunnerActiveRef.current = true;
          previewRunnerIsPreviewOverrideRef.current = isPreviewOverride;
          return handleUserReplyRef.current(0, false, messageText, false);
        })
        .finally(() => {
          previewRunnerIsPreviewOverrideRef.current = null;
          window.parent.postMessage(
            { type: "livebot:preview:reply-handled", sequence: data.sequence },
            "*",
          );
        });
    };

    window.addEventListener("message", handlePreviewMessage);
    return () => {
      window.removeEventListener("message", handlePreviewMessage);
    };
  }, []);

  function handleCooldownStarted(data) {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    pendingErrorMessage.current = "";
    setSocketErrorDetail("");
    setShowSocketErrorDetail(false);

    setCooldownActive(true);
    setCooldownMessage(
      data.message ||
        WAIT_BEFORE_TRYING_MESSAGE[
          botData?.aiBotConfig?.language?.toLowerCase()
        ] ||
        WAIT_BEFORE_TRYING_MESSAGE.english,
    );
    setCooldownRemainingTime(data.cooldownPeriod || 60);
    setIsCustomProviderError(data.isCustomProvider || false);
    setResponseLoader(false);
    setIsAiResError(false);
  }

  function handleCooldownStatus(data) {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    pendingErrorMessage.current = "";

    setCooldownRemainingTime(data.remainingTime || 0);
    setIsCustomProviderError(data.isCustomProvider || true);
    if (data.message) {
      setCooldownMessage(data.message);
    }
  }
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    if (!isMobile && !embed) {
      const scrollY = window.scrollY;
      const originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      Object.assign(document.body.style, {
        position: "fixed",
        top: `-${scrollY}px`,
        width: "100%",
      });

      return () => {
        document.body.style.cssText = "";
        document.body.style.overflow = originalBodyOverflow;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, embed]);

  useEffect(() => {
    const textarea = textareaRef.current;
    const handleFocus = () => {
      window.parent.postMessage({ type: "BOT_INPUT_FOCUSED" }, "*");
    };
    const handleBlur = () => {
      window.parent.postMessage({ type: "BOT_INPUT_BLUR" }, "*");
    };

    if (textarea) {
      textarea.addEventListener("focus", handleFocus);
      textarea.addEventListener("blur", handleBlur);
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("focus", handleFocus);
        textarea.removeEventListener("blur", handleBlur);
      }
    };
  }, []);

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      setMessage((prevItems) => [
        ...prevItems,
        {
          author: ai,
          timestamp: new Date(),
          text: "Meeting scheduled successfully.",
        },
      ]);
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      // Don't blur on iOS/mobile as it interferes with keyboard behavior
      if (isMobile) return;

      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement
      ) {
        active.blur();
      }
    };

    const wrapperElement = document.querySelector(
      ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
    ) as HTMLElement;

    if (wrapperElement) {
      wrapperElement.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      return () => wrapperElement.removeEventListener("scroll", handleScroll);
    } else {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isMobile]);

  const enableParentScrollHandoff = !COMPARE_MODE && window.parent !== window;

  const relayScrollToParent = useCallback(
    (deltaY: number) => {
      if (!deltaY || !enableParentScrollHandoff) return;

      const amplifiedDeltaY =
        Math.sign(deltaY) * Math.max(Math.abs(deltaY) * 1.35, 24);

      try {
        window.parent.postMessage(
          {
            type: "livebot:pass-through-scroll",
            deltaY: amplifiedDeltaY,
          },
          "*",
        );
      } catch (error) {
        console.error("Failed to relay scroll to parent:", error);
      }

      try {
        window.parent.scrollBy({
          top: amplifiedDeltaY,
          behavior: "auto",
        });
      } catch {
        // Cross-origin parents cannot be scrolled directly; postMessage above is the fallback.
      }
    },
    [enableParentScrollHandoff],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !enableParentScrollHandoff) return;

    const shouldPassScrollToParent = (
      scrollable: HTMLElement | null,
      deltaY: number,
    ) => {
      if (!scrollable) return true;

      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const hasScrollableOverflow = scrollHeight > clientHeight + 1;

      if (!hasScrollableOverflow) return true;

      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

      return (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
    };

    const getScrollableTarget = (target: EventTarget | null) => {
      if (target instanceof HTMLElement) {
        const nearestScrollable = target.closest(
          ".previewContainer, .new-scrollbar",
        ) as HTMLElement | null;

        if (nearestScrollable) return nearestScrollable;
      }

      return wrapper.querySelector(
        ".previewContainer, .new-scrollbar",
      ) as HTMLElement | null;
    };

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }

      const scrollable = getScrollableTarget(event.target);

      if (shouldPassScrollToParent(scrollable, event.deltaY)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        relayScrollToParent(event.deltaY);
        return;
      }

      event.stopPropagation();
    };

    let lastTouchY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? 0;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentTouchY = event.touches[0]?.clientY ?? lastTouchY;
      const deltaY = lastTouchY - currentTouchY;
      lastTouchY = currentTouchY;

      if (!deltaY) return;

      const scrollable = getScrollableTarget(event.target);

      if (shouldPassScrollToParent(scrollable, deltaY)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        relayScrollToParent(deltaY);
        return;
      }
    };

    wrapper.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });
    wrapper.addEventListener("touchstart", handleTouchStart, {
      passive: true,
      capture: true,
    });
    wrapper.addEventListener("touchmove", handleTouchMove, {
      passive: false,
      capture: true,
    });

    return () => {
      wrapper.removeEventListener("wheel", handleWheel, true);
      wrapper.removeEventListener("touchstart", handleTouchStart, true);
      wrapper.removeEventListener("touchmove", handleTouchMove, true);
    };
  }, [enableParentScrollHandoff, relayScrollToParent]);
  // Cleanup audio recorder on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioRecorderRef.current) {
        audioRecorderRef.current.stop();
      }
    };
  }, []);

  const mergedSalesForcePayload = questions.reduce(
    (acc: Record<string, any>, question) => {
      if (question.name === "salesForce" && question.salesForceData?.payload) {
        return { ...acc, ...question.salesForceData.payload };
      }
      return acc;
    },
    {},
  );
  const firstMessage = useRef<any[]>([]);

  const [suggestedQuestionsData, setSuggestedQuestionsData] = useState<any[]>(
    [],
  );

  // useEffect(() => {
  //   if (
  //     !lastMessage ||
  //     !lastMessage?.text ||
  //     !botData?.aiBotConfig?.showFollowUps
  //   )
  //     return;
  //   const content = String(lastMessage.text ?? "");
  //   const tag = "clickable-options-cbb";

  //   setSuggestedQuestionsData(
  //     extractClickableOptionsData(content, tag, {}) ?? [],
  //   );
  // }, [
  //   lastMessage?.text,
  //   extractClickableOptionsData,
  //   botData?.aiBotConfig?.showFollowUps,
  // ]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    if (previewRunnerActiveRef.current) return;

    if (responseLoader && scrollToButtomRef.current) {
      const scrollContainer = scrollToButtomRef.current;
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    }
  }, [responseLoader]);

  const handleStop = async () => {
    queryRequestSignal.current?.abort();
    wasLastRequestAborted.current = true; // Flag so next generatePreviousChat strips the cancelled exchange
    if (!!queryRequestId?.current) {
      axiosGet(`/ai/stop-query?requestId=${queryRequestId.current}`).catch(
        console.error,
      );
    }
    setResponseLoader(false);
    setUsingToolLoader(false);
    setIsAiResError(false);
    if (responseTimer.current) {
      clearTimeout(responseTimer.current);
      responseTimer.current = null;
    }
    botComponentRef.current?.setLoadingSeperation(false);
    // Clear any pending save operations when stopping
    // pendingSaveAnswer.current = null;
  };

  // Stable ref to handleStop so message listeners (with limited deps) can call
  // the latest version without a stale-closure risk.
  const handleStopRef = useRef(handleStop);
  handleStopRef.current = handleStop;

  useEffect(() => {
    handleResponseTimer();
  }, [responseLoader, message, handleStop, usingToolLoader, reasoning]);

  function handleResponseTimer() {
    if (responseTimer?.current) clearTimeout(responseTimer.current);
    if (reasoning.ongoing) return;

    if (responseLoader) {
      let timeout = 120000;
      if (responseTimer.current) {
        clearTimeout(responseTimer.current);
      }

      responseTimer.current = setTimeout(() => {
        if (usingToolLoader) {
          if (!COMPARE_MODE) setResponseLoader(false);
          const msgToShow =
            TAKING_LONGER_THAN_EXPECTED_MESSAGE[
              botData?.aiBotConfig?.language?.toLowerCase()
            ] || TAKING_LONGER_THAN_EXPECTED_MESSAGE.english;

          setMessage((prevMessages) => {
            const tempMessages = structuredClone(prevMessages);
            const lastMessage = tempMessages[tempMessages.length - 1];
            if (lastMessage?.isLoader) {
              lastMessage.text = msgToShow;
              lastMessage.isLoader = false;
              lastMessage.isTemp = true;
            } else if (
              !COMPARE_MODE ||
              !(lastMessage?.isTemp && lastMessage.text === msgToShow)
            ) {
              tempMessages.push({
                text: msgToShow,
                author: ai,
                isTemp: true,
              });
            }
            return tempMessages;
          });

          if (COMPARE_MODE) {
            console.log(
              "[RESPONSE TIMEOUT]: tool call is still running; keeping request active",
            );
          }
          return;
        }

        if (debug.current) return;
        if (!COMPARE_MODE) {
          setResponseLoader(false);

          setMessage((prevMessages) => {
            const lastMessage = prevMessages[prevMessages.length - 1];

            if (lastMessage?.isLoader) {
              if (hasReceivedStreamChunk.current) {
                console.log(
                  "[STREAMING TIMEOUT]: ignored as text chunks were received",
                );
                return prevMessages;
              }

              queryRequestSignal.current?.abort();
              setIsAiResError(true);
              setResponseLoader(false);

              console.log(
                "[STREAMING TIMEOUT]: streaming aborted as last message is loader",
              );
              return prevMessages.slice(0, -1);
            }

            console.log(
              "[STREAMING TIMEOUT]: streaming not aborted as last message is not loader",
            );
            return prevMessages;
          });
          reportFrontendQueryError({
            query: newReplyLast.current,
            errorType: "timeout",
            error: new Error(
              `live-calc: response timeout after ${timeout / 1000} sec, query - ${newReplyLast.current}`,
            ),
          });
          return;
        }
        // Once the stream has produced content, its lifecycle is owned by the
        // reader below. A quiet period must not make the iframe look idle while
        // that reader can still complete normally (or throw an error).
        if (hasReceivedStreamChunk.current) {
          console.log(
            "[STREAMING TIMEOUT]: ignored because the response stream is still active",
          );
          return;
        }

        // No response data arrived within the timeout, so settle this request
        // as an explicit timeout error before allowing a queued message to run.
        queryRequestSignal.current?.abort();
        isStreamingRef.current = false;
        setUsingToolLoader(false);
        setIsAiResError(true);

        setMessage((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];
          return lastMessage?.isLoader
            ? prevMessages.slice(0, -1)
            : prevMessages;
        });

        console.log(
          "[STREAMING TIMEOUT]: request aborted before any response data arrived",
        );

        // handleUserReply's finally block clears responseLoader after the
        // aborted fetch has actually settled. That is the only point at which
        // compare mode may release the next queued message.

        reportFrontendQueryError({
          query: newReplyLast.current,
          errorType: "timeout",
          error: new Error(
            `live-calc: response timeout after ${timeout / 1000} sec, query - ${newReplyLast.current}`,
          ),
        });
      }, timeout);
    }
  }

  useEffect(() => {
    if (
      !env()?.isAI &&
      ((botType == "ai" && !botData?.isTrained) ||
        ["button", "sendMessage"].includes(
          questions[currentQuestionIndex ?? 0]?.name,
        ))
    )
      setDisableInput(true);
    else setDisableInput(false);
  }, [currentQuestionIndex]);

  // useEffect(() => {
  //   if (chatConfig?.typography?.font?.fontURL) {
  //     let font;
  //     const id = "dynamic-font";
  //     if (document.getElementById(id)) {
  //       font = document.getElementById(id);
  //       font.href = chatConfig?.typography?.font?.fontURL;
  //     } else {
  //       font = document.createElement("link");
  //       font.rel = "stylesheet";
  //       font.id = id;
  //       font.href = chatConfig?.typography?.font?.fontURL;
  //       document.head.appendChild(font);
  //     }
  //   }
  // }, [chatConfig?.typography]);

  useEffect(() => {
    const selectedColor = chatConfig?.botBubbleColor;
    document.documentElement.style.setProperty(
      "--dynamic-color",
      chatConfig?.botBubbleColor,
    );

    const color = getTextColor(selectedColor);
    setTextColor(color);
  }, [chatConfig?.botBubbleColor]);

  useEffect(() => {
    if (message.length > 0 && !initialLoadComplete.current) {
      const timer = setTimeout(() => {
        initialLoadComplete.current = true;
        const scrollContainer = scrollToButtomRef.current;
        if (scrollContainer) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          if (distanceFromBottom > 100) {
            setShowGoToBottom(true);
            setIsAutoScrollPaused(true);
          }
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [message.length]);
  useEffect(() => {
    const scrollContainer = scrollToButtomRef.current;
    const bottomAnchor = scrollRef.current;

    if (!scrollContainer || !bottomAnchor) return;
    if (bottomObserver.current) {
      bottomObserver.current.disconnect();
    }
    bottomObserver.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isVisible = entry.isIntersecting;

        isAtBottom.current = isVisible;

        // Only show button if:
        // 1. Initial load is complete (prevents flicker)
        // 2. User has scrolled at least once (prevents showing on first render)
        // 3. Bottom anchor is NOT visible (user is away from bottom)
        if (
          initialLoadComplete.current &&
          hasUserScrolled.current &&
          !isVisible
        ) {
          setShowGoToBottom(true);
          setIsAutoScrollPaused(true);
        } else if (isVisible) {
          setShowGoToBottom(false);
          setIsAutoScrollPaused(false);
        }
      },
      {
        root: scrollContainer,
        rootMargin: "0px 0px 50px 0px",
        threshold: 0,
      },
    );

    bottomObserver.current.observe(bottomAnchor);

    return () => {
      if (bottomObserver.current) {
        bottomObserver.current.disconnect();
      }
    };
  }, [message.length]);

  useEffect(() => {
    if (previewRunnerActiveRef.current) return;
    if (!initialLoadComplete.current || isAutoScrollPaused) {
      return;
    }

    if (message.length > 0 && scrollToButtomRef.current && isAtBottom.current) {
      const scrollContainer = scrollToButtomRef.current;
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    }
  }, [message.length, isAutoScrollPaused]);

  // useEffect(() => {
  //   if (!embed) return;
  //   if (message.length === 0) return;
  //   const lastMessage = message[message.length - 1];

  //   if (
  //     lastMessage.author === ai &&
  //     !lastMessage.isLoader &&
  //     lastMessage.text &&
  //     lastMessage.aiResponse
  //   ) {
  //     const notificationTimer = setTimeout(() => {
  //       // Send notification to parent window (embed script)
  //       window.parent.postMessage(
  //         {
  //           type: 'NEW_BOT_MESSAGE',
  //           timestamp: Date.now(),
  //           messagePreview: typeof lastMessage.text === 'string'
  //             ? lastMessage.text.substring(0, 50)
  //             : '',
  //           hasAgent: !!lastMessage.agent
  //         },
  //         '*'
  //       );
  //     }, 100);

  //     return () => clearTimeout(notificationTimer);
  //   }
  // }, [message, embed, ai]);

  // useEffect(() => {
  //   if (!embed) return;
  //   if (message.length === 0) return;
  //   const lastMessage = message[message.length - 1];

  //   // Notify for human agent messages
  //   if (lastMessage?.agent && lastMessage.author !== ai) {
  //     const notificationTimer = setTimeout(() => {
  //       window.parent.postMessage(
  //         {
  //           type: 'NEW_AGENT_MESSAGE',
  //           timestamp: Date.now(),
  //           agent: lastMessage.agent,
  //           messagePreview: typeof lastMessage.text === 'string'
  //             ? lastMessage.text.substring(0, 50)
  //             : ''
  //         },
  //         '*'
  //       );
  //     }, 100);

  //     return () => clearTimeout(notificationTimer);
  //   }
  // }, [message, embed, ai]);
  const sendUserQuery = useCallback(
    _.debounce(async (query: string) => {
      if (!query || !query.trim()) {
        setSuggestedQuestionsData([]);
        return;
      }
      if (
        query.trim().length <= 3 ||
        !socket ||
        !socket.connected ||
        responseLoader
      )
        return;
      socket.emit("user-query", { botCustomId, query: query.trim() });
    }, 500),
    [botCustomId, socket, responseLoader],
  );

  function handleSendViaEnter(e) {
    if (e.key === "Enter") {
      if (e.shiftKey) return;
      e.preventDefault();
      if (!responseLoader && !isChatInputDisabled) handleSendReplyButton();
    }
  }

  // Track user scroll interaction (throttled for performance)
  const handleScroll = useCallback(() => {
    // Mark that user has scrolled at least once
    if (!hasUserScrolled.current) {
      hasUserScrolled.current = true;
    }
  }, []);

  // Scroll to bottom when the button is clicked
  const scrollToBottom = useCallback((event?: any) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (scrollToButtomRef.current) {
      const scrollContainer = scrollToButtomRef.current;
      const start = scrollContainer.scrollTop;
      const distance =
        scrollContainer.scrollHeight - scrollContainer.clientHeight - start;
      const duration = 300;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 0.5 - Math.cos(progress * Math.PI) / 2;
        scrollContainer.scrollTop = start + distance * easeProgress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          // After animation completes, ensure button is hidden
          setShowGoToBottom(false);
          setIsAutoScrollPaused(false);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, []);

  // Attach scroll event listener with passive option for performance
  useEffect(() => {
    const container = scrollToButtomRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (islast) {
      if (startLiveChat) {
        setIsLast(false);
      } else {
        socket && socket.disconnect();
        deleteCookies("senderId");
        if (botData?.chatTranscript) {
          let query = new URLSearchParams({
            email: userData.email,
            roomId: roomId || "",
          });
          axiosGet(`/livebot/sendChatTranscript?${query}`);
        }
      }
    }
  }, [islast]);
  useEffect(() => {
    processBotData();

    if (props.resumeConversationData) {
      processResumeChat(props.resumeConversationData);
    }
  }, []);

  async function processBotData() {
    try {
      const {
        _id,
        botCustomId,
        questions,
        projectName,
        startNodeId,
        botType,
        aiBotConfig,
        companyId,
      } = botData;

      const currentIndex = questions.findIndex(
        (ele) => ele.drawFlowId === startNodeId,
      );

      if (
        botData.aiStarterQues?.initialMsg &&
        botData.aiStarterQues?.data.length
      ) {
        setTempStore(botData.aiStarterQues?.data);
        setCheckMsg(!!botData.aiStarterQues?.initialMsg);
      }
      if (!chatConfig) {
        setChatConfig(botData.chatConfig);
      }
      setBotId(_id);
      setBotCustomId(botCustomId);
      setQuestions(questions);
      setBotName(projectName);
      setCurrentQuestionIndex(props.resumeConversationData ? 1 : currentIndex);
      setBotType(botType);

      // await triggerNudge(botData.aiBotConfig?.nudgeConfig);
    } catch (err) {
      if (environment === "live") router("/error");
    }
  }

  async function processResumeChat(resumeConversationData) {
    const messages: any[] = [];

    for (const data of resumeConversationData.userAnswers) {
      if (data.userAnswer === "NO_REPLY") {
        messages.push({
          author: ai,
          timestamp: new Date(data.date),
          text: data.nodeTitle,
          ...(data.terminateByUser && {
            terminateByUser: data.terminateByUser,
          }),
        });
      } else {
        if (data.userAnswer) {
          messages.push({
            author: person,
            timestamp: new Date(data.date),
            text: data.userAnswer,
            ...(data.terminateByUser && {
              terminateByUser: data.terminateByUser,
            }),
          });
        }

        if (data.nodeTitle) {
          messages.push({
            author: ai,
            timestamp: new Date(data.date),
            text: data.nodeTitle,
            aiResponse: true,
            agent: data.agent,
            ...(data.terminateByUser && {
              terminateByUser: data.terminateByUser,
            }),
          });
        }
      }
    }

    if (resumeConversationData.handoffData) {
      handoffData.current = resumeConversationData.handoffData;
      if (resumeConversationData.handoffData.handoffType === "inhouse") {
        setInhouseAssigneeName(
          resumeConversationData.handoffData.payload?.assignedUserName || "",
        );
      }
    }

    connectSocket(resumeConversationData._id);

    const chatHandler = handoffData.current.isConnected
      ? handoffData.current.handoffType
      : "ai";

    setRoomId(resumeConversationData._id);
    setMessage(messages);
    chatHandlerRef.current = chatHandler;

    setChatSummary(resumeConversationData.chatSummary?.summary ?? "");
    setNextChatSummaryStart(
      resumeConversationData.chatSummary?.nextSummaryStart ?? 0,
    );
    setChatSummaryPending(resumeConversationData.chatSummary?.pending ?? false);
  }

  useEffect(() => {
    if (currentQuestionIndex !== null && currentQuestionIndex >= 0) {
      if (questions[currentQuestionIndex]?.name === "sendMessage") {
        const titleData = variableParser(
          questions[currentQuestionIndex]?.data?.title,
          userData,
          null,
          mergedSalesForcePayload,
        );
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: ai,
            timestamp: new Date(),
            text: titleData,
            name: questions[currentQuestionIndex]?.name,
            hasAnswered: false,
            completeData: questions[currentQuestionIndex],
          },
        ]);
        setDisableInput(true);
        handleSendNoReplyMessage(titleData);
      } else {
        if (!handoffData.current.isConnected) {
          chatHandlerRef.current = "ai";
        }
      }
    }
  }, [currentQuestionIndex]);

  const handleSendNoReplyMessage = async (title) => {
    try {
      const variableDataToSend = {};
      for (const variableName in variableData) {
        if (PREDEFINED_VARIABLES.includes(variableName))
          variableDataToSend[variableName] = variableData[variableName];
      }

      if (
        environmentData?.isAI &&
        questions[currentQuestionIndex].drawFlowId == 1
      ) {
        firstMessage.current = [
          {
            botCustomId: botCustomId,
            nodeTitle: typeof title === "string" ? title : (title?.value ?? ""),
            drawFlowId: questions[currentQuestionIndex].drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData:
              variableDataToSend && Object.keys(variableDataToSend).length > 0
                ? variableDataToSend
                : {
                    location: `${locationData.city}, ${locationData.regionName}, ${locationData.country}`,
                  },
            userCredential,
            nodeType: questions[currentQuestionIndex].nodeType,
          },
        ];
      }

      if (
        !(
          environmentData?.isAI &&
          questions[currentQuestionIndex].drawFlowId == 1
        )
      ) {
        const response = await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${
            formattedLocation
          }`,
          {
            botCustomId: botCustomId,
            nodeTitle: typeof title === "string" ? title : (title?.value ?? ""),
            drawFlowId: questions[currentQuestionIndex].drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData:
              variableDataToSend && Object.keys(variableDataToSend).length > 0
                ? variableDataToSend
                : {
                    location: `${locationData.city}, ${locationData.regionName}, ${locationData.country}`,
                  },
            userCredential,
            nodeType: questions[currentQuestionIndex].nodeType,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
        setRoomId(response.data.roomId);
      }

      let pendingMsg = { ...arr3[0] };
      if (
        arr2.length === 0 &&
        checkMsg &&
        ((pendingMsg.hasOwnProperty("text") &&
          message.length < pendingMsg?.text?.durationVal) ||
          !pendingMsg.hasOwnProperty("text"))
      ) {
        setStarterQues(tempStore);
        if (botData?.aiStarterQues?.initialMsg?.trim().length > 0) {
          starterWelcomeRef.current = botData.aiStarterQues.initialMsg;
          setMessage((prevItems) => {
            // Prevent pushing a duplicate initial message if the flow already pushed an identical one
            if (
              prevItems.length > 0 &&
              prevItems[prevItems.length - 1].text ===
                botData?.aiStarterQues?.initialMsg
            ) {
              return prevItems;
            }
            return [
              ...prevItems,
              {
                author: ai,
                timestamp: new Date(),
                text: botData?.aiStarterQues?.initialMsg,
              },
            ];
          });
        }
        isSkip.current = true;
        setCheckMsg(false);
        setDisableInput(false);
        return;
      } else if (
        pendingMsg.hasOwnProperty("text") &&
        message.length >= pendingMsg?.text?.durationVal &&
        arr2.length === 0
      ) {
        isSkip.current = true;
        setArr3((prev: any) => {
          const [_, ...pendings] = prev;
          return pendings;
        });
        setMessage((prevItems) => [...prevItems, pendingMsg]);
      } else if (
        questions[currentQuestionIndex]?.outputs[0].connections !== undefined
      ) {
        let currentIndex = questions.findIndex(
          (x) =>
            x.drawFlowId ===
            questions[currentQuestionIndex]?.outputs[0].connections.node,
        );
        setCurrentQuestionIndex(currentIndex);
      } else {
        if (chatHandlerRef.current === "ai") {
          setCurrentQuestionIndex(questions.length - 1);
          setDisableInput(false);
          setCheckIndex(false);
          return;
        }
        setIsLast(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleButtonReply = (
    currentIndex,
    buttonSelected,
    buttonSelectedIndex,
    buttonValue,
    buttonImage = "",
  ) => {
    let oldMessages = [...message];
    let indexToUpdate = message.length - 1;
    oldMessages[indexToUpdate].hasAnswered = true;
    setMessage(oldMessages);

    setMessage((prevItems) => [
      ...prevItems,
      {
        author: person,
        timestamp: new Date(),
        text: buttonSelected,
        value: buttonValue,
        image: buttonImage,
      },
    ]);
    isSkip.current = true;
    let varName =
      message[currentIndex].completeData.variableName !== undefined
        ? message[currentIndex].completeData.variableName
        : null;
    let insideVarName =
      message[currentIndex].completeData.buttonDetails.variableName;
    const valuefButton =
      message[currentIndex].completeData.buttonDetails.buttons[
        buttonSelectedIndex
      ].value;

    let oldButtonVariables = [...buttonVariables];
    let newObj = {
      name: message[currentIndex].completeData.buttonDetails.variableName,
      value: valuefButton,
    };
    oldButtonVariables.push(newObj);
    setButtonVariables(oldButtonVariables);

    let variableDataToSend = {};
    if (varName !== null) {
      variableDataToSend = {
        [varName]: buttonSelected,
        [insideVarName]: valuefButton,
      };
    }

    // axiosPost(
    //   `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
    //   {
    //     botCustomId: botCustomId,
    //     nodeTitle: message[currentIndex].completeData.data.title,
    //     drawFlowId: message[currentIndex].completeData.drawFlowId ?? 0,
    //     userAnswer: buttonSelected,
    //     variableName: varName,
    //     variableData: variableDataToSend,
    //     userCredential,
    //     model: botData?.botModel,
    //   },
    // )
    //   .then((response) => {
    //     if (roomId === null) {
    //       setRoomId(response.data.roomId);
    //     }
    //     if (
    //       message[currentIndex].completeData?.outputs[0]?.connectionId ==
    //         "output_0" &&
    //       message[currentIndex].completeData?.outputs[0]?.connections?.node
    //     ) {
    //       let currentIndexValue = questions.findIndex(
    //         (x) =>
    //           x.drawFlowId ===
    //           message[currentIndex].completeData?.outputs[0]?.connections.node,
    //       );
    //       setCurrentQuestionIndex(currentIndexValue);
    //     } else if (
    //       message[currentIndex].completeData?.outputs[buttonSelectedIndex]
    //         ?.connections !== undefined
    //     ) {
    //       let currentIndexValue = questions.findIndex(
    //         (x) =>
    //           x.drawFlowId ===
    //           message[currentIndex].completeData?.outputs[buttonSelectedIndex]
    //             ?.connections.node,
    //       );
    //       setCurrentQuestionIndex(currentIndexValue);
    //     } else {
    //       setIsLast(true);
    //     }
    //   })
    //   .catch((error) => {
    //     console.log(error);
    //   });
  };

  const getSimpleObject = (data, mainKey = "") => {
    let obj = {};
    const objKeys = Object.keys(data ?? {});
    objKeys.forEach((key) => {
      if (typeof data[key] !== "object") {
        if (mainKey.length > 0)
          obj = { ...obj, [`${mainKey}.${key}`]: data[key] };
        else obj = { ...obj, [`${key}`]: data[key] };
      } else if (Array.isArray(data[key])) {
        const arr = data[key];
        const arrLen = data[key].length;

        let temoObj = convertArrayToObj(arr, key);
        temoObj = getSimpleObject(temoObj, mainKey);
        obj = { ...obj, ...temoObj };
      } else {
        let tempObj = {};
        if (mainKey.length > 0)
          tempObj = getSimpleObject(data[key], `${mainKey}.${key}`);
        else tempObj = getSimpleObject(data[key], `${key}`);
        obj = { ...obj, ...tempObj };
      }
    });
    return obj;
  };

  const convertArrayToObj = (arr, key) => {
    let obj = {};
    arr.forEach((a, index) => {
      obj[`${key}.${index}`] = a;
    });
    return obj;
  };

  // const handleStarterQues = async (reply: string) => {
  //   try {
  //     if (!islast) {
  //       if (roomId === null && firstMessage.current.length !== 0) {
  //         function capitalize(str) {
  //           return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  //         }
  //         const location = `${capitalize(locationData.city)}, ${capitalize(locationData.region)}, ${capitalize(locationData.country)}`;
  //         const response = await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${location}`,
  //           {
  //             botCustomId: botCustomId,
  //             nodeTitle: firstMessage.current[0].nodeTitle,
  //             drawFlowId: "1",
  //             userAnswer: "NO_REPLY",
  //             variableData: variableData,
  //             userCredential: userCredential,
  //             nodeType: firstMessage.current[0].nodeType,
  //             contactId: contactData?._id,
  //             userData,
  //             chatSummary: {
  //               summary: chatSummary,
  //               nextSummaryStart: nextChatSummaryStart,
  //               pending: chatSummaryPending,
  //             },
  //           },
  //         );

  //         roomID.current = response.data.roomId;
  //         setRoomId(response.data.roomId);
  //         connectSocket(response.data.roomId);

  //         setContactData(response.data?.contact);
  //         window.localStorage.setItem(
  //           "cbb-contact-id",
  //           response.data?.contact._id,
  //         );

  //         firstMessage.current = [];
  //       }

  //       await triggerNudge(botData?.aiBotConfig?.nudgeConfig);

  //       const isStaterQues = true;

  //       if (!activeCat && botData?.aiStarterQues.isCat) {
  //         let catQuesAns = botData?.aiStarterQues.data.filter(
  //           (item) => item.cat === reply,
  //         );
  //         setStarterQues(catQuesAns);
  //         setActiveCat({ text: reply });
  //       } else {
  //         setStarterQues(undefined);
  //         setActiveCat(undefined);
  //         setTempStore("");
  //         if (suggestedQuesAns) setSuggestedQuesAns(false);
  //         let ans = botData?.aiStarterQues?.data?.filter(
  //           (item) => item.ques == reply,
  //         );
  //         setMessage((prevItems: any) => {
  //           const tempMessage = [...prevItems];
  //           tempMessage.push({
  //             author: person,
  //             timestamp: new Date(),
  //             text: reply,
  //           });
  //           return tempMessage;
  //         });
  //         await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${variableData?.location}&starterQues=${isStaterQues}`,
  //           {
  //             isAiBot: true,
  //             botCustomId: botCustomId,
  //             nodeTitle: environmentData.isAI
  //               ? message[message.length - 1]?.name === "ask_question" ||
  //                 message[message.length - 1]?.name === "email" ||
  //                 message[message.length - 1]?.name === "job" ||
  //                 message[message.length - 1]?.name === "name" ||
  //                 message[message.length - 1]?.name === "address" ||
  //                 message[message.length - 1]?.name === "company" ||
  //                 message[message.length - 1]?.name === "phone"
  //                 ? message[message.length - 1].text.value
  //                 : message[message.length - 1].text
  //               : (message[message.length - 1].text ?? ""),
  //             drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
  //             userAnswer: reply,
  //             variableName: null,
  //             variableData: variableData,
  //             userCredential,
  //           },
  //         );
  //         setMessage((prevItems: any) => {
  //           const tempMessage = [...prevItems];
  //           tempMessage.push({
  //             author: ai,
  //             timestamp: new Date(),
  //             text: ans[0]?.ans,
  //           });
  //           return tempMessage;
  //         });
  //         await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${variableData?.location}`,
  //           {
  //             //maybe here
  //             isAiBot: true,
  //             botCustomId: botCustomId,
  //             nodeTitle: ans[0]?.ans,
  //             drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
  //             userAnswer: "NO_REPLY",
  //             variableName: null,
  //             variableData: variableData,
  //             userCredential,
  //           },
  //         );
  //         let pendingMsg = { ...arr3[0] };
  //         if (
  //           pendingMsg.hasOwnProperty("text") &&
  //           message.length + 2 >= pendingMsg?.text?.durationVal
  //         ) {
  //           isSkip.current = true;
  //           setArr3((prev: any) => {
  //             const [_, ...pendings] = prev;
  //             return pendings;
  //           });
  //           setMessage((prevItems) => [...prevItems, pendingMsg]);
  //           return;
  //         } else {
  //           setCurrentQuestionIndex(questions.length - 1);
  //         }
  //       }
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   } finally {
  //     isSkip.current = true;
  //   }
  // };

  async function typeWithDelay(text, delay = 50) {
    // Add the complete message first but mark it as streaming
    setMessage((prevItems: any) => {
      let tempMessage = [...prevItems];
      const newMessage = {
        author: ai,
        timestamp: new Date(),
        text: text,
        isStreaming: true,
        streamingIndex: 0,
      };
      tempMessage.push(newMessage);
      return tempMessage;
    });

    // Extract text content to get character count for animation
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    const totalChars = textContent.length;

    // Animate the streaming effect by updating the streaming index
    await new Promise<void>((resolve) => {
      let currentIndex = 0;
      const interval = setInterval(() => {
        currentIndex += 2; // Show 2 characters at a time for smoother effect

        setMessage((prevItems: any) => {
          let tempMessage = [...prevItems];
          const lastMessage = tempMessage[tempMessage.length - 1];
          if (lastMessage && lastMessage.isStreaming) {
            lastMessage.streamingIndex = Math.min(currentIndex, totalChars);
            if (currentIndex >= totalChars) {
              lastMessage.isStreaming = false;
              delete lastMessage.streamingIndex;
            }
          }
          return [...tempMessage];
        });

        if (currentIndex >= totalChars) {
          clearInterval(interval);
          resolve();
        }
      }, delay);
    });
  }

  const handleStarterQues = async (reply: string) => {
    if (isChatInputLocked) return;

    setResponseLoader(true);
    let handledByUserReply = false; // Flag to skip finally block's nudge
    try {
      {
        const isStaterQues = true;
        const isCategoryMode = !activeCat && botData?.aiStarterQues.isCat;
        if (isCategoryMode) {
          const catQuesAns = botData?.aiStarterQues.data.filter(
            (item) => item.cat === reply,
          );
          setStarterQues(catQuesAns);
          setActiveCat({ text: reply });
          return;
        }
        const ans = botData?.aiStarterQues?.data?.find(
          (item) => item.ques === reply,
        );

        if (ans?.answeredByAI) {
          // handleUserReply will create the room itself, so don't create it here
          // Also, handleUserReply manages its own loader and nudge
          handledByUserReply = true; // Mark that handleUserReply handles everything
          setMessage((prevItems) => [
            ...prevItems,
            {
              author: person,
              timestamp: new Date(),
              text: stripHTML(reply),
            },
          ]);

          // handleUserReply is async and handles its own loader/nudge
          handleUserReply(
            message?.length ? message?.length - 1 : 0,
            false,
            stripHTML(reply),
            false,
          );
          return; // Exit early - handleUserReply handles loader and nudge
        } else {
          // Only create room in this path, not when answeredByAI
          const roomCreationPromise =
            roomId === null && firstMessage.current.length !== 0
              ? createRoom()
              : Promise.resolve();

          await updateUIForStarterQuestion(stripHTML(reply), ans);

          resetStarterQuestionState();

          const [roomData] = await Promise.all([roomCreationPromise]);

          if (roomData) {
            handleRoomCreation(roomData);
          }

          // Save user question first to get/use the roomId
          const userQuestionResponse = await saveUserQuestion(
            stripHTML(reply),
            isStaterQues,
          );

          // If we got a new roomId from the first call, update it before the second call
          if (
            userQuestionResponse?.data?.roomId &&
            !roomId &&
            !roomID.current
          ) {
            roomID.current = userQuestionResponse.data.roomId;
            setRoomId(userQuestionResponse.data.roomId);
          }

          // Now save bot answer with the established roomId
          await saveBotAnswer(ans);

          handlePendingMessages();
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      isSkip.current = true;
      // Only reset loader and trigger nudge if NOT handled by handleUserReply
      if (!handledByUserReply) {
        setResponseLoader(false);
        triggerNudge(botData?.aiBotConfig?.nudgeConfig);
      }
    }
  };

  const handleCompareStarterQues = async (reply: string) => {
    broadcastCompareClickAction({ kind: "starter", text: reply });
    await handleStarterQues(reply);
  };

  // Helper functions
  const createRoom = async () => {
    const response = await saveAnswerPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
      {
        botCustomId,
        nodeTitle: firstMessage.current[0].nodeTitle,
        drawFlowId: "1",
        userAnswer: "NO_REPLY",
        variableData,
        userCredential,
        nodeType: firstMessage.current[0].nodeType,
        contactId: contactData?._id,
        userData,
        chatSummary: {
          summary: chatSummary,
          nextSummaryStart: nextChatSummaryStart,
          pending: chatSummaryPending,
        },
        model:
          (COMPARE_MODE && compareConfigRef.current?.modelConfig?.modelName) ||
          botData?.botModel,
      },
    );
    return response.data;
  };

  const updateUIForStarterQuestion = async (reply, ans) => {
    setMessage((prevItems) => [
      ...prevItems,
      {
        author: person,
        timestamp: new Date(),
        text: reply,
      },
      {
        author: ai,
        timestamp: new Date(),
        text: ans?.ans,
      },
    ]);

    // await typeWithDelay(ans?.ans, 30);
  };

  const resetStarterQuestionState = () => {
    setStarterQues(undefined);
    setActiveCat(undefined);
    setTempStore("");
    if (suggestedQuesAns) setSuggestedQuesAns(false);
  };

  const handleRoomCreation = (roomData) => {
    roomID.current = roomData.roomId;
    setRoomId(roomData.roomId);
    connectSocket(roomData.roomId);
    setContactData(roomData?.contact);
    window.localStorage.setItem("cbb-contact-id", roomData?.contact._id);
    firstMessage.current = [];
  };

  const saveUserQuestion = async (reply, isStaterQues) => {
    return saveAnswerPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}&starterQues=${isStaterQues}`,
      {
        isAiBot: true,
        botCustomId,
        nodeTitle: environmentData.isAI
          ? message[message.length - 1]?.name === "ask_question" ||
            message[message.length - 1]?.name === "email" ||
            message[message.length - 1]?.name === "job" ||
            message[message.length - 1]?.name === "name" ||
            message[message.length - 1]?.name === "address" ||
            message[message.length - 1]?.name === "company" ||
            message[message.length - 1]?.name === "phone"
            ? removeCustomTags(message[message.length - 1].text.value)
            : removeCustomTags(message[message.length - 1].text)
          : (removeCustomTags(message[message.length - 1].text) ?? ""),
        drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
        userAnswer: removeCustomTags(reply),
        variableName: null,
        variableData,
        userCredential,
        model:
          (COMPARE_MODE && compareConfigRef.current?.modelConfig?.modelName) ||
          botData?.botModel,
      },
    );
  };

  const saveBotAnswer = async (ans) => {
    return saveAnswerPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}`,
      {
        isAiBot: true,
        botCustomId,
        nodeTitle: typeof ans?.ans === "string" ? ans.ans : "",
        drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
        userAnswer: "NO_REPLY",
        variableName: null,
        variableData,
        userCredential,
        model:
          (COMPARE_MODE && compareConfigRef.current?.modelConfig?.modelName) ||
          botData?.botModel,
      },
    );
  };

  const handlePendingMessages = () => {
    const pendingMsg = arr3[0];
    if (
      pendingMsg?.hasOwnProperty("text") &&
      message.length + 2 >= pendingMsg?.text?.durationVal
    ) {
      isSkip.current = true;
      setArr3((prev) => prev.slice(1)); // More efficient than destructuring
      setMessage((prevItems) => [...prevItems, pendingMsg]);
    } else {
      setCurrentQuestionIndex(questions.length - 1);
    }
  };
  const handleNudgeMech = async (nudgeConfig: any, isFinal: boolean) => {
    try {
      if (chatHandlerRef.current === "crisp") return;
      let msgToSend = isFinal ? nudgeConfig?.msg : nudgeConfig?.finalMsg;
      setMessage((prevItems: any) => {
        const tempMessage = [...prevItems];
        tempMessage.push({
          author: ai,
          timestamp: new Date(),
          text: msgToSend,
        });
        return tempMessage;
      });
      if (roomId || roomID.current) {
        await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${
            formattedLocation
          }`,
          {
            botCustomId: botCustomId,
            nodeTitle: msgToSend,
            drawFlowId: questions[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData: {},
            userCredential,
            isAiBot: true,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const triggerNudge = async (nudgeConfig: any) => {
    console.log("nudge hide ");
    // Don't trigger nudge while streaming is in progress (use ref for current value)
    // if (isStreamingRef.current) return;

    // if (chatHandlerRef.current == "ai" && nudgeConfig?.collectNudgeDetails) {
    //   clearTimeout(nudgeTimer3.current);
    //   if (nudgeConfig?.msg) {
    //     clearTimeout(nudgeTimer.current);
    //     clearTimeout(nudgeTimer2.current);
    //     nudgeTimer.current = setTimeout(
    //       () => {
    //         // Check again at execution time - streaming might have started
    //         if (isStreamingRef.current) return;
    //         const lastMessage = message[message.length - 1];
    //         if (!lastMessage?.isLoader) {
    //           handleNudgeMech(nudgeConfig, true);
    //         }
    //         clearTimeout(nudgeTimer.current);
    //       },
    //       (nudgeConfig?.durationUnit === "minutes"
    //         ? nudgeConfig?.duration * 60
    //         : nudgeConfig?.duration) * 1000,
    //     );

    //     if (nudgeConfig?.finalMsg) {
    //       clearTimeout(nudgeTimer2.current);
    //       nudgeTimer2.current = setTimeout(
    //         () => {
    //           // Check again at execution time - streaming might have started
    //           if (isStreamingRef.current) return;
    //           const lastMessage = message[message.length - 1];
    //           if (!lastMessage?.isLoader) {
    //             handleNudgeMech(nudgeConfig, false);
    //           }
    //           clearTimeout(nudgeTimer2.current);
    //         },
    //         (nudgeConfig?.durationUnit === "minutes"
    //           ? nudgeConfig?.duration * 60
    //           : nudgeConfig?.duration) *
    //           1000 *
    //           2,
    //       );
    //     }
    //   }
    //   nudgeTimer3.current = setTimeout(
    //     () => {
    //       // Check again at execution time - streaming might have started
    //       if (isStreamingRef.current) return;
    //       if (chatHandlerRef.current === "ai") setIsLast(true);
    //     },
    //     (() => {
    //       const duration1InSec =
    //         nudgeConfig?.durationUnit === "minutes"
    //           ? nudgeConfig?.duration * 60
    //           : nudgeConfig?.duration;

    //       const duration2InSec =
    //         nudgeConfig?.endSession?.durationUnit === "minutes"
    //           ? nudgeConfig?.endSession?.duration * 60
    //           : nudgeConfig?.endSession?.duration;

    //       return (
    //         ((nudgeConfig?.finalMsg ? duration1InSec * 2 : duration1InSec) +
    //           duration2InSec) *
    //         1000
    //       );
    //     })(),
    //   );
    // }
  };

  const clearNudgeTimers = () => {
    if (botData?.aiBotConfig?.nudgeConfig?.collectNudgeDetails) {
      if (nudgeTimer?.current) clearTimeout(nudgeTimer.current);
      if (nudgeTimer2?.current) clearTimeout(nudgeTimer2.current);
      if (nudgeTimer3?.current) clearTimeout(nudgeTimer3.current);
    }
  };

  const handleRegenerate = async (retry: boolean) => {
    if (retry && regenerate.current) return;
    regenerate.current = true;
    setMessage((prevItems: any) => {
      const tempMessage = [...prevItems];
      const lastMessage = tempMessage[tempMessage.length - 1];
      if (lastMessage.author == ai) {
        tempMessage.pop();
        tempMessage.pop();
      } else {
        tempMessage.pop();
      }
      return tempMessage;
    });
    let retryMsgIndex;
    if (retry) {
      if (message[message.length - 1]?.author) {
        //user
        retryMsgIndex = 1;
      } else {
        if (message[message.length - 2]?.author) {
          //user
          retryMsgIndex = 2;
        } else {
          retryMsgIndex = 3;
        }
      }
    }
    const targetMsg = message[message?.length - (retry ? retryMsgIndex : 1)];
    const targetText = targetMsg?.caption || targetMsg?.text;

    await handleUserReply(
      message?.length ? message?.length - 1 : 0,
      false,
      targetText,
      regenerate.current,
      !!targetMsg?.image,
      targetMsg?.image,
    );

    regenerate.current = false;
  };

  async function handlefetchResponseFromAI(
    aiReply,
    reply,
    fallback = false,
    isImage = false,
    imgUrl = "",
    caption = "",
    // imageQuery = "",
  ) {
    const aiChatCount = getAiChatCount(botId);
    const effectiveIsPreview = COMPARE_MODE
      ? true
      : (previewRunnerIsPreviewOverrideRef.current ?? false);
    if (
      hasReachedQuestionLimit(aiChatCount, botData?.aiBotConfig?.usageLimit) &&
      !effectiveIsPreview
    ) {
      setIsAiRateLimitError(true);
      setIsQuestionLimitReached(true);
      return null;
    } else {
      setAiChatCount(botId, aiChatCount + 1);

      if (!fallback) {
        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                id: queryRequestId.current,

                author: person,

                timestamp: new Date(),

                text: "",

                caption: caption,

                image: imgUrl,

                showImg: true,

                isPendingImageText: true,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
          {
            author: ai,
            timestamp: new Date(),
            isLoader: true,
            text: "Loading...",
          },
        ]);
      }
      setResponseLoader(true);
      const { previousChat, totalMessages } = generatePreviousChat();

      queryRequestSignal.current = new AbortController();
      queryRequestId.current = nanoid();
      hasReceivedStreamChunk.current = false;
      reasoning.stop(true);

      let _contactData = contactData;

      if (roomId === null && firstMessage.current.length !== 0) {
        const response = await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            botCustomId: botCustomId,
            nodeTitle: firstMessage.current[0].nodeTitle,
            drawFlowId: "1",
            userAnswer: "NO_REPLY",
            variableData: variableData,
            userCredential: userCredential,
            nodeType: firstMessage.current[0].nodeType,
            contactId: contactData?._id,
            userData,
            chatSummary: {
              summary: chatSummary,
              nextSummaryStart: nextChatSummaryStart,
              pending: chatSummaryPending,
            },
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );

        roomID.current = response.data.roomId;
        connectSocket(response.data.roomId);
        if (response.data?.contact) {
          setContactData(response.data.contact);
          setLocalContactData(response.data.contact);
        }
        window.localStorage.setItem(
          "cbb-contact-id",
          response.data?.contact?._id,
        );
        _contactData = response.data?.contact;

        firstMessage.current = [];
      }

      // Mark streaming as started and cancel any pending nudge timers
      isStreamingRef.current = true;
      clearTimeout(nudgeTimer.current);
      clearTimeout(nudgeTimer2.current);
      clearTimeout(nudgeTimer3.current);
      if (imgUrl && caption) {
        try {
          await axios.post(
            `${environmentData.SERVER_API_URL}/ai/updateImageCaption`,
            {
              imgUrl,
              caption,
            },
          );
        } catch (err) {
          console.log("Error updating image caption:", err);
        }
      }

      const queryUrl = `${environmentData.SERVER_API_URL}/ai/query`;
      const response = await fetch(queryUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          queryRequestId: queryRequestId.current,
          query: isImage ? imgUrl : reply,
          language: botData?.aiBotConfig?.language,
          includeSource: botData.showDataSource,
          namespace: botData.botCustomId,
          streaming: true,
          previousChat: previousChat,
          totalMessages: totalMessages,
          configId: botData.aiBotConfig.configId,
          botId: botData._id,
          botCreatedAt: botData.createdAt,
          updateTime: new Date(botData.updatedAt).getTime(),
          companyId: botData.companyId,
          debug: debug.current,
          showSteps: steps.current,
          ip: variableData?.ip,
          socketRoomId: socketRoomId.current,
          botModel:
            (COMPARE_MODE &&
              compareConfigRef.current?.modelConfig?.modelName) ||
            botData.botModel,
          ...(COMPARE_MODE &&
          (compareInstanceId.current || compareConfigRef.current)
            ? {
                // Backend gates the runtime overlay on isPlaygroundQuery.
                // Prefer instanceId (server runs the stored patch as the
                // source of truth); inline config is a fallback applied on
                // top for unsaved edits not yet persisted to the instance.
                isPlaygroundQuery: true,
                compareMode: true,
                debug: true,
                runtimeParameters: {
                  ...(compareInstanceId.current
                    ? { instanceId: compareInstanceId.current }
                    : {}),
                  ...(compareConfigRef.current
                    ? {
                        config: {
                          ...(compareConfigRef.current.modelConfig
                            ? {
                                modelConfig:
                                  compareConfigRef.current.modelConfig,
                              }
                            : {}),
                          ...(compareConfigRef.current.customPrompt
                            ? {
                                customPrompt:
                                  compareConfigRef.current.customPrompt,
                              }
                            : {}),
                          ...(compareConfigRef.current.toolOverridesById?.length
                            ? {
                                toolOverridesById:
                                  compareConfigRef.current.toolOverridesById,
                              }
                            : {}),
                          ...(compareConfigRef.current.calendlyOverridesById
                            ?.length
                            ? {
                                calendlyOverridesById:
                                  compareConfigRef.current
                                    .calendlyOverridesById,
                              }
                            : {}),
                          ...(compareConfigRef.current.customGoals
                            ? {
                                customGoals:
                                  compareConfigRef.current.customGoals,
                              }
                            : {}),
                        },
                      }
                    : {}),
                },
              }
            : {}),
          humanAgentConfig: botData?.aiBotConfig?.humanAgentConfig ?? {},
          userResponseId: roomId ?? roomID.current,
          userData,
          contactId: _contactData?._id,
          isPreview: isPreviewModeChat,
          responseCacheControl: botData?.responseCacheControl,
          location: formattedLocation,
          customGoalsTriggered: customGoalsTriggered.current,
          creditId,
          isTrial,
          fromOutgrow,
          userPrompt: localStorage.getItem("userPrompt"),
          enableHyde: searchParams.get("hyde") === "true",
          maestroMode: searchParams.get("maestroMode"),
          isImageQuery: isImage,
          userAgent: navigator.userAgent,
          userId: botData?.userId,
          isAiBot: true,
          drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
          userAnswer: reply,
          botCustomId: botData?.botCustomId,
          variableData: variableData,
        }),
        signal: queryRequestSignal.current?.signal,
      });

      if (!response.ok) {
        let responseText = "";

        try {
          responseText = await response.text();
        } catch (responseReadError) {
          console.error(
            "Failed to read frontend query error body",
            responseReadError,
          );
        }

        const httpError = new Error(
          `LiveCalc: query request failed with status ${response.status}${responseText ? `, body: ${responseText.slice(0, 200)}` : ""}`,
        ) as Error & { statusCode?: number; requestUrl?: string };
        httpError.name = "FrontendQueryHttpError";
        httpError.statusCode = response.status;
        httpError.requestUrl = queryUrl;
        throw httpError;
      }
      const reader = (response.body ?? new ReadableStream<Uint8Array>())
        .pipeThrough(new TextDecoderStream() as any)
        .getReader();

      while (true) {
        let { value, done } = await reader.read();

        if (done === undefined || done) {
          // Mark streaming as done before triggering nudge
          isStreamingRef.current = false;

          if (!creditConsumedNotifiedRef.current) {
            creditConsumedNotifiedRef.current = true;
            console.log("[CREDIT_CONSUMED] postMessage fired to parent");
            window.parent.postMessage({ type: "CREDIT_CONSUMED" }, "*");
          }

          await triggerNudge(botData?.aiBotConfig?.nudgeConfig);
          setUsingToolLoader(false);

          if (showCalendlyBtn.current) {
            showCalendlyBtn.current = false;

            setMessage((prevItems: any) => {
              const tempMessage = structuredClone(prevItems);
              const lastMessage = tempMessage[tempMessage.length - 1];

              lastMessage.name = "show_schedule_call";

              return tempMessage;
            });
          }
          reasoning.stop(true);
          // Clear pending tool data after it's been attached to the message
          pendingToolData.current = {
            toolCalls: [],
            toolResults: [],
          };
          break;
        }

        if ((value as string)?.length) {
          hasReceivedStreamChunk.current = true;
        }

        // Detect the credit/rate-limit sentinel on the raw chunk before the
        // reasoning handler can buffer/swallow it.
        if ((value as string)?.includes("Rate_Limit_Exceeded")) {
          setIsAiRateLimitError(true);
          return "";
        }

        const { ongoing: reasoningOngoing } = reasoning.handleStream(
          value as string,
        );
        if (reasoningOngoing) continue;

        // value = reasoning.sanitizeTags(value as string);
        aiReply += value;

        if (aiReply?.includes("Rate_Limit_Exceeded")) {
          setIsAiRateLimitError(true);
          return "";
        }

        if (aiReply.includes("<<SHOW_RETRY_EXCEEDED>>")) {
          const sentinelIdx = aiReply.indexOf("<<SHOW_RETRY_EXCEEDED>>");
          const payloadStr = aiReply
            .slice(sentinelIdx + "<<SHOW_RETRY_EXCEEDED>>".length)
            .trim();
          try {
            const payload = JSON.parse(payloadStr);
            showRetryHandlerRef.current?.(payload);
            return aiReply.slice(0, sentinelIdx);
          } catch {
            continue;
          }
        }
        setMessage((prevItems: any) => {
          const tempMessage = structuredClone(prevItems);
          const lastMessage = tempMessage[tempMessage.length - 1];

          if (lastMessage.isLoader || lastMessage?.isTemp) {
            tempMessage.pop();
            tempMessage.push({
              author: ai,
              timestamp: new Date(),
              text: aiReply,
              aiResponse: true,
              toolCalls: pendingToolData.current.toolCalls,
              toolResults: pendingToolData.current.toolResults,
            });
          } else {
            if (lastMessage.author === ai) {
              lastMessage.text = aiReply;
            } else {
              tempMessage.push({
                author: ai,
                timestamp: new Date(),
                text: aiReply,
                aiResponse: true,
                toolCalls: pendingToolData.current.toolCalls,
                toolResults: pendingToolData.current.toolResults,
              });
            }
          }
          return tempMessage;
        });
      }
      return aiReply;
    }
  }

  async function handleUserReply(
    currentIndex,
    isPhone = false,
    replyMsg = newReply,
    regenerated,
    isImage = false,
    imgUrl = "",
    // imageQuery = "",
  ) {
    if (isChatInputLocked) return;

    try {
      setResponseCacheOptions(null);
      setSuggestedQuestionsData([]);
      setShowError(false);
      setVoiceError("");
      creditConsumedNotifiedRef.current = false;
      // const currentDataSource = dataSource;
      setDataSource([]);
      dataSourceRef.current = []; // Clear ref as well
      setIsAnswerSaved(false);
      customGoalsMsg.current = "";
      newReplyLast.current = replyMsg;
      lastSourceQuery.current = "";
      // pendingSaveAnswer.current = null;

      setIsCustomProviderError(false);

      if (handoffData.current.isConnected && handoffData.current.handoffType) {
        chatHandlerRef.current = handoffData.current.handoffType;
      }

      await triggerNudge(botData?.aiBotConfig?.nudgeConfig);
      // clearNudgeTimers();
      let reply = replyMsg;
      setNewReply("");
      if (!checkMsg) {
        setTempStore("");
        setStarterQues(undefined);
      }
      if (starterQues) {
        const updateMsgs = message.filter(
          (ele) => ele.text !== botData?.aiStarterQues?.initialMsg,
        );
        setMessage(updateMsgs);
        chatHandlerRef.current = "ai";
      }
      setIsAiResError(false);

      if (chatHandlerRef.current === "ai" || starterQues) {
        if (checkIndex) {
          const check = botData.aiBotConfig?.aiInitMessage;
          if (check) {
            chatHandlerRef.current = "web";
            setMessage((prevItems) => [
              ...prevItems,
              {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
            ]);
            setCurrentQuestionIndex(questions.length - 1);
          } else {
            setMessage((prevItems) => [
              ...prevItems,
              {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
            ]);
          }
          setCheckIndex(false);
        } else {
          let aiReply = "";
          try {
            aiReply = await handlefetchResponseFromAI(
              aiReply,
              reply,
              false,
              isImage,
              imgUrl,
              reply,
              // imageQuery,
            );
            if (aiReply?.includes("<handoff>")) {
              const match = aiReply.match(/<handoff>([\s\S]*?)<\/?handoff>/);
              const handoffData = JSON.parse(match?.[1] ?? "{}");

              if (handoffData?.isConnected) {
                handOffConnect(handoffData);
              } else {
                console.error("failed to connect to human agent", handoffData);
              }
            }
            if (aiReply?.includes("<imageDescription>")) {
              const match = aiReply.match(
                /<imageDescription>([\s\S]*?)<\/imageDescription>/,
              );

              const imgData = match?.[1] || "";
              reply = imgData;
              setMessage((prevItems: any) => {
                return prevItems.map((item: any) => {
                  if (
                    item.id === queryRequestId.current &&
                    item.isPendingImageText
                  ) {
                    return {
                      ...item,

                      text: reply,

                      isPendingImageText: false,
                    };
                  }

                  return item;
                });
              });
            }

            if (aiReply === "") {
              const isAborted =
                queryRequestSignal.current &&
                queryRequestSignal.current.signal.aborted === true;

              const errMsg = `LiveCalc: got empty response from backend, query - ${reply}`;
              console.error(errMsg);
              reportFrontendQueryError({
                query: reply,
                errorType: "empty_response",
                error: new Error(errMsg),
              });
            }
          } catch (err: any) {
            console.error(err);
            const isAborted =
              queryRequestSignal.current &&
              queryRequestSignal.current.signal.aborted === true;

            if (!isAborted) {
              const selectedModel =
                (COMPARE_MODE &&
                  compareConfigRef.current?.modelConfig?.modelName) ||
                botData?.botModel;
              if (
                isMissingModelSelection(selectedModel) ||
                isMissingModelValidationError(err)
              ) {
                reportFrontendQueryError({
                  query: reply,
                  errorType: "http",
                  error:
                    err instanceof Error
                      ? err
                      : new Error(
                          `LiveCalc: missing model validation error, query - ${reply}`,
                        ),
                  statusCode:
                    typeof err?.response?.status === "number"
                      ? err.response.status
                      : undefined,
                  requestUrl: `${environmentData.SERVER_API_URL}/liveBot/saveAnswer`,
                });

                setCooldownActive(false);
                setCooldownRemainingTime(0);
                setCooldownMessage(defaultConnectionIssueMessage);
                setSocketErrorDetail(
                  isPreviewModeChat ? SELECT_MODEL_DROPDOWN_MESSAGE : "",
                );
                setShowSocketErrorDetail(false);
                setIsCustomProviderError(true);
                setIsAiResError(false);
                return;
              }

              // TypeError from fetch => CORS / network
              // You cannot get a status/body here in the browser.
              console.error("Network/CORS error:", err?.name || err);
              const errMsg = err instanceof Error ? err.message : String(err);
              if (err instanceof Error) {
                const queryError = err as FrontendQueryTransportError;

                reportFrontendQueryError({
                  query: reply,
                  errorType:
                    typeof queryError.statusCode === "number"
                      ? "http"
                      : "network",
                  error: new Error(
                    `LiveCalc: while getting response, triggering fallback - msg: ${err.message}, code: ${(err as any).code ?? ""}, query - ${reply}`,
                  ),
                  statusCode:
                    typeof queryError.statusCode === "number"
                      ? queryError.statusCode
                      : undefined,
                  requestUrl:
                    typeof queryError.requestUrl === "string"
                      ? queryError.requestUrl
                      : `${environmentData.SERVER_API_URL}/ai/query`,
                });
                try {
                  clearTimeout(responseTimer.current);
                  handleResponseTimer();
                  setMessage((pre) =>
                    produce(pre, (draft: any[]) => {
                      const lastMessage = draft.at(-1);
                      if (!lastMessage) return;

                      if (lastMessage.author === ai) {
                        draft.pop();
                      }
                      draft.push({
                        author: ai,
                        timestamp: new Date(),
                        isLoader: true,
                        text: "Loading...",
                      });
                    }),
                  );
                  aiReply = await handlefetchResponseFromAI(
                    aiReply,
                    reply,
                    true,
                  );

                  return;
                } catch (error) {
                  console.error(
                    "Error occurred while fetching response from AI:",
                    error,
                  );
                  const fallbackError =
                    error instanceof Error
                      ? error
                      : new Error(
                          `LiveCalc: while getting fallback response - msg: ${err.message}, code: ${(err as any).code ?? ""}, query - ${reply}`,
                        );
                  reportFrontendQueryError({
                    query: reply,
                    errorType: "fallback_failed",
                    error: fallbackError,
                    statusCode:
                      typeof (error as any)?.statusCode === "number"
                        ? (error as any).statusCode
                        : undefined,
                    requestUrl:
                      typeof (error as any)?.requestUrl === "string"
                        ? (error as any).requestUrl
                        : `${environmentData.SERVER_API_URL}/ai/query`,
                  });
                }
              }
            }

            if (
              !(
                queryRequestSignal.current &&
                queryRequestSignal.current.signal.aborted === true
              )
            ) {
              setIsAiResError(true);
            }
          } finally {
            isStreamingRef.current = false; // Ensure streaming flag is reset on error/completion
            setResponseLoader(false);

            const isAborted =
              queryRequestSignal.current &&
              queryRequestSignal.current.signal.aborted === true;

            setMessage((pre) =>
              produce(pre, (draft: any[]) => {
                const lastMessage = draft.at(-1);
                if (!lastMessage) return;

                if (lastMessage.isLoader) {
                  draft.pop();
                } else if (isAborted) {
                  lastMessage.terminateByUser = true;

                  if (lastMessage.author === ai && draft.at(-2)) {
                    draft.at(-2).terminateByUser = true;
                  }
                }
              }),
            );

            console.log("[SaveAnswer Livebot] Sending tool data:", {
              toolCallsCount: pendingToolData.current.toolCalls.length,
              toolResultsCount: pendingToolData.current.toolResults.length,
              toolCalls: pendingToolData.current.toolCalls,
              toolResults: pendingToolData.current.toolResults,
            });

            // axiosPost(
            //   `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}`,
            //   {
            //     isAiBot: true,
            //     media: {
            //       type: isImage ? "image" : "text",
            //       fileUrl: imgUrl || "",
            //       caption: replyMsg,
            //     },
            //     botCustomId: botCustomId,
            //     nodeTitle: aiReply ?? "",
            //     drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            //     userAnswer: reply,
            //     variableName: null,
            //     variableData: variableData,
            //     userCredential,
            //     userData,
            //     regenerated,
            //     sources: dataSourceRef.current,
            //     chatSummary: {
            //       summary: chatSummary,
            //       nextSummaryStart: nextChatSummaryStart,
            //       pending: chatSummaryPending,
            //     },
            //     toolCalls: pendingToolData.current.toolCalls,
            //     toolResults: pendingToolData.current.toolResults,
            //     ...(isAborted && {
            //       terminateByUser: isAborted,
            //     }),
            //     model: botData?.botModel,
            //   },
            // )
            //   .then((botAnswer) => {
            //     setRoomId(roomID.current || botAnswer.data.roomId);
            //     if (botAnswer.data.contact) {
            //       setContactData(botAnswer.data.contact);
            //       setLocalContactData(botAnswer.data.contact);
            //     }

            //     if (botAnswer.data.answerId) {
            //       setIsAnswerSaved(true);
            //       setMessage((prevItems: any) => {
            //         const tempMessage = structuredClone(prevItems);

            //         const lastMessage = tempMessage[tempMessage.length - 1];

            //         if (lastMessage && lastMessage.aiResponse) {
            //           lastMessage.sources = dataSourceRef.current;
            //           lastMessage.id = botAnswer.data.answerId;
            //         }

            //         return tempMessage;
            //       });
            //     }
            //   })
            //   .catch((err) => {
            //     console.log(err);
            //   });

            return;
          }
        }

        const variableDataToSend = {};
        const varName = message[currentIndex]?.completeData?.variableName;
        if (varName) variableDataToSend[varName] = reply;

        // await axiosPost(
        //   `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
        //   {
        //     isAiBot: true,
        //     botCustomId: botCustomId,
        //     media: {
        //       type: isImage ? "image" : "text",
        //       fileUrl: imgUrl || "",
        //       caption: "",
        //     },
        //     nodeTitle: environmentData.isAI
        //       ? message[message.length - 1]?.name === "ask_question" ||
        //         message[message.length - 1]?.name === "email" ||
        //         message[message.length - 1]?.name === "job" ||
        //         message[message.length - 1]?.name === "name" ||
        //         message[message.length - 1]?.name === "address" ||
        //         message[message.length - 1]?.name === "company" ||
        //         message[message.length - 1]?.name === "phone"
        //         ? message[message.length - 1].text.value
        //         : message[message.length - 1].text
        //       : (message[message.length - 1].text ?? ""),
        //     drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
        //     userAnswer: reply,
        //     sources: dataSourceRef.current,
        //     variableName: varName,
        //     variableData: variableDataToSend,
        //     userCredential,
        //     model: botData?.botModel,
        //   },
        // );
      } else if (chatHandlerRef.current === "intercom") {
        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                author: person,

                timestamp: new Date(),

                text: replyMsg || "",

                caption: replyMsg || "",

                image: imgUrl,

                showImg: true,

                isPendingImageText: false,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/intercom", {
          configId: handoffData.current.configId,
          contactId: handoffData.current.payload.intercomUser?.id,
          conversationId: handoffData.current.payload.conversationId,
          message: reply,
          isImage,
          isAudio: false,
          caption: isImage ? (replyMsg ?? "") : "",
        });

        await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            media: {
              type: isImage ? "image" : "text",
              fileUrl: imgUrl || "",
              caption: replyMsg || "",
            },
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply || "",
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "zendesk") {
        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                author: person,

                timestamp: new Date(),

                text: replyMsg || "",

                caption: replyMsg || "",

                image: imgUrl,

                showImg: true,

                isPendingImageText: false,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/zendesk", {
          configId: handoffData.current.configId,
          authorId: String(handoffData.current.payload.zendeskUser?.id),
          ticketId: handoffData.current.payload.conversationId,
          message: reply,
          isImage,
          isAudio: false,
          caption: isImage ? (replyMsg ?? "") : "",
        });

        await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            media: {
              type: isImage ? "image" : "text",
              fileUrl: imgUrl || "",
              caption: replyMsg || "",
            },
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply || "",
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "cbb-live-agent") {
        //cbb-live-agent image uplaod pending......!
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/cbb-live-agent", {
          conversationId: handoffData.current.payload.conversationId,
          message: reply,
        });

        await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "inhouse") {
        const activeRoomId = roomId || roomID.current;
        if (!activeRoomId) throw new Error("Missing inhouse conversation id");

        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                author: person,

                timestamp: new Date(),

                text: replyMsg || "",

                caption: replyMsg || "",

                image: imgUrl,

                showImg: true,

                isPendingImageText: false,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        const inhouseSaveResponse = await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${activeRoomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            media: {
              type: isImage ? "image" : "text",
              fileUrl: imgUrl || "",
              caption: replyMsg || "",
            },
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply || "",
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );

        if (inhouseSaveResponse?.data?.roomId) {
          roomID.current = inhouseSaveResponse.data.roomId;
          setRoomId(inhouseSaveResponse.data.roomId);
        } else {
          roomID.current = activeRoomId;
          setRoomId(activeRoomId);
        }
      } else if (chatHandlerRef.current === "crisp") {
        // setMessage((prevItems) => [
        //   ...prevItems,
        //   {
        //     author: person,
        //     timestamp: new Date(),
        //     text: reply,
        //   },
        // ]);

        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                author: person,

                timestamp: new Date(),

                text: replyMsg || "",

                caption: replyMsg || "",

                image: imgUrl,

                showImg: true,

                isPendingImageText: false,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/crisp", {
          configId: handoffData.current.configId,
          conversationId: handoffData.current.payload.conversationId,
          message: isImage ? imgUrl : reply,
          isImage,
          isAudio: false,
          caption: isImage ? (replyMsg ?? "") : "",
        });

        await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            media: {
              type: isImage ? "image" : "text",
              fileUrl: imgUrl || "",
              caption: replyMsg || "",
            },
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply || "",
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "slack") {
        // setMessage((prevItems) => [
        //   ...prevItems,
        //   {
        //     author: person,
        //     timestamp: new Date(),
        //     text: reply,
        //   },
        // ]);

        setMessage((prevItems) => [
          ...prevItems,
          isImage
            ? {
                author: person,

                timestamp: new Date(),

                text: replyMsg || "",

                caption: replyMsg || "",

                image: imgUrl,

                showImg: true,

                isPendingImageText: false,
              }
            : {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/slack", {
          configId: handoffData.current.configId,
          conversationId: handoffData.current.payload.conversationId,
          message: isImage ? imgUrl : reply,
          isImage,
          isAudio: false,
          caption: isImage ? (replyMsg ?? "") : "",
          threadTs: handoffData.current.payload.threadTs,
          userName: userCredential?.name,
        });

        const activeRoomId = getActiveRoomId();

        const slackSaveResponse = await saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${activeRoomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            media: {
              type: isImage ? "image" : "text",
              fileUrl: imgUrl || "",
              caption: replyMsg || "",
            },
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply || "",
            variableName: null,
            variableData: variableData,
            userCredential,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        );

        if (slackSaveResponse?.data?.roomId) {
          roomID.current = slackSaveResponse.data.roomId;
          setRoomId(slackSaveResponse.data.roomId);
        }
      } else {
        const tempMessage = [...message];
        tempMessage[message.length - 1].hasAnswered = true;
        setMessage(tempMessage);

        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        const variableDataToSend = {};
        const varName = message[currentIndex]?.completeData?.variableName;
        if (varName) variableDataToSend[varName] = reply;

        const userCredentialData = { ...userCredential };
        if (message[message.length - 1]?.name === "email")
          userCredentialData.email = reply;
        if (message[message.length - 1]?.name === "phone")
          userCredentialData.phone = reply;
        if (message[message.length - 1]?.name === "username")
          userCredentialData.name = reply;
        setUserCredential(userCredentialData);

        let newNodeTitle = structuredClone([...message].slice(-1)[0]);

        if (startLiveChat && newNodeTitle?.author !== 0) {
          newNodeTitle.text = "";
        }

        const tempDrawFlowId =
          (startLiveChat
            ? liveChatNode?.drawFlowId
            : questions[currentQuestionIndex]?.drawFlowId) ??
          questions[currentQuestionIndex]?.drawFlowId;

        saveAnswerPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}&isLiveChat=${startLiveChat}`,
          {
            botCustomId: botCustomId,
            nodeTitle: newNodeTitle.text.value ?? newNodeTitle.text ?? "",
            drawFlowId: tempDrawFlowId,
            userAnswer: reply,
            sources: dataSourceRef.current,
            variableName: varName,
            variableData: variableDataToSend,
            userCredential: userCredentialData,
            isAiBot: true,
            model:
              (COMPARE_MODE &&
                compareConfigRef.current?.modelConfig?.modelName) ||
              botData?.botModel,
          },
        ).then((response) => {
          if (roomId === null) {
            setRoomId(response.data.roomId);
          }
          if (!startLiveChat) {
            if (
              questions[currentQuestionIndex]?.outputs[0].connections !==
              undefined
            ) {
              let currentIndex = questions.findIndex(
                (x) =>
                  x.drawFlowId ===
                  questions[currentQuestionIndex]?.outputs[0].connections.node,
              );
              setCurrentQuestionIndex(currentIndex);
            } else {
              if (
                questions[currentQuestionIndex]?.outputs[0]?.connections !==
                undefined
              ) {
                let currentIndex = questions.findIndex(
                  (x) =>
                    x.drawFlowId ===
                    questions[currentQuestionIndex]?.outputs[0].connections
                      .node,
                );
                setCurrentQuestionIndex(currentIndex);
              } else {
                setIsLast(true);
              }
            }
          }
        });
      }
    } catch (err) {
      setMessage((prevItems: any) => {
        if (prevItems[prevItems.length - 1]?.isLoader) {
          const tempMessage = structuredClone(prevItems);
          tempMessage.pop();
          return tempMessage;
        }
        return prevItems;
      });
      console.log(err);
    } finally {
      isSkip.current = true;
      setUsingToolLoader(false);
    }
  }

  interface Conversation {
    role: "user" | "assistant" | "tool";
    content: string | any[]; // Can be string or array for tool calls/results
  }

  function generatePreviousChat() {
    const SUMMARY_THRESHOLD = 30; // after how many message summarization will be triggered

    const conversation: Conversation[] = [];

    for (const data of message) {
      if (data.terminateByUser) continue;

      const content = data?.text?.value ?? data?.text;

      // Add the main text message
      conversation.push({
        role: data.author ? "user" : "assistant",
        content: content,
      });

      // If this is an assistant message with tool calls, add them
      if (!data.author && data.toolCalls && data.toolCalls.length > 0) {
        const formattedToolCalls = data.toolCalls.map((tc: any) => ({
          type: "tool-call",
          toolCallId: tc.toolCallId,
          toolName: tc.toolName,
          input: tc.input || tc.args,
        }));

        conversation.push({
          role: "assistant",
          content: formattedToolCalls,
        });

        // If there are tool results, add them as well
        if (data.toolResults && data.toolResults.length > 0) {
          const formattedToolResults = data.toolResults.map((tr: any) => ({
            type: "tool-result",
            toolCallId: tr.toolCallId,
            toolName: tr.toolName || tr.tool,
            output: tr.output || tr.result,
          }));

          conversation.push({
            role: "tool",
            content: formattedToolResults,
          });
        }
      }
    }

    // If the last request was cancelled by the user, strip the incomplete exchange
    // (user question + partial AI answer) before sending to /query. This handles the
    // race condition where setMessage(terminateByUser) hasn't committed yet when the
    // next question is already being sent.
    if (wasLastRequestAborted.current) {
      wasLastRequestAborted.current = false;
      // Remove trailing assistant entries (partial answer)
      while (conversation.length > 0 && conversation.at(-1)!.role !== "user") {
        conversation.pop();
      }
      // Remove the cancelled user question itself
      if (conversation.length > 0 && conversation.at(-1)!.role === "user") {
        conversation.pop();
      }
    }

    if (conversation.length - nextChatSummaryStart >= SUMMARY_THRESHOLD) {
      summarizeChat(
        conversation.slice(nextChatSummaryStart),
        conversation.length,
      );
    }

    const previousChat = conversation.slice(nextChatSummaryStart);

    if (chatSummary) {
      previousChat.unshift({
        role: "assistant",
        content: chatSummary,
      });
    }

    return { previousChat, totalMessages: conversation.length };
  }

  async function summarizeChat(
    conversation: Conversation[],
    conversationLen: number,
  ) {
    try {
      if (chatSummaryPending) return;
      setChatSummaryPending(true);

      if (chatSummary) {
        conversation.unshift({
          role: "assistant",
          content: chatSummary,
        });
      }

      const res = await axiosPost("/ai/summarize-chat", {
        conversation,
        botCustomId: botData.botCustomId,
        updateTime: new Date(botData.updatedAt).getTime(),
        companyId: botData.companyId,
      });

      if (res.data?.data?.summary) {
        setChatSummary(res.data.data.summary);
        setNextChatSummaryStart(conversationLen);
        setScrapedData(mergeData(scrapedData, res.data.data.scrappedData));
      }

      setChatSummaryPending(false);
    } catch (err) {
      console.log("Chat summary: ", err);
      setChatSummaryPending(false);
    }
  }

  const handleEnterSubmit = (
    currentIndex,
    isPhone = false,
    isEmail = false,
    isImage = false,
    imgUrl = "",
  ) => {
    setShowError(true);
    if ((!newReply && !isImage) || !isReplyValid) return;
    handleUserReply(
      currentIndex,
      isPhone,
      newReply,
      regenerate.current,
      isImage,
      imgUrl,
    );
    if (isImage) clearPendingImage();
    setShowError(false);
  };

  const handleSendReplyButton = (event: any = false) => {
    if (isChatInputDisabled) return;

    setResponseCacheOptions(null);
    if (debounce.current) return;
    debounce.current = true;
    setTimeout(() => {
      debounce.current = false;
    }, 1000);
    if (event) event.preventDefault();
    if (pendingImage?.isUploading) return;
    const pendingImageUrl = pendingImage?.fileUrl || "";
    const hasReadyPendingImage = Boolean(
      pendingImageUrl && pendingImage && !pendingImage.error,
    );
    if (!newReply.trim().length && !hasReadyPendingImage) return;
    if (message[message.length - 1]?.isLoader) return;

    if (COMPARE_MODE && !suppressCompareOutgoingRef.current) {
      postCompareMessage({ type: "COMPARE_USER_SUBMIT", text: newReply });
    }

    {
      const lastIndex = message.length ? message.length - 1 : 0;
      if (
        message[lastIndex]?.name == "email" &&
        message[lastIndex]?.text?.isValidate &&
        isSkip.current &&
        !hasReadyPendingImage
      ) {
        axiosPost("/liveBot/emailValidation", { newReply }).then((res) => {
          if (!res.data.status) {
            setCheckValidate(true);
            setError(
              EMAIL_VALIDATION_MESSAGE[
                botData?.aiBotConfig?.language?.toLowerCase()
              ] || EMAIL_VALIDATION_MESSAGE.english,
            );
            return;
          }
          setCheckValidate(false);
          setError("");
          handleEnterSubmit(
            lastIndex,
            false,
            false,
            hasReadyPendingImage,
            pendingImageUrl,
          );
        });
      } else {
        handleEnterSubmit(
          lastIndex,
          false,
          false,
          hasReadyPendingImage,
          pendingImageUrl,
        );
      }
    }
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset to single-line baseline (or 42 as fallback before first measure)
      textarea.style.height = `${singleLineHeightRef.current || 42}px`;
    }
  };

  const debounce = useRef<any>();

  const handleReaction = async (msg, reaction) => {
    try {
      setMessage((prevItems) =>
        prevItems.map((item) =>
          item.id === msg.id ? { ...item, reaction: reaction } : item,
        ),
      );
      // reaction true means helpful & vice-versa
      socket.emit("response-reaction", {
        botCustomId,
        cacheResponseId: responseCacheOptions?.id,
        reaction: reaction,
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleMicClick = () => {
    if (isChatInputDisabled || responseLoader) return;
    if (voiceError && canRetryVoicePermission) return;
    startRecording();
  };

  const startRecording = async () => {
    try {
      setVoiceError("");
      setCanRetryVoicePermission(false);

      if (!navigator.mediaDevices?.getUserMedia) {
        setVoiceError(
          "Microphone permission is required to record a voice message, but this browser does not support microphone access.",
        );
        setCanRetryVoicePermission(false);
        return;
      }

      if (typeof MediaRecorder === "undefined") {
        setVoiceError(
          "Voice recording is not supported in this browser. Please try a different browser.",
        );
        setCanRetryVoicePermission(false);
        return;
      }

      const userAgent = navigator.userAgent.toLowerCase();

      const isSafari =
        userAgent.includes("safari") && !userAgent.includes("chrome");

      const isFirefox = userAgent.includes("firefox");

      const preferredMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mimeType =
        preferredMimeTypes.find((type) =>
          MediaRecorder.isTypeSupported(type),
        ) || "";

      console.log("Using mimeType:", mimeType || "browser default");

      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: { ideal: true },
        noiseSuppression: { ideal: true },
        autoGainControl: { ideal: true },
        channelCount: { ideal: 1 },
        sampleSize: { ideal: 16 },
      };

      if (!isSafari && !isFirefox) {
        audioConstraints.sampleRate = { ideal: 48000 };
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      const recorderOptions: MediaRecorderOptions = {
        audioBitsPerSecond: 128000,
      };

      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }

      const mediaRecorder = new MediaRecorder(stream, recorderOptions);

      audioRecorderRef.current = mediaRecorder;

      audioChunksRef.current = [];
      setAudioData([]);
      setRecordingTime(0);
      setIsRecording(true);

      // ✅ REAL binary audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          console.log("Audio chunk:", event.data.size);

          audioChunksRef.current.push(event.data);
          setAudioData((prev) => [...prev, event.data]);
        }
      };

      // ✅ Start recording
      // larger chunks = cleaner audio
      mediaRecorder.start(1000);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      console.log("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);

      setIsRecording(false);
      const errorName = error instanceof DOMException ? error.name : "";
      const isPermissionError =
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError" ||
        errorName === "SecurityError";

      setCanRetryVoicePermission(true);
      setVoiceError(
        isPermissionError
          ? "Microphone permission is required to record a voice message. Please allow microphone access and try again."
          : "Unable to access the microphone. Please check your microphone permission and try again.",
      );
    }
  };

  const stopRecording = async () => {
    return new Promise<void>((resolve) => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);

        recordingTimerRef.current = null;
      }

      if (audioRecorderRef.current) {
        // ✅ Wait for final chunk
        audioRecorderRef.current.onstop = () => {
          // ✅ stop mic tracks
          audioRecorderRef.current?.stream
            ?.getTracks()
            ?.forEach((track) => track.stop());

          resolve();
        };

        if (audioRecorderRef.current.state !== "inactive") {
          audioRecorderRef.current.stop();
        } else {
          resolve();
        }
      } else {
        resolve();
      }

      setIsRecording(false);
    });
  };

  const sendAudioForTranscription = async () => {
    await stopRecording();
    setVoiceError("");
    setIsCustomProviderError(false);
    setIsAiResError(false);
    setIsAiRateLimitError(false);
    const audioChunks = audioChunksRef.current;
    if (!audioChunks.length) return;

    setIsTranscribing(true);

    try {
      const recorderMimeType = audioRecorderRef.current?.mimeType;
      const mimeType = recorderMimeType || audioChunks[0]?.type || "audio/webm";

      const audioBlob = new Blob(audioChunks, {
        type: mimeType,
      });

      const filename = mimeType.includes("ogg")
        ? "voice-note.ogg"
        : mimeType.includes("mp4")
          ? "voice-note.m4a"
          : mimeType.includes("webm")
            ? "voice-note.webm"
            : "voice-note.mp3";

      const formData = new FormData();
      formData.append("audio", audioBlob, filename);
      if (botData?._id) formData.append("botId", String(botData._id));
      if (botData?.companyId) {
        formData.append("companyId", String(botData.companyId));
      }
      const transcriptionBotCustomId = botData?.botCustomId || botCustomId;
      if (transcriptionBotCustomId) {
        formData.append("botCustomId", String(transcriptionBotCustomId));
        formData.append("namespace", String(transcriptionBotCustomId));
      }
      if (botData?.aiBotConfig?.configId) {
        formData.append("configId", String(botData.aiBotConfig.configId));
      }

      const response = await axios.post(
        `${environmentData.SERVER_API_URL}/ai/transcribe`,
        formData,
      );

      const transcribedText =
        response?.data?.text || response?.data?.transcription || "";

      console.log("Transcription:", transcribedText);

      console.log(
        "[CREDIT_CONSUMED] /ai/transcribe postMessage fired to parent",
      );
      window.parent.postMessage({ type: "CREDIT_CONSUMED" }, "*");

      if (!transcribedText.trim()) {
        throw new Error("Transcription returned no text");
      }

      setEditable(true);
      setNewReply((prev) =>
        prev.trim()
          ? `${prev.trim()} ${transcribedText.trim()}`
          : transcribedText.trim(),
      );
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.focus();
        const end = textarea.value.length;
        try {
          textarea.setSelectionRange(end, end);
        } catch {}
        adjustTextareaHeight();
      });
    } catch (error) {
      console.error("Error transcribing audio:", error);
      const limitHit = getErrorText(error)
        .toLowerCase()
        .includes("credit limit");
      setVoiceError(
        limitHit
          ? defaultQuestionLimitReachedMessage
          : "Unable to transcribe voice message. Please try again.",
      );
      setCanRetryVoicePermission(false);
    } finally {
      audioChunksRef.current = [];
      setAudioData([]);
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };
  const renderVoiceErrorBanner = (className = "") => {
    if (!voiceError) return null;

    return (
      <div
        role="alert"
        className={`mx-auto mb-[10px] flex w-[calc(100%-24px)] max-w-[680px] items-center justify-between gap-[10px] rounded-[8px] border border-[#FB5F66]/40 bg-[#FFF1F1] px-[12px] py-[9px] text-[13px] font-medium leading-[18px] text-[#D01C24] ${className}`}
      >
        <span>{voiceError}</span>
        {canRetryVoicePermission && (
          <button
            type="button"
            className="shrink-0 rounded-[6px] border border-[#FB5F66]/40 px-[8px] py-[4px] text-[12px] font-semibold"
            onClick={startRecording}
          >
            Try again
          </button>
        )}
      </div>
    );
  };

  const clearPendingImage = () => {
    pendingImageUploadId.current += 1;
    setPendingImage(null);
    setIsUploadingImage(false);
  };

  const deleteUploadedPendingImage = async (image: { fileUrl: string }) => {
    if (!image.fileUrl) return;

    try {
      await axios.post(
        `${environmentData.SERVER_API_URL}/ai/imageDeleteFromBucket`,
        {
          fileUrl: image.fileUrl,
        },
      );
    } catch (error) {
      console.error("Failed to delete image from S3:", error);
    }
  };

  const handleRemovePendingImage = () => {
    const imageToDelete = pendingImage;
    clearPendingImage();

    if (imageToDelete?.fileUrl && !imageToDelete.error) {
      deleteUploadedPendingImage(imageToDelete);
    }
  };

  const uploadImageFile = async (file: File) => {
    const uploadId = pendingImageUploadId.current + 1;
    pendingImageUploadId.current = uploadId;
    const previewUrl = URL.createObjectURL(file);

    setPendingImage({
      previewUrl,
      fileUrl: "",
      name: file.name,
      isUploading: true,
    });
    setIsUploadingImage(true);
    console.log(botData);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("inputType", "image");
      const response = await axios.post(
        `${environmentData.SERVER_API_URL}/ai/imageTranscribe`,
        formData,
      );

      const imageUrl = response?.data?.fileUrl || "";
      if (!imageUrl) throw new Error("Image upload did not return a file URL");
      // "https://cdn-icons-png.flaticon.com/512/4159/4159053.png?w=360";

      // const transcribedText =
      //   "in this image there is a big music logo of light pink color. ";

      // const messageText = [
      //   "User uploaded an image.",

      //   "The system analyzed the image and converted its contents into text for your understanding.",

      //   "Do not mention OCR, extraction, or that the image was converted into text.",

      //   "Respond naturally as if you directly understood the image.",

      //   transcribedText,
      // ]
      //   .filter(Boolean)
      //   .join("\n");

      // const finalUserMessage = [
      //   "User previously uploaded an image in the conversation.",
      //   "The following text describes the uploaded image.",
      //   "Respond naturally as if you understood and received the image.",
      //   `Internal image reference: ${imageUrl}`,
      //   transcribedText,
      // ]
      //   .filter(Boolean)
      //   .join("\n");

      // const imageQuery = messageText;

      setPendingImage((currentImage) => {
        if (pendingImageUploadId.current !== uploadId || !currentImage) {
          return currentImage;
        }

        return {
          ...currentImage,
          previewUrl: imageUrl,
          fileUrl: imageUrl,
          isUploading: false,
          error: undefined,
        };
      });
    } catch (error) {
      console.error("Image upload/transcription failed:", error);
      setPendingImage((currentImage) => {
        if (pendingImageUploadId.current !== uploadId || !currentImage) {
          return currentImage;
        }

        return {
          ...currentImage,
          isUploading: false,
          error: "Upload failed",
        };
      });
    } finally {
      if (pendingImageUploadId.current === uploadId) {
        setIsUploadingImage(false);
      }
    }
  };

  const handleImageSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isChatInputDisabled || responseLoader) return;

    await uploadImageFile(file);
  };

  const handleImagePaste = async (
    event: React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    if (isChatInputDisabled || responseLoader || isUploadingImage) {
      return;
    }

    const clipboardItems = Array.from(event.clipboardData?.items || []);
    const imageItem = clipboardItems.find((item) =>
      item.type.startsWith("image/"),
    );
    const pastedImage =
      imageItem?.getAsFile() ||
      Array.from(event.clipboardData?.files || []).find((file) =>
        file.type.startsWith("image/"),
      );

    if (!pastedImage) return;

    event.preventDefault();
    await uploadImageFile(pastedImage);
  };
  const cancelRecording = async () => {
    await stopRecording();
    audioChunksRef.current = [];
    setAudioData([]);
    setRecordingTime(0);
  };
  function adjustTextareaHeight() {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const minHeight = Math.max(singleLineHeightRef.current || 0, 42);
    const maxHeight = 120;

    // Disable CSS transitions momentarily so scrollHeight reflects the true content
    // size, not an animating intermediate box height.
    textarea.style.transition = "none";
    textarea.style.height = "auto";
    const contentHeight = textarea.scrollHeight;

    const finalHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    textarea.style.height = `${finalHeight}px`;

    const hasScrollbar = contentHeight > maxHeight;
    setHasTextareaScrollbar((previous) =>
      previous === hasScrollbar ? previous : hasScrollbar,
    );

    // Restore CSS transitions (empty string = defer to stylesheet / class).
    // Force a reflow first so future transitions start from the settled height.
    textarea.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
    textarea.style.transition = "";

    textarea.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
    if (contentHeight <= maxHeight) textarea.scrollTop = 0;

    setIsScrolledToBottom(
      Math.abs(
        textarea.scrollHeight - textarea.scrollTop - textarea.clientHeight,
      ) <= 15,
    );

    const minRadius = 24;
    const maxRadius = 100;
    const ratio = (finalHeight - minHeight) / (maxHeight - minHeight);
    const newRadius = Math.max(
      minRadius,
      maxRadius - ratio * (maxRadius - minRadius),
    );
    setBorderRadius(newRadius);
  }

  // MOUNT EFFECT — capture the true single-line scrollHeight as our wrapping baseline.
  // We measure once (with the empty textarea in narrow-padding context) so we have an
  // accurate reference point instead of hardcoding 42.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const prevTransition = textarea.style.transition;
    const prevHeight = textarea.style.height;
    textarea.style.transition = "none";
    textarea.style.height = "1px";
    singleLineHeightRef.current = textarea.scrollHeight || 42;
    textarea.style.height = prevHeight;
    textarea.style.transition = prevTransition;
  }, []); // run once on mount

  // EFFECT 1 — drive isMultiline by detecting actual line-wrapping, not just "has text".
  //
  // Entry  (isMultiline=false → true):
  //   Measure scrollHeight in the current narrow layout. If content exceeds one line,
  //   store textarea.clientWidth in narrowWidthRef (the stable narrow reference) and
  //   switch to multiline.
  //
  // Exit   (isMultiline=true → false):
  //   Temporarily simulate the narrow layout by forcing the stored narrow width and
  //   narrow vertical padding. If content now fits on one line, revert to single-line.
  //   No oscillation: the exit measurement always uses the stored narrow width as the
  //   reference frame, so a text length that fits there will still fit after reverting.
  useLayoutEffect(() => {
    if (!newReply) {
      setIsMultiline(false);
      return;
    }

    const textarea = textareaRef.current;
    if (!textarea) return;
    const baseline = singleLineHeightRef.current || 42;

    if (isMultiline) {
      // --- Exit check: does text still need more than one line in narrow layout? ---
      // Skip if we have no stored narrow width (edge case on initial mount with pre-filled text).
      if (!narrowWidthRef.current) return;

      // Temporarily simulate narrow-layout measurement context:
      //   • Force the stored narrow width (overrides the !w-full class with !important)
      //   • Apply narrow vertical padding (matches mount-time baseline: 8px top / 5px bottom)
      //   • Collapse height to 1px so scrollHeight = true content height
      const prevTransition = textarea.style.transition;
      const prevHeight = textarea.style.height;
      const prevPT = textarea.style.paddingTop;
      const prevPB = textarea.style.paddingBottom;
      const prevPL = textarea.style.paddingLeft;
      const prevPR = textarea.style.paddingRight;

      textarea.style.transition = "none";
      textarea.style.height = "1px";
      textarea.style.paddingTop = "8px";
      textarea.style.paddingBottom = "5px";
      textarea.style.setProperty(
        "padding-left",
        narrowPaddingLeftRef.current,
        "important",
      );
      textarea.style.setProperty(
        "padding-right",
        narrowPaddingRightRef.current,
        "important",
      );
      textarea.style.setProperty(
        "width",
        `${narrowWidthRef.current}px`,
        "important",
      );

      const scrollH = textarea.scrollHeight;

      textarea.style.removeProperty("width");
      textarea.style.removeProperty("padding-left");
      textarea.style.removeProperty("padding-right");
      textarea.style.height = prevHeight;
      textarea.style.paddingTop = prevPT;
      textarea.style.paddingBottom = prevPB;
      if (prevPL) textarea.style.paddingLeft = prevPL;
      if (prevPR) textarea.style.paddingRight = prevPR;
      textarea.style.transition = prevTransition;

      if (scrollH <= baseline + 1) {
        setIsMultiline(false);
      }
      return;
    }

    // --- Entry check: does text wrap in the current narrow layout? ---
    const prevTransition = textarea.style.transition;
    const prevHeight = textarea.style.height;
    textarea.style.transition = "none";
    textarea.style.height = "1px";
    const scrollH = textarea.scrollHeight;
    textarea.style.height = prevHeight;
    textarea.style.transition = prevTransition;

    if (scrollH > baseline + 1) {
      narrowWidthRef.current = textarea.clientWidth; // capture before layout switches to wide
      const computed = window.getComputedStyle(textarea);
      narrowPaddingLeftRef.current = computed.paddingLeft;
      narrowPaddingRightRef.current = computed.paddingRight;
      setIsMultiline(true);
    }
  }, [newReply, isMultiline]);

  // EFFECT 2 — measure and set textarea height.
  // Depends on BOTH newReply (text changed) AND isMultiline (layout/width changed).
  // By the time this runs, Effect 1 has already updated isMultiline, React has
  // re-rendered, and the DOM has the correct final width — so scrollHeight is accurate.
  // adjustTextareaHeight() does NOT call setIsMultiline → no feedback loop possible.
  useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [newReply, isMultiline]);

  // The embed width can change without the draft changing (iframe resize,
  // compare mode, or a responsive breakpoint). Re-measure in that case so
  // wrapped lines never remain clipped at the previous width.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const container = textarea?.parentElement;
    if (!textarea || !container || typeof ResizeObserver === "undefined") {
      return;
    }

    let previousWidth = container.clientWidth;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      if (Math.abs(nextWidth - previousWidth) < 1) return;
      previousWidth = nextWidth;
      adjustTextareaHeight();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [isMultiline]);

  function handleButtomInput(event: any, isCheckBox: boolean = false) {
    const value = event.target.value;
    setIsTyping(true);
    setHasUserScrolledTextarea(false);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 220);
    setNewReply(value);
    if (botData?.aiBotConfig?.showTypingSuggestions) sendUserQuery(value);
  }

  const [currentElem, setCurrentElem] = useState<any>(null);

  const stopScroll = () => {
    if (currentElem) {
      const wrapperElement = document.querySelector(
        ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
      ) as HTMLElement;

      if (wrapperElement) {
        const wrapperRect = wrapperElement.getBoundingClientRect();
        const elemRect = currentElem.getBoundingClientRect();
        const relativeTop =
          elemRect.top - wrapperRect.top + wrapperElement.scrollTop - 1000;
        wrapperElement.scrollTo({
          top: relativeTop,
          behavior: "smooth",
        });
      } else {
        const top =
          currentElem.getBoundingClientRect().top + window.scrollY - 1000;
        window.scrollTo({
          top,
          behavior: "smooth",
        });
      }
    }
  };

  function connectSocket(roomId: string) {
    socket.connect();

    const joinLiveCalcRoom = () => {
      socket.emit("join", {
        roomId: socketRoomId.current,
        senderId: roomId,
      });
    };

    if (socket.connected) {
      joinLiveCalcRoom();
      return;
    }

    socket.once("connect", joinLiveCalcRoom);
  }

  useEffect(() => {
    const lastMessage = message[message.length - 1];
    if (
      lastMessage?.aiResponse &&
      lastMessage?.sources &&
      isAnswerSaved &&
      dataSource.length > 0
    ) {
      setDataSource([]);
      dataSourceRef.current = [];
    }
  }, [message, isAnswerSaved]);

  useEffect(() => {
    const handleContactUpdate = (data: any) => {
      if (data && contactData && data._id === contactData._id) {
        // preserve the fieldData structure merge if needed, but usually full object is sent
        setContactData(data);
        setLocalContactData(data);
      }
    };

    socket.on("contactUpdate", handleContactUpdate);

    return () => {
      socket.off("contactUpdate", handleContactUpdate);
    };
  }, [contactData]);

  useEffect(() => {
    return () => {
      socket.disconnect();
    };
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (COMPARE_MODE) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!embed) event.preventDefault();
      if (!socket) return;
      const activeRoomId = roomId || roomID.current;
      if (activeRoomId) {
        socket.emit("customer-close", { conversationId: activeRoomId });
      }
      socket.disconnect();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [roomId, embed]);

  useEffect(() => {
    if (!COMPARE_MODE) return;
    const handleCompareBroadcast = (e: MessageEvent) => {
      if (e.data?.source !== "compare") return;
      if (e.data?.type !== "COMPARE_BROADCAST_MESSAGE") return;
      const text = typeof e.data.text === "string" ? e.data.text : "";
      const imageUrl =
        typeof e.data.imageUrl === "string" ? e.data.imageUrl : "";
      if (!text.trim().length && !imageUrl) return;
      pendingCompareBroadcastRef.current = {
        text,
        ...(imageUrl ? { imageUrl } : {}),
      };
      if (imageUrl) {
        setPendingImage({
          previewUrl: imageUrl,
          fileUrl: imageUrl,
          name: "uploaded-image",
          isUploading: false,
        });
      }
      setNewReply(text);
      setCompareBroadcastTrigger((n) => n + 1);
    };
    window.addEventListener("message", handleCompareBroadcast);
    return () => window.removeEventListener("message", handleCompareBroadcast);
  }, []);

  useEffect(() => {
    if (!COMPARE_MODE) return;
    const handleCompareConfig = (e: MessageEvent) => {
      if (e.data?.source !== "compare") return;
      if (e.data?.type !== "COMPARE_CONFIG") return;
      const cfg = e.data.config;
      if (!cfg || typeof cfg !== "object") return;
      const mc =
        cfg.modelConfig && typeof cfg.modelConfig === "object"
          ? cfg.modelConfig
          : undefined;
      compareConfigRef.current = {
        ...(mc
          ? {
              modelConfig: {
                ...(typeof mc.modelName === "string"
                  ? { modelName: mc.modelName }
                  : {}),
                ...(typeof mc.temperature === "number"
                  ? { temperature: mc.temperature }
                  : {}),
                ...(mc.thinkingConfig && typeof mc.thinkingConfig === "object"
                  ? { thinkingConfig: mc.thinkingConfig }
                  : {}),
                ...(mc.providerOptions && typeof mc.providerOptions === "object"
                  ? { providerOptions: mc.providerOptions }
                  : {}),
              },
            }
          : {}),
        ...(typeof cfg.customPrompt === "string"
          ? { customPrompt: cfg.customPrompt }
          : {}),
        ...(Array.isArray(cfg.toolOverridesById)
          ? { toolOverridesById: cfg.toolOverridesById }
          : {}),
        ...(Array.isArray(cfg.calendlyOverridesById)
          ? { calendlyOverridesById: cfg.calendlyOverridesById }
          : {}),
        ...(cfg.customGoals && typeof cfg.customGoals === "object"
          ? { customGoals: cfg.customGoals }
          : {}),
      };
    };
    window.addEventListener("message", handleCompareConfig);

    postCompareMessage({ type: "COMPARE_READY" });
    return () => window.removeEventListener("message", handleCompareConfig);
  }, []);

  // Compare: push the local message array up to the parent on every change so
  // the builder owns the history. Debounced so streaming responses don't spam
  // postMessage. Suppressed for one tick when we just received a restore.
  useEffect(() => {
    if (!COMPARE_MODE) return;
    if (isRestoringCompareHistoryRef.current) {
      isRestoringCompareHistoryRef.current = false;
      return;
    }

    if (compareRestoreWindowOpenRef.current) return;
    if (compareHistoryPostTimerRef.current) {
      clearTimeout(compareHistoryPostTimerRef.current);
    }
    compareHistoryPostTimerRef.current = setTimeout(() => {
      compareHistoryPostTimerRef.current = null;
      window.parent.postMessage(
        {
          source: "compare",
          type: "COMPARE_HISTORY_UPDATE",
          instanceId: compareInstanceId.current,
          messages: message,
        },
        "*",
      );
    }, 80);
    return () => {
      if (compareHistoryPostTimerRef.current) {
        clearTimeout(compareHistoryPostTimerRef.current);
        compareHistoryPostTimerRef.current = null;
      }
    };
  }, [message, compareRestoreWindowClosedTick]);

  // Compare: replay history pushed down by the parent on mount. Only honored
  // once per iframe load and only for non-empty arrays, so first-mount paths
  // that seed messages from botData still win when there's nothing to restore.
  const hasRestoredCompareHistoryRef = useRef(false);
  // Close the restore window: stop holding outgoing history and re-run the
  // emit effect. Idempotent — safe to call from both the restore handler and
  // the grace timer, whichever fires first.
  const closeCompareRestoreWindow = useCallback(() => {
    if (!compareRestoreWindowOpenRef.current) return;
    compareRestoreWindowOpenRef.current = false;
    if (compareRestoreWindowTimerRef.current) {
      clearTimeout(compareRestoreWindowTimerRef.current);
      compareRestoreWindowTimerRef.current = null;
    }
    setCompareRestoreWindowClosedTick((t) => t + 1);
  }, []);
  useEffect(() => {
    if (!COMPARE_MODE) return;
    const handleCompareHistoryRestore = (e: MessageEvent) => {
      if (e.data?.source !== "compare") return;
      if (e.data?.type !== "COMPARE_HISTORY_RESTORE") return;
      if (hasRestoredCompareHistoryRef.current) return;
      const msgs = e.data?.messages;
      if (!Array.isArray(msgs) || msgs.length === 0) return;
      hasRestoredCompareHistoryRef.current = true;
      isRestoringCompareHistoryRef.current = true;
      setMessage(msgs);
      // The restore replaced local state with the saved transcript; further
      // holding would only risk dropping it, so open the outgoing path now.
      closeCompareRestoreWindow();
    };
    window.addEventListener("message", handleCompareHistoryRestore);
    return () =>
      window.removeEventListener("message", handleCompareHistoryRestore);
  }, [closeCompareRestoreWindow]);

  // Compare: open a bounded restore window on mount. If no COMPARE_HISTORY_RESTORE
  // arrives within the grace period (nothing saved, or the parent had no
  // botCustomId yet), close it so normal outgoing persistence resumes.
  useEffect(() => {
    if (!COMPARE_MODE) return;
    compareRestoreWindowTimerRef.current = setTimeout(() => {
      compareRestoreWindowTimerRef.current = null;
      closeCompareRestoreWindow();
    }, 2500);
    return () => {
      if (compareRestoreWindowTimerRef.current) {
        clearTimeout(compareRestoreWindowTimerRef.current);
        compareRestoreWindowTimerRef.current = null;
      }
    };
  }, [closeCompareRestoreWindow]);

  useEffect(() => {
    if (!COMPARE_MODE) return;
    postCompareMessage({
      type: "COMPARE_INPUT_STATUS",
      disabled: isChatInputDisabled || responseLoader,
    });
  }, [isChatInputDisabled, responseLoader]);

  // Pure "is the bot generating" signal for the shared composer's stop button.
  // Unlike COMPARE_INPUT_STATUS this is NOT affected by input-gating flags
  // (isChatInputDisabled), so the stop square reverts to the send
  // arrow as soon as generation actually ends.
  useEffect(() => {
    if (!COMPARE_MODE) return;
    postCompareMessage({
      type: "COMPARE_GENERATING",
      generating: Boolean(responseLoader),
    });
  }, [responseLoader]);

  useEffect(() => {
    if (!COMPARE_MODE) return;
    if (compareBroadcastTrigger === 0) return;
    if (pendingCompareBroadcastRef.current == null) return;
    if (newReply !== pendingCompareBroadcastRef.current.text) return;
    suppressCompareOutgoingRef.current = true;
    try {
      handleSendReplyButton();
    } finally {
      suppressCompareOutgoingRef.current = false;
      pendingCompareBroadcastRef.current = null;
    }
  }, [compareBroadcastTrigger, newReply]);

  // Compare: mirror the draft text out to the parent on every change (typing,
  // paste, IME commit, programmatic clear). Empty string is allowed (so clearing
  // the box clears it everywhere) — but don't broadcast the empty initial state
  // on mount, only changes from a value we've already tracked. Skip the post
  // while we're applying a draft we received from the parent.
  useEffect(() => {
    const prev = newReplyRef.current;
    newReplyRef.current = newReply;
    if (!COMPARE_MODE) return;
    if (isApplyingRemoteCompareInputRef.current) {
      isApplyingRemoteCompareInputRef.current = false;
      return;
    }
    if (prev === newReply) return;
    if (compareInputPostTimerRef.current) {
      clearTimeout(compareInputPostTimerRef.current);
    }
    const text = newReply;
    compareInputPostTimerRef.current = setTimeout(() => {
      compareInputPostTimerRef.current = null;
      postCompareMessage({ type: "COMPARE_INPUT_CHANGE", text });
    }, 40);
    return () => {
      if (compareInputPostTimerRef.current) {
        clearTimeout(compareInputPostTimerRef.current);
        compareInputPostTimerRef.current = null;
      }
    };
  }, [newReply]);

  // Reset textarea states when input is cleared (e.g. after sending a message)
  useEffect(() => {
    if (newReply === "") {
      setIsMultiline(false);
      setHasTextareaScrollbar(false);
      setIsScrolledToBottom(true);
      if (textareaRef.current) {
        textareaRef.current.style.height = "42px";
      }
    }
  }, [newReply]);

  // Compare: apply a mirrored draft pushed from the parent. Just update the
  // text — do not submit, focus-steal, or scroll. Keep the caret at the end.
  useEffect(() => {
    if (!COMPARE_MODE) return;
    const handleCompareBroadcastInput = (e: MessageEvent) => {
      if (e.data?.source !== "compare") return;
      if (e.data?.type !== "COMPARE_BROADCAST_INPUT") return;
      if (typeof e.data.text !== "string") return;
      if (e.data.text === newReplyRef.current) return;
      isApplyingRemoteCompareInputRef.current = true;
      setNewReply(e.data.text);
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea && document.activeElement === textarea) {
          const end = textarea.value.length;
          try {
            textarea.setSelectionRange(end, end);
          } catch {}
        }
        adjustTextareaHeight();
      });
    };
    window.addEventListener("message", handleCompareBroadcastInput);
    return () =>
      window.removeEventListener("message", handleCompareBroadcastInput);
  }, []);

  useEffect(() => {
    if (!COMPARE_MODE) return;
    const handleCompareClickAction = (e: MessageEvent) => {
      if (e.data?.source !== "compare") return;
      if (e.data?.type === "COMPARE_STOP") {
        handleStopRef.current?.();
        return;
      }
      if (e.data?.type !== "COMPARE_CLICK_ACTION") return;
      const action = e.data?.action;
      if (!action || typeof action !== "object") return;

      suppressCompareClickRef.current = true;
      try {
        if (action.kind === "starter" && typeof action.text === "string") {
          void handleStarterQues(action.text);
        }
      } finally {
        suppressCompareClickRef.current = false;
      }
    };
    window.addEventListener("message", handleCompareClickAction);
    return () =>
      window.removeEventListener("message", handleCompareClickAction);
  }, [message]);

  async function handOffConnect(data) {
    const normalizedData = data?.handoffData || data;
    handoffData.current = normalizedData;
    setInhouseAssigneeName(
      normalizedData?.handoffType === "inhouse"
        ? normalizedData?.payload?.assignedUserName || ""
        : "",
    );
    chatHandlerRef.current = normalizedData?.handoffType || "ai";
    clearNudgeTimers();
  }

  function handleAgentConnected(data) {
    const eventConversationId =
      data?.conversationId || data?.handoffData?.payload?.roomId;
    const activeConversationId = roomId || roomID.current;
    if (
      eventConversationId &&
      activeConversationId &&
      String(eventConversationId) !== String(activeConversationId)
    ) {
      return;
    }
    void handOffConnect(data);
  }

  useEffect(() => {
    async function handleScheduleCallConnect(data) {
      const { name, email, config } = data;
      setUserCredential((prev) => ({
        ...prev,
        name,
        email,
      }));
      if (!config.isEnable) return;

      setCalendlyConfig(config);
      showCalendlyBtn.current = true;
    }

    function handleVariableData(data) {
      setScrapedData(mergeData(scrapedData, data));
    }

    async function handleShowSources(data) {
      const { sources, type: sourceType = "vectorDb" } = data;
      // const visitedName = new Set<string>();
      let _dataSource: any[] = [];

      if (lastSourceQuery.current === newReplyLast.current) {
        _dataSource = [...dataSourceRef.current];
      } else {
        lastSourceQuery.current = newReplyLast.current;
        _dataSource = [];
      }

      function extractLogo(content: string): string | undefined {
        const imgRegex = /https?:\/\/[^\s'")]+?\.(png|jpg|jpeg|gif)/gi;
        const matches = content.match(imgRegex);
        if (!matches) return undefined;

        // Prefer images with 'logo' or 'brand' in the path
        const preferred = matches.find((url) => /logo|brand/i.test(url));

        return preferred || matches[0]; // fallback to first image if no match
      }

      if (sources.length) {
        const nameIndexMap = new Map<string, number>();

        // Pre-populate map from existing sources
        _dataSource.forEach((item, index) => {
          if (item.name) {
            nameIndexMap.set(item.name, index);
          }
        });

        for (const data of sources) {
          const raw = data?.name ?? data?.url;
          let name: string = sourceType == "web" ? data?.title : raw;
          let link: string = raw;
          let type: "link" | "txt" = sourceType == "web" ? "link" : "txt";

          if (
            sourceType == "vectorDb" &&
            typeof raw === "string" &&
            raw.startsWith("https")
          ) {
            type = "link";
            const url = new URL(raw);
            const segments = url.pathname.split("/").filter(Boolean);
            name =
              segments.length > 0
                ? segments[segments.length - 1]
                : url.hostname;
            link = raw;
          }

          const logo = extractLogo(data.content || "");

          if (nameIndexMap.has(name)) {
            const existingIndex = nameIndexMap.get(name)!;
            const existingData = _dataSource[existingIndex];

            _dataSource[existingIndex] = {
              ...existingData,
              content: `${existingData.content ?? ""}\n${data.content ?? ""}`,
              link: existingData.link || link,
              logo: existingData.logo || logo,
            };
          } else {
            const newItem = {
              ...data,
              name,
              link,
              type,
              logo,
            };
            nameIndexMap.set(name, _dataSource.length);
            _dataSource.push(newItem);
          }
        }

        setDataSource(_dataSource);
        dataSourceRef.current = _dataSource; // Update ref immediately
      }
    }

    async function handleShowDebugData(data) {
      console.log(data);
    }

    function usingTool(data) {
      const isCurrentRequestAborted =
        queryRequestSignal?.current?.signal?.aborted ?? false;
      if (isCurrentRequestAborted) return;

      console.log("Executing tool:", data);
      if (data?.type === "queryVectorDatabase") {
        setUsingToolLoader(true);
      }
    }

    function handleClearResponseTimeout(data) {
      console.log("Clearing response timeout from backend signal", data);
      if (responseTimer.current) {
        clearTimeout(responseTimer.current);
        responseTimer.current = null;
      }
      handleResponseTimer();
    }

    function handleShowRetry(data) {
      if (queryRequestSignal?.current?.signal?.aborted) return;

      console.log("Retry exhausted: ", data);
      const errMessage = data.errMessage ?? data.message ?? "";
      const shouldShowPlanModelError =
        typeof errMessage === "string" &&
        errMessage.includes("is not allowed with current plan");
      const buildPreviewErrorDetail = (friendly) => {
        const dbg = data?.isPreview ? data?.debugError : null;
        if (!isPreviewModeChat || !dbg) return friendly;
        const parts = [friendly].filter(Boolean);
        if (dbg.message) parts.push(`Error: ${dbg.message}`);
        if (dbg.phase) parts.push(`Phase: ${dbg.phase}`);
        if (dbg.statusCode) parts.push(`Status: ${dbg.statusCode}`);
        if (dbg.stack) parts.push(dbg.stack);
        return parts.join("\n");
      };
      const isAnyProviderError =
        data.forUser || data.isCustomProviderError || data.isProviderError;
      if (isAnyProviderError) {
        // Clear any pending error message timeout since we're showing a specific error
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }
        pendingErrorMessage.current = "";

        setCooldownActive(true);
        setIsCustomProviderError(true);
        setSocketErrorDetail(
          isPreviewModeChat &&
            (shouldShowDetailedSocketError || shouldShowPlanModelError)
            ? buildPreviewErrorDetail(errMessage)
            : "",
        );
        setShowSocketErrorDetail(false);
        setCooldownMessage(
          shouldShowDetailedSocketError && data.errMessage
            ? defaultConnectionIssueMessage
            : data.errorCode == 529 || data.errorCode == 429
              ? ERROR_529[botData?.aiBotConfig?.language?.toLowerCase()] ||
                ERROR_529.english
              : defaultConnectionIssueMessage,
        );
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      if (!data.isCustomProvider) {
        setResponseLoader(false);
        setIsAiResError(true);
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      if (data.canRetry) {
        setCooldownActive(false);
        setCooldownRemainingTime(0);
        setCooldownMessage("");
        setSocketErrorDetail("");
        setShowSocketErrorDetail(false);
        setIsCustomProviderError(false);
      } else if (data.isCustomProvider) {
        setIsCustomProviderError(true);
        setSocketErrorDetail(
          isPreviewModeChat &&
            (shouldShowDetailedSocketError || shouldShowPlanModelError)
            ? buildPreviewErrorDetail(errMessage)
            : "",
        );
        setShowSocketErrorDetail(false);
        setCooldownMessage(
          data.message ||
            WAIT_BEFORE_TRYING_MESSAGE[
              botData?.aiBotConfig?.language?.toLowerCase()
            ] ||
            WAIT_BEFORE_TRYING_MESSAGE.english,
        );
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      trackError(
        new Error(
          `live-calc: error from backend - ${errMessage}, query - ${newReplyLast.current}`,
        ),
      );
    }

    function handleShowReactionOrImprove({ id, preserved, answered }) {
      setResponseCacheOptions({ id, preserved, answered });
    }

    function handleSuggestedQuestions(data) {
      if (data && data.length > 0 && !responseLoader && !usingToolLoader)
        setSuggestedQuestionsData(data);
    }

    function handleCUstomGoalsTriggered(data) {
      customGoalsTriggered.current = true;
      customGoalsMsg.current = data.message;
    }

    socket.on("aiToolCalls", (data) => {
      console.log("Received tool calls:", data);
      pendingToolData.current = {
        toolCalls: data.toolCalls || [],
        toolResults: data.toolResults || [],
      };

      // Immediately update the last assistant message with tool data
      setMessage((prevItems) => {
        const tempMessage = structuredClone(prevItems);
        const lastMessage = tempMessage[tempMessage.length - 1];

        // Only update if the last message is from the assistant
        if (lastMessage && lastMessage.author === 0) {
          lastMessage.toolCalls = data.toolCalls || [];
          lastMessage.toolResults = data.toolResults || [];
          console.log("Attached tool data to message:", {
            toolCalls: lastMessage.toolCalls,
            toolResults: lastMessage.toolResults,
          });
        }

        return tempMessage;
      });
    });

    async function handleHumanAgentMessage(data) {
      const eventConversationId =
        data?.conversationId || data?.handoffData?.payload?.roomId;
      const activeConversationId = roomId || roomID.current;
      if (
        eventConversationId &&
        activeConversationId &&
        String(eventConversationId) !== String(activeConversationId)
      ) {
        return;
      }
      if (chatHandlerRef.current === "ai")
        chatHandlerRef.current = data.agentType;
      if (
        data.agentType === "zendesk" &&
        (!data.authorId ||
          !handoffData.current.payload.zendeskUser?.userId ||
          data.authorId === handoffData.current.payload.zendeskUser.userId)
      ) {
        return;
      }
      const agentMessage = parseMessage(data.message);

      setMessage((prevItems) => [
        ...prevItems,
        {
          author: ai,
          timestamp: new Date(),
          text: agentMessage,
          agent: data.agent,
        },
      ]);
    }

    async function humanAgentDisconnect(data) {
      const eventConversationId =
        data?.conversationId || data?.handoffData?.payload?.roomId;
      const activeConversationId = roomId || roomID.current;
      if (
        eventConversationId &&
        activeConversationId &&
        String(eventConversationId) !== String(activeConversationId)
      ) {
        return;
      }
      chatHandlerRef.current = "ai";
      handoffData.current = {
        ...handoffData.current,
        isConnected: false,
      };
      setTimeout(() => triggerNudge(botData?.aiBotConfig?.nudgeConfig), 0);
      setMessage((prevItems) => [
        ...prevItems,
        {
          author: person,
          timestamp: new Date(),
          text: "Do not connect me to a human agent, do not make any tool calls involving a human agent, and do not mention anything about disconnecting or avoiding a human agent in your response.", //"disconnect me from the human agent and dont make tool call to human agent and even dont mention about that i have dissconnected with agent in the response",
          name: "agent-end",
        },
      ]);
    }

    function handleAiNudgeMessage({
      message,
      action,
      isFinalNudge,
      userResponseId,
    }) {
      setMessage((prev) => [
        ...prev,
        {
          author: ai,
          timestamp: new Date(),
          text: message,
          aiResponse: true,
        },
      ]);

      if (isFinalNudge) setIsLast(true);
    }

    socket.on("showSources", handleShowSources);
    socket.on("ai-nudge-message", handleAiNudgeMessage);

    socket.on("showDebugData", handleShowDebugData);
    socket.on("agent-connected", handleAgentConnected);
    socket.on("human-agent-message", handleHumanAgentMessage);
    socket.on("handoff-disconnect", humanAgentDisconnect);
    socket.on("schedule-call-connect", handleScheduleCallConnect);
    socket.on("variable-data", handleVariableData);
    socket.on("executing-tool", usingTool);
    socket.on("show-retry-exceeded", handleShowRetry);
    showRetryHandlerRef.current = handleShowRetry;
    socket.on("response-cache-options", handleShowReactionOrImprove);
    socket.on("suggested-questions", handleSuggestedQuestions);
    socket.on("cutom-goals-triggered", handleCUstomGoalsTriggered);
    socket.on("cooldown-started", handleCooldownStarted);
    socket.on("cooldown-status", handleCooldownStatus);
    socket.on("clear-response-timeout", handleClearResponseTimeout);

    return () => {
      socket.off("aiToolCalls");
      socket.off("ai-nudge-message", handleAiNudgeMessage);

      socket.off("human-agent-message", handleHumanAgentMessage);
      socket.off("handoff-disconnect", humanAgentDisconnect);
      socket.off("showSources", handleShowSources);
      socket.off("showDebugData", handleShowDebugData);
      socket.off("agent-connected", handleAgentConnected);
      socket.off("schedule-call-connect", handleScheduleCallConnect);
      socket.off("variable-data", handleVariableData);
      socket.off("executing-tool", usingTool);
      socket.off("show-retry-exceeded", handleShowRetry);
      socket.off("response-cache-options", handleShowReactionOrImprove);
      socket.off("suggested-questions", handleSuggestedQuestions);
      socket.off("cutom-goals-triggered", handleCUstomGoalsTriggered);
      socket.off("cooldown-started", handleCooldownStarted);
      socket.off("cooldown-status", handleCooldownStatus);
      socket.off("clear-response-timeout", handleClearResponseTimeout);
    };
  }, [roomId, botData, message, shouldShowDetailedSocketError]);

  const parseMessage = (htmlMessage: string) => {
    const element = document.createElement("div");
    element.innerHTML = htmlMessage;
    return element.textContent;
  };

  const getActiveHumanAgent = async () => {
    let activeHumanAgent: Record<any, any> = {};
    let humanAgentConfig = botData?.aiBotConfig?.humanAgentConfig || {};
    if (typeof humanAgentConfig == "object") {
      Object.keys(humanAgentConfig).forEach((key) => {
        if (
          typeof humanAgentConfig[key] == "object" &&
          humanAgentConfig[key].isEnable
        ) {
          activeHumanAgent = { ...humanAgentConfig[key], agentType: key };
        }
      });
    }
    return activeHumanAgent;
  };

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setShowLoader(false);
  //   }, 800);
  //   return () => clearTimeout(timer);
  // }, []);

  function mergeData(
    preData: Record<string, any>,
    newData: Record<string, any> = {},
  ) {
    const data = { ...preData };

    for (const key in newData) {
      if (key in preData && !newData[key]) continue;
      data[key] = newData[key];
    }

    return data;
  }

  // useEffect(() => {
  //   if (!botCustomId) return;
  //   if (!socket) return;
  //   if (!socket.connected) {
  //     socket.connect();
  //   }
  //   // Join the bot-specific room
  //   socket.emit("join", { roomId: `bot_${botCustomId}` });

  //   // Listen for bot-deleted event
  //   const handleBotDeleted = (data) => {
  //     if (data.botCustomId === botCustomId) {
  //       // Stop chat and redirect to 404
  //       setDisableInput(true);
  //       setTimeout(() => {
  //         router("/error");
  //       }, 100);
  //     }
  //   };
  //   socket.on("bot-deleted", handleBotDeleted);
  //   return () => {
  //     socket.off("bot-deleted", handleBotDeleted);
  //   };
  // }, [botCustomId]);
  const messageBoxBg = chatConfig?.messageBoxBgColor;
  const isComposerStacked = Boolean(pendingImage) || isMultiline;
  const renderPendingImagePreview = () => {
    if (!pendingImage) return null;

    const textColorForMessageBox = getTextColor(messageBoxBg);

    return (
      <div className="col-span-full mb-2 flex !w-full !justify-start !self-stretch px-[15px] pt-2">
        <div
          className="relative !ml-0 !mr-auto h-16 w-16 shrink-0 overflow-hidden rounded-[12px] border"
          style={{
            borderColor: pendingImage.error
              ? "#ef4444"
              : getColorWithAlpha(textColorForMessageBox, 0.25),
          }}
        >
          <img
            src={pendingImage.previewUrl}
            alt={pendingImage.name || "selected"}
            className={`h-full w-full object-cover ${
              pendingImage.isUploading || pendingImage.error ? "opacity-50" : ""
            }`}
          />
          {pendingImage.isUploading && (
            <div className="absolute inset-0 grid place-items-center bg-black/25">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            </div>
          )}
          <button
            type="button"
            onClick={handleRemovePendingImage}
            className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-[#444444E5] text-white transition-colors hover:bg-black"
            aria-label="Remove image"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  if (embed) {
    return (
      <div
        ref={wrapperRef}
        className={`cbb-embed-preview-wrapper preview-container-main embed embed-view-container ${responsive || isMobile ? "cbb-mobile-live" : ""} ${COMPARE_MODE ? "cbb-compare-mode" : ""}`}
        style={{
          fontFamily: chatConfig?.typography?.font?.fontFamily,
          background: chatConfig?.chatBgColor,
          overscrollBehavior: COMPARE_MODE ? "auto" : "contain",
          touchAction: COMPARE_MODE ? "pan-y" : "pan-y pinch-zoom",
          isolation: "isolate",
          contain: "layout style paint",
        }}
      >
        {!COMPARE_MODE && (
          <div
            className={`cbb-embed-header embed-header !gap-[10px]`}
            style={{
              background: chatConfig?.greetingConfig?.headerFooterBgColor,
            }}
          >
            {(showBackBtn || isPreviewModeChat || hasSessionConversation) && (
              <button
                type="button"
                className="cbb-embed-back-btn cp flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-full"
                aria-label="Back to welcome screen"
                onClick={props.onBack}
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="16"
                  viewBox="0 0 9 16"
                  fill="none"
                  style={{ width: 9, height: 16 }}
                >
                  <path
                    d="M8.37616 0.517379C8.48069 0.621058 8.56366 0.744406 8.62028 0.880312C8.6769 1.01622 8.70606 1.16199 8.70606 1.30922C8.70606 1.45645 8.6769 1.60222 8.62028 1.73812C8.56366 1.87403 8.48069 1.99738 8.37616 2.10106L3.26823 7.20899C3.1637 7.31267 3.08073 7.43602 3.02411 7.57192C2.96749 7.70783 2.93833 7.8536 2.93833 8.00083C2.93833 8.14806 2.96749 8.29383 3.02411 8.42974C3.08073 8.56564 3.1637 8.68899 3.26823 8.79267L8.37616 13.9006C8.48069 14.0043 8.56366 14.1276 8.62028 14.2635C8.6769 14.3994 8.70605 14.5452 8.70605 14.6924C8.70605 14.8397 8.6769 14.9854 8.62028 15.1213C8.56366 15.2573 8.48069 15.3806 8.37616 15.4843C8.1672 15.692 7.88453 15.8086 7.5899 15.8086C7.29526 15.8086 7.01259 15.692 6.80363 15.4843L1.68455 10.3652C1.05799 9.73786 0.706055 8.88747 0.706055 8.00083C0.706055 7.11419 1.05799 6.2638 1.68455 5.63646L6.80363 0.517379C7.01259 0.309659 7.29526 0.193065 7.5899 0.193065C7.88453 0.193065 8.1672 0.309659 8.37616 0.517379Z"
                    fill={getTextColor(
                      chatConfig?.greetingConfig?.headerFooterBgColor,
                    )}
                  />
                </svg>
              </button>
            )}
            <div className="cbb-embed-header-container embed-left-item fx al-cnt w-full justify-between gap-[10px] pe-[40px]">
              <div className="cbb-embed-header-content fx items-center gap-[10px]">
                <div
                  className={`cbb-embed-header-logo flex-cnt h-[50px] w-[50px] rounded-[100px] p-[0px]`}
                >
                  {chatConfig?.logo ? (
                    <img
                      src={chatConfig?.logo}
                      alt="boticon"
                      className="cbb-embed-header-logo-image !h-[50px] !w-[50px] flex-shrink-0 !rounded-[100px]"
                      onError={(e) =>
                        applyImageFallback(
                          e.currentTarget,
                          chatConfig?.logoFallbackUrl,
                        )
                      }
                    />
                  ) : fromOutgrow ? (
                    <OutgrowBotIcon />
                  ) : (
                    <BotIconOld />
                  )}
                </div>
                <div className="cbb-embed-header-inner embed-header-inner grid gap-[5px]">
                  <h3
                    className={`cbb-embed-header-title !font-medium`}
                    style={{
                      fontSize:
                        chatConfig?.greetingConfig?.header?.fontSize + "px",
                      color: getTextColor(
                        chatConfig?.greetingConfig?.headerFooterBgColor,
                      ),
                    }}
                  >
                    {chatConfig?.greetingConfig?.header?.text.slice(0, 30)}
                  </h3>
                  {chatConfig?.greetingConfig?.description?.enabled && (
                    <p
                      className={`cbb-embed-header-description`}
                      style={{
                        fontSize:
                          chatConfig?.greetingConfig?.description?.fontSize +
                          "px",
                        color: getTextColor(
                          chatConfig?.greetingConfig?.headerFooterBgColor,
                        ),
                      }}
                    >
                      {chatConfig?.greetingConfig?.description?.text}
                    </p>
                  )}
                </div>
              </div>
              {(isMobileParent ||
                botData?.companyId === "64652848be7f202bf2716ffe") &&
                !pageLocation().href.includes("preview") && (
                  <button
                    className="cbb-mobile-screen-cross-icon grid h-[20px] w-[20px] flex-shrink-0 place-items-center rounded-full"
                    style={{
                      background: deriveShade(
                        chatConfig?.greetingConfig?.headerFooterBgColor,
                      ),
                    }}
                    onClick={() => {
                      window.parent.postMessage({ type: "close" }, "*");
                    }}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.8323 10.0166L19.62 2.22871C20.1268 1.72214 20.1268 0.90305 19.62 0.396534C19.1134 -0.110043 18.2943 -0.110043 17.7878 0.396534L9.99991 8.18433L2.21229 0.396534C1.70547 -0.110043 0.886691 -0.110043 0.380115 0.396534C-0.126705 0.903111 -0.126705 1.72214 0.380115 2.22871L8.16767 10.0166L0.380175 17.8044C-0.126644 18.3109 -0.126644 19.13 0.380175 19.6365C0.500338 19.7571 0.643127 19.8526 0.80034 19.9178C0.957552 19.9829 1.12609 20.0164 1.29627 20.0162C1.62791 20.0162 1.95967 19.8892 2.21236 19.6365L9.99991 11.8488L17.7878 19.6365C17.908 19.757 18.0508 19.8526 18.208 19.9177C18.3652 19.9829 18.5337 20.0163 18.7039 20.0162C19.0356 20.0162 19.3673 19.8892 19.62 19.6365C20.1268 19.13 20.1268 18.3109 19.62 17.8044L11.8323 10.0166Z"
                        fill={getTextColor(
                          deriveShade(
                            chatConfig?.greetingConfig?.headerFooterBgColor,
                          ),
                        )}
                      />
                    </svg>
                  </button>
                )}
            </div>
          </div>
        )}
        <div
          className={`cbb-embed-preview-container previewContainer new-scrollbar embed embed-scroll relative`}
          ref={scrollToButtomRef}
          // style={{ background: chatConfig?.chatBgColor }}
        >
          <div className="cbb-embed-chats-wrapper centered-div embed">
            {/* {showLoader ? (
              <span
                className="cbb-embed-chats-loader before-chat-loader"
                style={{
                  borderTop:
                    chatConfig?.theme === "dark"
                      ? "3px solid white"
                      : "3px solid black",
                }}
              ></span>
            ) : ( */}
            <>
              <div className="cbb-embed-chats-container chatDataContainer">
                {message?.map((msg, currentIndex) => {
                  if (msg.name === "button") {
                    return (
                      <Fragment key={currentIndex}>
                        {msg.author === 0 ? (
                          <BotComponent
                            inhouseAssigneeName={inhouseAssigneeName}
                            isInhouseHandoff={
                              handoffData.current.handoffType === "inhouse"
                            }
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            embed={embed}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleCompareStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            handleReaction={handleReaction}
                            dataSource={dataSource}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            embedConfig={botData?.embedConfig}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            previousChat={props.resumeConversationData}
                            message={message}
                          />
                        )}
                        {!msg.hasAnswered && (
                          <div className="cbb-button-container buttonContainer embed">
                            {msg.options
                              .sort(
                                (a, b) =>
                                  (a.image?.url.length || 0) -
                                  (b.image?.url.length || 0),
                              )
                              .map((value, index) => (
                                <div
                                  className="cbb-question-button questionButton flex-cnt fx-d-col gap-10"
                                  key={index}
                                  onClick={() => {
                                    handleButtonReply(
                                      currentIndex,
                                      value.name,
                                      index,
                                      value.value,
                                    );
                                  }}
                                >
                                  {msg?.showImg && value.image && (
                                    <img src={value.image.url} alt="" />
                                  )}
                                  <div className="cbb-option-title-wrapper option-title-wrapper flex-cnt fx-strt wd-100 gap-10">
                                    <div className="cbb-circle-radio-outer circle-radio-outer">
                                      <div className="cbb-circle-radio-inner circle-radio-inner"></div>
                                    </div>
                                    <p
                                      style={{
                                        fontSize:
                                          chatConfig?.typography?.size + "px",
                                      }}
                                    >
                                      {value.name}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </Fragment>
                    );
                  } else {
                    return (
                      <Fragment key={currentIndex}>
                        {msg.author === 0 ? (
                          <BotComponent
                            inhouseAssigneeName={inhouseAssigneeName}
                            isInhouseHandoff={
                              handoffData.current.handoffType === "inhouse"
                            }
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            botType={botType}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleCompareStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            dataSource={dataSource}
                            handleReaction={handleReaction}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            embedConfig={botData?.embedConfig}
                            embed={embed}
                            responseLoader={responseLoader}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            botType={botType}
                            previousChat={props.resumeConversationData}
                            message={message}
                          />
                        )}
                      </Fragment>
                    );
                  }
                })}
              </div>
              <span ref={scrollRef}></span>
            </>
            {/* )} */}
          </div>

          {showGoToBottom && !responseLoader && (
            <div
              className={`cbb-got-to-bottom-button gotobottom-btn fx sticky bottom-[20px] left-1/2 z-10 h-[36px] w-[36px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-[100px] ${
                chatConfig?.theme === "dark"
                  ? "bg-[#6D6D6D]"
                  : "bg-[#fff] drop-shadow-[0_4px_10px_rgba(0,0,0,0.15)]"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToBottom(e);
              }}
            >
              <GoToBottomIcon
                fill={chatConfig?.theme === "dark" ? "#fff" : "#444"}
              />
            </div>
          )}
        </div>
        <div className={`cbb-chat-screen-footer footer-sec !bg-transparent`}>
          {/* {botData?.enableGemini && <VoiceCoach url={undefined} botCustomId={botData?.botCustomId} />} */}
          {isAiResError && !isCustomProviderError && (
            <div
              className={`cbb-error-msg-wrapper fx py-[10px m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start justify-center gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-[30px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p
                  className={`cbb-error-text py-[5px] text-[12px]`}
                  style={{
                    color: getTextColor(messageBoxBg),
                  }}
                >
                  {defaultConnectionIssueMessage}
                </p>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => handleRegenerate(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                  RETRY_LABEL.english}
              </button>
            </div>
          )}

          {isAiRateLimitError && (
            <div
              className={`cbb-error-msg-wrapper fx py-[10px m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p
                  className={`cbb-error-text py-[5px] text-[12px]`}
                  style={{
                    color: getTextColor(messageBoxBg),
                  }}
                >
                  {isPreviewModeChat || isQuestionLimitReached
                    ? defaultQuestionLimitReachedMessage
                    : defaultConnectionIssueMessage}
                </p>
              </div>
              {!isPreviewModeChat && !isQuestionLimitReached && (
                <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => handleRegenerate(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                    RETRY_LABEL.english}
                </button>
              )}
            </div>
          )}

          {isCustomProviderError && (
            <div
              className={`cbb-error-msg-wrapper fx m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`mt-[5px] h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <div className="flex flex-col !items-start gap-[2px]">
                  <div className="flex items-start justify-between gap-[8px]">
                    <p
                      className={`cbb-error-text text-[12px]`}
                      style={{
                        color: getTextColor(messageBoxBg),
                      }}
                    >
                      {previewSocketErrorMessage}
                    </p>
                    {hasSocketErrorDetail && (
                      <button
                        type="button"
                        className="flex h-[16px] w-[16px] shrink-0 items-center justify-center bg-transparent p-0"
                        style={{ color: getTextColor(messageBoxBg) }}
                        onClick={() =>
                          setShowSocketErrorDetail((prevState) => !prevState)
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d={
                              showSocketErrorDetail
                                ? "M3 7.5L6 4.5L9 7.5"
                                : "M3 4.5L6 7.5L9 4.5"
                            }
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  {hasSocketErrorDetail && showSocketErrorDetail && (
                    <p
                      className="text-[12px]"
                      style={{ color: getTextColor(messageBoxBg) }}
                    >
                      {socketErrorDetail}
                    </p>
                  )}
                </div>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => {
                  handleRegenerate(true);
                  setIsCustomProviderError(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                  className={``}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                  RETRY_LABEL.english}
              </button>
            </div>
          )}

          {isCustomProviderError && !cooldownActive && (
            <div
              className={`cbb-custom-provider-error fx m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`mt-[5px] h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <div className="flex flex-col gap-[2px]">
                  <div className="flex items-start justify-between gap-[8px]">
                    <p
                      className={`text-[12px]`}
                      style={{
                        color: getTextColor(messageBoxBg),
                      }}
                    >
                      {previewSocketErrorMessage}
                    </p>
                    {hasSocketErrorDetail && (
                      <button
                        type="button"
                        className="flex h-[16px] w-[16px] shrink-0 items-center justify-center bg-transparent p-0"
                        style={{ color: getTextColor(messageBoxBg) }}
                        onClick={() =>
                          setShowSocketErrorDetail((prevState) => !prevState)
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d={
                              showSocketErrorDetail
                                ? "M3 7.5L6 4.5L9 7.5"
                                : "M3 4.5L6 7.5L9 4.5"
                            }
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  {hasSocketErrorDetail && showSocketErrorDetail && (
                    <p
                      className="text-[12px]"
                      style={{ color: getTextColor(messageBoxBg) }}
                    >
                      {socketErrorDetail}
                    </p>
                  )}
                </div>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => {
                  handleRegenerate(true);
                  setIsCustomProviderError(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                  className={``}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                Retry
              </button>
            </div>
          )}
          <div className="cbb-chat-bottom branding">
            {renderVoiceErrorBanner()}
            {suggestedQuestionsData.length > 0 && (
              <div className="cbb-suggested-questions suggested-que relative mx-auto w-[50%]">
                {/* <h6
                  style={{
                  }}
                  className={`text-text-clr text-[14px] font-medium ${chatConfig?.theme === "dark" ? "text-[#fff]" : "text-text-clr"}`}
                >
                  Suggestions
                </h6> */}
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={10}
                  slidesPerView="auto"
                  navigation={false}
                  pagination={false}
                  loop={false}
                  speed={600}
                  className="suggested-questions-swiper"
                  style={{
                    paddingInlineStart: "10px",
                    paddingInlineEnd: "10px",
                    paddingTop: `10px`,
                  }}
                  onSlideChange={(swiper) => {
                    if (swiper.isEnd) {
                      swiper.autoplay.stop();
                    }
                  }}
                >
                  {suggestedQuestionsData.map((item, index) => (
                    <SwiperSlide
                      className="cbb-suggested-questions-slide"
                      key={index}
                      style={{
                        width: "auto",
                        height: "auto",
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.2 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: index * 0.1,
                          },
                        }}
                        whileHover={{
                          scale: 1.02,
                          y: -1,
                          backgroundColor: chatConfig?.userBubbleColor,
                          color: getTextColor(chatConfig?.userBubbleColor),
                          border: `1px solid ${chatConfig?.userBubbleColor}`,
                          transition: { duration: 0.3, ease: "easeOut" },
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="cbb-suggested-question cursor-pointer whitespace-nowrap text-nowrap rounded-[100px] border px-[15px] py-[10px]"
                        onClick={() => {
                          const lastIndex = message.length
                            ? message.length - 1
                            : 0;
                          handleUserReply(lastIndex, false, item, false);
                        }}
                        style={{
                          fontSize: chatConfig?.typography?.size + "px",
                          border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                          color: getTextColor(chatConfig?.chatBgColor),
                        }}
                      >
                        {item}
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div
                  style={{
                    background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                  }}
                  className={`cbb-fadeout-div fadeout-div !right-[0] !top-0 !h-full !w-[20px]`}
                ></div>
              </div>
            )}
            <div
              style={{
                opacity: isChatInputDisabled ? "0.3" : "1",
                borderRadius: isComposerStacked ? "24px" : `${borderRadius}px`,
                background: messageBoxBg,
              }}
              className={`tamana cbb-user-container-preview user-container-preview embed !items-end rounded-[100px] ${
                isComposerStacked ? "!flex !flex-col" : ""
              }`}
            >
              {renderPendingImagePreview()}
              {isRecording || isTranscribing ? (
                <div className="cbb-recording-on col-span-full flex w-full items-center justify-between px-4 py-3">
                  <div className="flex flex-1 items-center !justify-start gap-[8px] overflow-hidden">
                    <div className="flex items-center gap-[5px]">
                      <div
                        className={`h-[6px] w-[6px] rounded-full bg-[#D01C24] ${isTranscribing ? "" : "animate-pulse"}`}
                      ></div>
                      <span
                        className="text-sm font-medium tabular-nums"
                        style={{ color: getTextColor(messageBoxBg) }}
                      >
                        {Math.floor(recordingTime / 60)}:
                        {String(recordingTime % 60).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex h-[26px] items-end gap-[3px]">
                      {Array.from({ length: 30 }).map((_, i) => {
                        const shape = Math.abs(
                          Math.sin(i * 0.45) * 0.55 +
                            Math.sin(i * 1.2) * 0.3 +
                            Math.sin(i * 2.5) * 0.15,
                        );
                        const transH = 10 + shape * 5;
                        const minH = 4 + shape * 5;
                        const peak = 12 + shape * 12;
                        const dur = 0.55 + shape * 0.45;
                        return (
                          <motion.div
                            key={i}
                            className="w-[3px] flex-shrink-0 rounded-full"
                            animate={{
                              height: isTranscribing
                                ? transH
                                : [minH, peak, minH],
                            }}
                            transition={{
                              repeat: Infinity,
                              repeatType: "mirror",
                              duration: dur,
                              ease: "easeInOut",
                              delay: i * 0.035,
                            }}
                            style={{
                              backgroundColor: getTextColor(messageBoxBg),
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ps-2">
                    <button
                      onClick={cancelRecording}
                      disabled={isTranscribing}
                      className="rounded-full p-2 transition-colors hover:bg-black/10 disabled:opacity-50"
                      style={{ color: getTextColor(messageBoxBg) }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    {isTranscribing ? (
                      <div className="flex items-center justify-center p-2">
                        <div
                          className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
                          style={{ color: getTextColor(messageBoxBg) }}
                        ></div>
                      </div>
                    ) : (
                      <button
                        onClick={sendAudioForTranscription}
                        disabled={audioData.length === 0}
                        className="rounded-full p-2 transition-colors hover:bg-black/10 disabled:opacity-50"
                        style={{ color: getTextColor(messageBoxBg) }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className={
                    isComposerStacked
                      ? "relative flex w-full items-end"
                      : "contents"
                  }
                  style={{
                    paddingBottom: isMultiline ? "47px" : "0px",
                    paddingInlineStart: isMultiline
                      ? `${STACKED_COMPOSER_INSET - (rtl.isRTL && hasTextareaScrollbar ? TEXTAREA_OVERFLOW_INSET : 0)}px`
                      : "0px",
                    paddingInlineEnd: isMultiline
                      ? `${STACKED_COMPOSER_INSET - (!rtl.isRTL && hasTextareaScrollbar ? TEXTAREA_OVERFLOW_INSET : 0)}px`
                      : "0px",
                    transition:
                      "padding-inline 0.3s ease-in-out, padding-bottom 0.3s ease-in-out",
                  }}
                >
                  {(botData?.aiBotConfig?.enableImage ?? false) && (
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      disabled={
                        isChatInputDisabled ||
                        responseLoader ||
                        isUploadingImage
                      }
                      className={`h-[38px] w-max disabled:opacity-50 ${
                        isComposerStacked
                          ? "absolute z-10"
                          : "mb-[7px] ps-[15px]"
                      }`}
                      style={
                        isComposerStacked
                          ? {
                              color: getTextColor(messageBoxBg),
                              [rtl.isRTL ? "right" : "left"]:
                                `${STACKED_COMPOSER_INSET}px`,
                              bottom: "8px",
                            }
                          : { color: getTextColor(messageBoxBg) }
                      }
                    >
                      {isUploadingImage ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <ImageUploadIcon fill={getTextColor(messageBoxBg)} />
                      )}
                    </button>
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageSelected}
                  />
                  <textarea
                    className={`cbb-user-textarea userInput embed_tarea new-scrollbar my-[5px] me-[5px] !bg-transparent ${
                      isMultiline
                        ? `cbb-multiline !me-0 !w-full transition-all duration-300 ease-in-out`
                        : "!pb-[5px] !pt-[8px]"
                    }`}
                    ref={textareaRef}
                    rows={1}
                    style={{
                      color: getTextColor(messageBoxBg),
                      fontSize: "14px !important",
                      overflowY: isMultiline ? "auto" : "hidden",
                      ...(isMultiline
                        ? {
                            paddingInlineStart:
                              rtl.isRTL && hasTextareaScrollbar
                                ? `${TEXTAREA_SCROLLBAR_GAP}px`
                                : "0px",
                            paddingInlineEnd:
                              !rtl.isRTL && hasTextareaScrollbar
                                ? `${TEXTAREA_SCROLLBAR_GAP}px`
                                : "0px",
                            paddingTop: "0px",
                            paddingBottom: "0px",
                          }
                        : pendingImage
                          ? {
                              paddingInlineStart:
                                (botData?.aiBotConfig?.enableImage ?? false)
                                  ? "48px"
                                  : "16px",
                              paddingInlineEnd: "105px",
                            }
                          : {}),
                    }}
                    placeholder={
                      COMPOSE_MESSAGE_PLACEHOLDER[
                        botData?.aiBotConfig?.language?.toLowerCase()
                      ] || COMPOSE_MESSAGE_PLACEHOLDER.english + "..."
                    }
                    autoFocus={!isMobile}
                    value={newReply}
                    onChange={handleButtomInput}
                    onKeyDown={handleSendViaEnter}
                    onPaste={handleImagePaste}
                    readOnly={isChatInputDisabled || !editable}
                    disabled={isRecording || isTranscribing}
                    onFocus={() => {
                      setEditable(true);
                    }}
                    onTouchStart={() => {
                      if (!isChatInputDisabled) {
                        setEditable(true);
                        setTimeout(() => textareaRef.current?.focus(), 100);
                      }
                    }}
                    onScroll={(e) => {
                      const target = e.currentTarget;
                      setHasUserScrolledTextarea(target.scrollTop > 0);
                      setIsScrolledToBottom(
                        Math.abs(
                          target.scrollHeight -
                            target.scrollTop -
                            target.clientHeight,
                        ) <= 15,
                      );
                    }}
                  />
                  {pendingImage &&
                    isMultiline &&
                    !isScrolledToBottom &&
                    !isTyping &&
                    hasUserScrolledTextarea && (
                      <div
                        className="pointer-events-none absolute left-0 z-10 h-[8px] w-full"
                        style={{
                          bottom: "40px",
                          backdropFilter: "blur(3px)",
                          WebkitBackdropFilter: "blur(3px)",
                          maskImage:
                            "linear-gradient(to bottom, transparent, black)",
                          WebkitMaskImage:
                            "linear-gradient(to bottom, transparent, black)",
                        }}
                      ></div>
                    )}
                  <div
                    className={`flex items-center ${rtl.isRTL ? "gap-[4px]" : "gap-[8px]"} ${isComposerStacked ? "absolute z-10" : "mb-[9px]"}`}
                    style={
                      isComposerStacked
                        ? {
                            [rtl.isRTL ? "left" : "right"]:
                              `${STACKED_COMPOSER_INSET}px`,
                            bottom: "8px",
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`cbb-suggested-cta suggested-cta flex-cnt ${
                        !botData?.aiStarterQues?.data.length ? "none" : ""
                      } ${
                        message[message.length - 1]?.text?.isMandatory
                          ? "opacity-30"
                          : ""
                      }`}
                    >
                      <div
                        className="cbb-suggested-cta-content"
                        onClick={() => {
                          if (message[message.length - 1]?.isLoader) return;
                          setSuggestedQuesAns(true);
                          setStarterQues(botData?.aiStarterQues?.data);
                        }}
                      >
                        <SuggestedQuesAns />
                      </div>
                    </div>
                    {(botData?.aiBotConfig?.enableVoice ?? false) && (
                      <button
                        onClick={handleMicClick}
                        disabled={isChatInputDisabled || responseLoader}
                        style={
                          {
                            "--mic-color": getTextColor(messageBoxBg),
                            "--mic-hover-bg": deriveShade(messageBoxBg, 0.08),
                            "--mic-hover-color": getTextColor(messageBoxBg),
                          } as React.CSSProperties
                        }
                        className="cbb-mic-icon grid h-[32px] w-[32px] place-items-center rounded-full text-[var(--mic-color)] transition-all duration-200 hover:scale-110 hover:bg-[var(--mic-hover-bg)] hover:text-[var(--mic-hover-color)] disabled:opacity-50"
                      >
                        <Tooltip
                          content="Use microphone"
                          anchorSelector=".cbb-mic-icon"
                          placement="top"
                          className="!-mt-[15px] !-translate-x-[4px]"
                        />
                        <MicIcon fill="currentColor" />
                      </button>
                    )}
                    <div
                      className={`cbb-embed-userBtn userBtn embed__send ${isComposerStacked ? "!pe-[0px]" : "pe-[12px]"} `}
                    >
                      <div
                        className="cbb-send-button-wrapper svg-box d !rounded-[100px]"
                        style={{
                          background: `${
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "black"
                              : chatConfig?.botBubbleColor === "#F1F1F1"
                                ? ""
                                : chatConfig?.sendBtnBgColor
                          }`,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          responseLoader
                            ? handleStop()
                            : handleSendReplyButton();
                        }}
                      >
                        <span
                          className="cbb-send-button"
                          style={{
                            color: `${
                              chatConfig?.botBubbleColor === "#F1F1F1"
                                ? "#fff"
                                : getTextColor(chatConfig?.sendBtnBgColor)
                            }`,
                          }}
                        >
                          {responseLoader ? (
                            <svg
                              className="cbb-stop-generation-svg"
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="0.607422"
                                y="0.5"
                                width="14"
                                height="14"
                                rx="4"
                                fill={getTextColor(chatConfig?.sendBtnBgColor)}
                              />
                            </svg>
                          ) : (
                            <svg
                              className="cbb-send-svg"
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                            >
                              <path
                                d="M1.12457 0.888736L15.5045 8.07234C15.7365 8.18823 15.8318 8.47283 15.7175 8.70801C15.6719 8.80185 15.597 8.87778 15.5045 8.92402L1.12487 16.1075C0.892888 16.2234 0.612165 16.1267 0.497855 15.8915C0.447871 15.7886 0.436284 15.6709 0.465235 15.5601L1.88993 10.1057C1.93705 9.92536 2.08403 9.78969 2.26558 9.759L8.70978 8.66968C8.78873 8.65634 8.85401 8.6035 8.88538 8.53167L8.90224 8.47457C8.92046 8.36373 8.85976 8.25804 8.76172 8.21654L8.70978 8.20141L2.22919 7.10639C2.04757 7.0757 1.90054 6.93995 1.85347 6.75949L0.46491 1.436C0.398763 1.18253 0.547815 0.922697 0.797827 0.855636C0.907113 0.826322 1.02317 0.838079 1.12457 0.888736Z"
                                fill={
                                  botData?.url === "omni001-290" ||
                                  botData?.url === "tarun-4" ||
                                  botData?.url === "tarun-25"
                                    ? "white"
                                    : getTextColor(chatConfig?.sendBtnBgColor)
                                }
                              />
                            </svg>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!isReplyValid && showError && error !== "Field is required" && (
              <span className="cbb-small-error-txt small-txt error p-left no-m-tb">
                {error}
              </span>
            )}
            {/* {true && (
              <span className="cbb-small-error-txt small-txt error p-left no-m-tb">
                error mss
              </span>
            )} */}
            {checkValidate && (
              <span className="cbb-small-error-txt small-txt error p-left no-m-tb">
                {error}
              </span>
            )}
          </div>
          {botData?.cookieConsent && consent && (
            <div
              className={`cbb-cookie-consent cookieConsent cookie-popup h-max ${chatConfig?.omniBranding ? "!mb-[-10px]" : ""}`}
              style={{
                background: deriveShade(chatConfig?.chatBgColor),
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: botData?.cookieConsentContent,
                }}
                className={`cbb-cookie-consent-inner cookieConsent-inner bg-transparent`}
                style={{
                  color: getTextColor(deriveShade(chatConfig?.chatBgColor)),
                }}
              />
              <div
                className={`cbb-cookie-btn-wrapper cbb-cookie-btn-wqr btn btn-popup bg-transparent`}
              >
                <button
                  onClick={() => {
                    setConsent(false);
                    window.localStorage.setItem("privacy_policy", "true");
                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=true; expires=${expires.toUTCString()}; path=/`;

                    // Send message to parent window to load GTM
                    try {
                      window.parent.postMessage(
                        {
                          type: "LOAD_GTM",
                          gtmId: "GTM-PS96TMC8",
                          consent: true,
                        },
                        "*",
                      );
                      console.log("GTM load message sent to parent window");
                    } catch (e) {
                      console.log(
                        "Failed to send message to parent, loading in current window",
                      );

                      // Fallback: load in current window
                      const GTM_ID = "GTM-PS96TMC8";
                      if (!document.querySelector(`script[src*="${GTM_ID}"]`)) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          "gtm.start": new Date().getTime(),
                          event: "gtm.js",
                        });

                        const script = document.createElement("script");
                        script.async = true;
                        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
                        script.onload = () =>
                          console.log("GTM loaded in iframe");
                        document.head.appendChild(script);
                      }
                    }
                  }}
                >
                  {ACCEPT_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ACCEPT_MESSAGE.english}
                </button>
                <button
                  className={`cbb-decline-cookie-btn ${chatConfig?.theme === "dark" ? "dark-font" : "light-font"}`}
                  onClick={() => {
                    setConsent(false);

                    // Set declined state
                    window.localStorage.setItem("privacy_policy", "false");
                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=false; expires=${expires.toUTCString()}; path=/`;

                    console.log("Privacy policy declined, GTM will not load");
                  }}
                >
                  {DECLINE_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || DECLINE_MESSAGE.english}
                </button>
              </div>
            </div>
          )}
          {!COMPARE_MODE && (
            <ProtectedBranding
              chatConfig={chatConfig}
              environmentData={environmentData}
              // BrandingLogoCBB={BrandingLogoCBB}
              BrandingLogoOmni={BrandingLogoOmni}
              fromOutgrow={fromOutgrow}
              contactData={localContactData || contactData || null}
              hasUserSentMessage={hasUserSentMessage}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      data-iframe={isInIframe ? "true" : "false"}
      className={`cbb-full-screen-preview-wrapper full-screen-mobile ${responsive || isMobile ? "cbb-mobile-live" : ""} ${COMPARE_MODE ? "cbb-compare-mode" : ""} ${isInIframe ? "cbb-in-iframe" : "cbb-standalone-window"}`}
      style={{
        fontFamily: chatConfig?.typography?.font?.fontFamily,
        overscrollBehavior: COMPARE_MODE ? "auto" : "contain",
        touchAction: COMPARE_MODE ? "pan-y" : "pan-y pinch-zoom",
        isolation: "isolate",
        contain: "layout style paint",
      }}
    >
      <div
        className={`cbb-full-preview-container-main preview-container-main in-page live-bot ${COMPARE_MODE ? "rounded-[20px]" : ""}`}
        style={{
          background: chatConfig?.chatBgColor,
        }}
      >
        {environment === "dev" && (
          <div className={`cbb-preview-head preview-head`}>
            <h1
              className={`cbb-preview-title ${
                chatConfig?.theme === "dark" ? "dark-font" : "light-font"
              }`}
            >
              {botName}
            </h1>
            <div className="cbb-right-head-preview right-head-preview">
              <button
                className="cbb-close-preview-button close-preview-button"
                onClick={() => router(-1)}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.91615 5.00779L9.80995 1.11387C10.0634 0.860582 10.0634 0.451058 9.80995 0.197775C9.55667 -0.0555083 9.14715 -0.0555083 8.89386 0.197775L4.99994 4.09169L1.10614 0.197775C0.85274 -0.0555083 0.443335 -0.0555083 0.190051 0.197775C-0.0633505 0.451058 -0.0633505 0.860582 0.190051 1.11387L4.08385 5.00779L0.190051 8.90171C-0.0633505 9.15499 -0.0633505 9.56451 0.190051 9.8178C0.316278 9.94414 0.482247 10.0076 0.648097 10.0076C0.813947 10.0076 0.979797 9.94414 1.10614 9.8178L4.99994 5.92388L8.89386 9.8178C9.02021 9.94414 9.18606 10.0076 9.35191 10.0076C9.51776 10.0076 9.68361 9.94414 9.80995 9.8178C10.0634 9.56451 10.0634 9.15499 9.80995 8.90171L5.91615 5.00779Z"
                    fill="#FB5F66"
                  />
                </svg>
                Close Preview
              </button>
            </div>
          </div>
        )}
        {!COMPARE_MODE && (
          <div
            className={`cbb-full-preview-header embed-header no-radius`}
            style={{
              background: chatConfig?.greetingConfig?.headerFooterBgColor,
            }}
          >
            {chatConfig?.logo ? (
              <img
                className="cbb-full-preview-logo"
                src={chatConfig?.logo}
                alt="boticon"
                onError={(e) =>
                  applyImageFallback(
                    e.currentTarget,
                    chatConfig?.logoFallbackUrl,
                  )
                }
              />
            ) : fromOutgrow ? (
              <OutgrowBotIcon />
            ) : (
              <BotIconOld />
            )}
            <div className="cbb-full-preview-header-content embed-header-desc grid gap-[5px]">
              <h3
                className={`cbb-full-preview-heading !font-medium`}
                style={{
                  fontSize: chatConfig?.greetingConfig?.header?.fontSize + "px",
                  color: getTextColor(
                    chatConfig?.greetingConfig?.headerFooterBgColor,
                  ),
                }}
              >
                {chatConfig?.greetingConfig?.header?.text.slice(0, 30)}
              </h3>
              {chatConfig?.greetingConfig?.description?.enabled && (
                <p
                  className={`cbb-full-preview-description`}
                  style={{
                    fontSize:
                      chatConfig?.greetingConfig?.description?.fontSize + "px",
                    color: getTextColor(
                      chatConfig?.greetingConfig?.headerFooterBgColor,
                    ),
                  }}
                >
                  {fromOutgrow
                    ? " Enhance your business with Outgrow"
                    : chatConfig?.greetingConfig?.description?.text}
                </p>
              )}{" "}
            </div>
          </div>
        )}
        <div
          className={`cbb-full-preview-container previewContainer new-scrollbar embed fx-d-col relative ${responsive || isMobile ? "embed-scroll" : ""}`}
          ref={scrollToButtomRef}
          style={{
            background: chatConfig?.chatBgColor,
          }}
        >
          <div className="cbb-full-chats-wrapper centered-div">
            <div className="cbb-full-chats-container chatDataContainer">
              <>
                {message?.map((msg, currentIndex) => {
                  if (msg.name === "agent-start") {
                    return (
                      <div className="cbb-full-live-agent-wrapper live-agent-wrapper wd-100 flex items-center gap-[15px]">
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                        <span
                          className={`cbb-full-live-agent-text live-agent-text ${
                            chatConfig?.theme === "dark"
                              ? "dark-font"
                              : "light-font"
                          } flex-cnt w-[500px] text-[11px] uppercase`}
                        >
                          Chat with live agent
                        </span>
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                      </div>
                    );
                  } else if (msg.name === "agent-end") {
                    return (
                      <div className="cbb-full-live-agent-wrapper live-agent-wrapper wd-100 flex items-center gap-[15px]">
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                        <span
                          className={`cbb-full-live-agent-text live-agent-text ${
                            chatConfig?.theme === "dark"
                              ? "dark-font"
                              : "light-font"
                          } flex-cnt w-[500px] text-[11px] uppercase`}
                        >
                          Chat end with live agent
                        </span>
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                      </div>
                    );
                  } else if (msg.name === "button") {
                    return (
                      <div
                        key={currentIndex}
                        className="cbb-msg-container gid gap-[10px]"
                      >
                        {msg.author === 0 ? (
                          <BotComponent
                            inhouseAssigneeName={inhouseAssigneeName}
                            isInhouseHandoff={
                              handoffData.current.handoffType === "inhouse"
                            }
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            embed={embed}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleCompareStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            dataSource={dataSource}
                            handleReaction={handleReaction}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            responseLoader={responseLoader}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            previousChat={props.resumeConversationData}
                            message={message}
                          />
                        )}
                        {!msg.hasAnswered && (
                          <div className="cbb-button-container buttonContainer">
                            {msg.options
                              .sort(
                                (a, b) =>
                                  (a.image?.url.length || 0) -
                                  (b.image?.url.length || 0),
                              )
                              .map((value, index) => (
                                <div
                                  className="cbb-question-button questionButton flex-cnt fx-d-col gap-10"
                                  key={index}
                                  onClick={() => {
                                    handleButtonReply(
                                      currentIndex,
                                      value.name,
                                      index,
                                      value.value,
                                    );
                                  }}
                                >
                                  {msg?.showImg && value.image && (
                                    <img src={value.image.url} alt="" />
                                  )}
                                  <div className="cbb-option-title-wrapper option-title-wrapper flex-cnt fx-strt wd-100 gap-10">
                                    <div className="cbb-circle-radio-outer circle-radio-outer">
                                      <div className="cbb-circle-radio-inner circle-radio-inner"></div>
                                    </div>
                                    <p
                                      style={{
                                        fontSize:
                                          chatConfig?.typography?.size + "px",
                                      }}
                                    >
                                      {value.name}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <Fragment key={currentIndex}>
                        <div className="cbb-msg-inner-wrapper flex flex-col !items-stretch gap-[10px]">
                          {msg.author === 0 ? (
                            <BotComponent
                              inhouseAssigneeName={inhouseAssigneeName}
                              isInhouseHandoff={
                                handoffData.current.handoffType === "inhouse"
                              }
                              reasoning={reasoning}
                              hideClickableOptions={
                                suggestedQuestionsData?.length > 0
                              }
                              ref={botComponentRef}
                              responseCacheOptions={responseCacheOptions}
                              toolLoader={
                                message.length - 1 === currentIndex
                                  ? usingToolLoader
                                  : false
                              }
                              botData={botData}
                              currentIndex={currentIndex}
                              chatConfig={chatConfig}
                              msg={msg}
                              starterQues={starterQues}
                              activeCat={activeCat}
                              handleStarterQues={handleCompareStarterQues}
                              textColor={textColor}
                              suggestedQuesAns={suggestedQuesAns}
                              message={message}
                              setReadPdf={setReadPdf}
                              setReadPdfText={setReadPdfText}
                              dataSource={dataSource}
                              handleReaction={handleReaction}
                              userCredential={userCredential}
                              calendlyConfig={calendlyConfig}
                              handleUserReply={handleUserReply}
                              isAnswerSaved={isAnswerSaved}
                              handleRegenerate={handleRegenerate}
                              responseLoader={responseLoader}
                              previousChat={props.resumeConversationData}
                              customGoalsMsg={customGoalsMsg.current}
                              steps={steps.current}
                              setDisableInput={setDisableInput}
                              isLast={islast}
                              isOverLimit={overLimit}
                            />
                          ) : (
                            <UserComponent
                              isCurrentLastUserMessage={
                                lastUserMsgIndex === currentIndex
                              }
                              currentIndex={currentIndex}
                              chatConfig={chatConfig}
                              msg={msg}
                              textColor={textColor}
                              embed={embed}
                              botType={botType}
                              previousChat={props.resumeConversationData}
                              message={message}
                            />
                          )}
                        </div>
                      </Fragment>
                    );
                  }
                })}
                <span ref={scrollRef}></span>
              </>
            </div>
          </div>
          {showGoToBottom && !responseLoader && (
            <div
              className={`cbb-got-to-bottom-button gotobottom-btn fx sticky bottom-[20px] left-1/2 h-[36px] w-[36px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-[100px] ${
                chatConfig?.theme === "dark"
                  ? "bg-[#6D6D6D]"
                  : "bg-[#fff] drop-shadow-[0_4px_10px_rgba(0,0,0,0.15)]"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToBottom(e);
              }}
            >
              <GoToBottomIcon
                fill={chatConfig?.theme === "dark" ? "#fff" : "#444"}
              />
            </div>
          )}
        </div>
        <div className="cbb-chat-screen-footer footer-sec !bg-transparent">
          {isAiResError && !isCustomProviderError && (
            <div className="cbb-error-msg-wrapper mt-3 flex w-full items-center justify-center">
              <div
                className={`cbb-retry-error-msg retry-error-msg fx w-1/2 items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
                style={{ background: messageBoxBg }}
              >
                <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`mt-[5px] h-[16px] w-[16px]`}
                  >
                    <g clipPath="url(#clip0_3826_34009)">
                      <path
                        d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3826_34009">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.607422)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p
                    className={`cbb-error-text text-[12px]`}
                    style={{
                      color: getTextColor(messageBoxBg),
                    }}
                  >
                    {defaultConnectionIssueMessage}
                  </p>
                </div>
                <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => handleRegenerate(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    className={``}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                    RETRY_LABEL.english}
                </button>
              </div>
            </div>
          )}

          {isAiRateLimitError && (
            <div className="cbb-error-msg-wrapper mt-3 flex w-full items-center justify-center">
              <div
                className={`cbb-retry-error-msg retry-error-msg fx w-1/2 items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
                style={{ background: messageBoxBg }}
              >
                <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`mt-[5px] h-[16px] w-[16px]`}
                  >
                    <g clipPath="url(#clip0_3826_34009)">
                      <path
                        d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3826_34009">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.607422)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p
                    className={`cbb-error-text text-[12px]`}
                    style={{
                      color: getTextColor(messageBoxBg),
                    }}
                  >
                    {defaultQuestionLimitReachedMessage}
                  </p>
                </div>
                {/* <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => handleRegenerate(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    className={``}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                    RETRY_LABEL.english}
                </button> */}
              </div>
            </div>
          )}

          {isCustomProviderError && (cooldownActive || !isAiResError) && (
            <div className="cbb-error-msg-wrapper mt-3 flex w-full items-center justify-center">
              <div
                className={`cbb-retry-error-msg retry-error-msg fx w-1/2 items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
                style={{ background: messageBoxBg }}
              >
                <div className="cbb-error-msg-content grid grid-cols-[auto,1fr] items-start gap-[6px]">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`mt-[5px] h-[16px] w-[16px]`}
                  >
                    <g clipPath="url(#clip0_3826_34009)">
                      <path
                        d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3826_34009">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.607422)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <div className="flex flex-col !items-start gap-[2px]">
                    <div className="flex items-start justify-between gap-[8px]">
                      <p
                        className={`cbb-error-text text-[12px]`}
                        style={{
                          color: getTextColor(messageBoxBg),
                        }}
                      >
                        {previewSocketErrorMessage}
                      </p>
                      {hasSocketErrorDetail && (
                        <button
                          type="button"
                          className="flex h-[16px] w-[16px] shrink-0 items-center justify-center bg-transparent p-0"
                          style={{ color: getTextColor(messageBoxBg) }}
                          onClick={() =>
                            setShowSocketErrorDetail((prevState) => !prevState)
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d={
                                showSocketErrorDetail
                                  ? "M3 7.5L6 4.5L9 7.5"
                                  : "M3 4.5L6 7.5L9 4.5"
                              }
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    {hasSocketErrorDetail && showSocketErrorDetail && (
                      <p
                        className="text-[12px]"
                        style={{ color: getTextColor(messageBoxBg) }}
                      >
                        {socketErrorDetail}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => {
                    handleRegenerate(true);
                    setIsCustomProviderError(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    className={``}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  Retry
                </button>
              </div>
            </div>
          )}
          <div
            className={`cbb-chat-bottom wd-50 branding ${
              chatConfig?.theme === "dark" ? " " : " "
            }`}
          >
            {/* {botData?.enableGemini && (
              <VoiceCoach url={undefined} botCustomId={botData?.botCustomId} />
            )} */}
            {/* suggested que and ans */}
            {renderVoiceErrorBanner()}
            {suggestedQuestionsData.length > 0 && (
              <div className="cbb-suggested-questions suggested-que relative mx-auto w-[50%]">
                {/* <h6
                 
                  className={`mb-[10px] text-[14px] font-medium ${chatConfig?.theme === "dark" ? `dark-font` : `text-[#444]`}`}
                >
                  Suggestions
                </h6> */}
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={10}
                  slidesPerView="auto"
                  navigation={false}
                  pagination={false}
                  loop={false}
                  speed={600}
                  className="cbb-suggested-questions-swiper suggested-questions-swiper"
                  style={{
                    paddingInlineStart: "10px",
                    paddingInlineEnd: "10px",
                    paddingTop: `10px`,
                  }}
                  onSlideChange={(swiper) => {
                    if (swiper.isEnd) {
                      swiper.autoplay.stop();
                    }
                  }}
                >
                  {suggestedQuestionsData.map((item, index) => (
                    <SwiperSlide
                      className="cbb-suggested-questions-slide"
                      key={index}
                      style={{
                        width: "auto",
                        height: "auto",
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.2 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: index * 0.1,
                          },
                        }}
                        whileHover={{
                          scale: 1.02,
                          y: -1,
                          backgroundColor: chatConfig?.userBubbleColor,
                          color: getTextColor(chatConfig?.userBubbleColor),
                          border: `1px solid ${chatConfig?.userBubbleColor}`,
                          transition: { duration: 0.3, ease: "easeOut" },
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="cbb-suggested-question cursor-pointer whitespace-nowrap text-nowrap rounded-[100px] border px-[15px] py-[10px]"
                        onClick={() => {
                          const lastIndex = message.length
                            ? message.length - 1
                            : 0;
                          handleUserReply(lastIndex, false, item, false);
                        }}
                        style={{
                          fontSize: chatConfig?.typography?.size + "px",
                          border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                          color: getTextColor(chatConfig?.chatBgColor),
                        }}
                      >
                        {item}
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div
                  style={{
                    background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                  }}
                  className={`cbb-fadeout-div fadeout-div !right-[0] !top-0 !h-full !w-[56px]`}
                ></div>
              </div>
            )}
            <div
              style={{
                opacity: isChatInputDisabled ? "0.3" : "1",
                borderRadius: isComposerStacked ? "24px" : `${borderRadius}px`,
                background: messageBoxBg,
              }}
              className={`cbb-user-container-preview user-container-preview wd-50 embed in-page-test !items-end pe-[14px] ${
                isComposerStacked ? "!flex !flex-col" : ""
              }`}
            >
              {renderPendingImagePreview()}
              {isRecording || isTranscribing ? (
                <div className="cbb-recording-on col-span-full flex w-full items-center justify-between px-4 py-3">
                  <div className="flex flex-1 items-center !justify-start gap-[8px] overflow-hidden">
                    <div className="flex items-center gap-[5px]">
                      <div
                        className={`h-[6px] w-[6px] rounded-full bg-[#D01C24] ${isTranscribing ? "" : "animate-pulse"}`}
                      ></div>
                      <span
                        className="text-sm font-medium tabular-nums"
                        style={{ color: getTextColor(messageBoxBg) }}
                      >
                        {Math.floor(recordingTime / 60)}:
                        {String(recordingTime % 60).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex h-[26px] items-end gap-[3px]">
                      {Array.from({ length: 30 }).map((_, i) => {
                        const shape = Math.abs(
                          Math.sin(i * 0.45) * 0.55 +
                            Math.sin(i * 1.2) * 0.3 +
                            Math.sin(i * 2.5) * 0.15,
                        );
                        const transH = 10 + shape * 5;
                        const minH = 4 + shape * 5;
                        const peak = 12 + shape * 12;
                        const dur = 0.55 + shape * 0.45;
                        return (
                          <motion.div
                            key={i}
                            className="w-[3px] flex-shrink-0 rounded-full"
                            animate={{
                              height: isTranscribing
                                ? transH
                                : [minH, peak, minH],
                            }}
                            transition={{
                              repeat: Infinity,
                              repeatType: "mirror",
                              duration: dur,
                              ease: "easeInOut",
                              delay: i * 0.035,
                            }}
                            style={{
                              backgroundColor: getTextColor(messageBoxBg),
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ps-2">
                    <button
                      onClick={cancelRecording}
                      disabled={isTranscribing}
                      className="rounded-full p-2 transition-colors hover:bg-black/10 disabled:opacity-50"
                      style={{ color: getTextColor(messageBoxBg) }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    {isTranscribing ? (
                      <div className="flex items-center justify-center p-2">
                        <div
                          className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
                          style={{ color: getTextColor(messageBoxBg) }}
                        ></div>
                      </div>
                    ) : (
                      <button
                        onClick={sendAudioForTranscription}
                        disabled={audioData.length === 0}
                        className="rounded-full p-2 transition-colors hover:bg-black/10 disabled:opacity-50"
                        style={{
                          background: `${
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "black"
                              : chatConfig?.botBubbleColor === "#F1F1F1"
                                ? ""
                                : chatConfig?.botBubbleColor
                          }`,
                        }}
                      >
                        <svg
                          width="16"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "white"
                              : chatConfig?.typography?.color || "currentColor"
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className={
                    isComposerStacked
                      ? "relative flex w-full items-end"
                      : "contents"
                  }
                  style={{
                    paddingBottom: isMultiline ? "49px" : "0px",
                    paddingInlineStart: isMultiline
                      ? `${STACKED_COMPOSER_INSET - (rtl.isRTL && hasTextareaScrollbar ? TEXTAREA_OVERFLOW_INSET : 0)}px`
                      : "0px",
                    paddingInlineEnd: isMultiline
                      ? `${STACKED_COMPOSER_INSET - (!rtl.isRTL && hasTextareaScrollbar ? TEXTAREA_OVERFLOW_INSET : 0)}px`
                      : "0px",
                    transition:
                      "padding-inline 0.3s ease-in-out, padding-bottom 0.3s ease-in-out",
                  }}
                >
                  {(botData?.aiBotConfig?.enableImage ?? false) && (
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      disabled={
                        isChatInputDisabled ||
                        responseLoader ||
                        isUploadingImage
                      }
                      className={`h-[38px] w-max disabled:opacity-50 ${
                        isComposerStacked
                          ? "absolute z-10"
                          : "mb-[9px] ps-[15px]"
                      }`}
                      style={
                        isComposerStacked
                          ? {
                              color: getTextColor(messageBoxBg),
                              [rtl.isRTL ? "right" : "left"]:
                                `${STACKED_COMPOSER_INSET}px`,
                              bottom: "10px",
                            }
                          : { color: getTextColor(messageBoxBg) }
                      }
                    >
                      {isUploadingImage ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <ImageUploadIcon fill={getTextColor(messageBoxBg)} />
                      )}
                    </button>
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageSelected}
                  />
                  <textarea
                    ref={textareaRef}
                    className={`cbb-user-textarea userInput textBox cbb-full-screen-textarea new-scrollbar me-[5px] !bg-transparent ${
                      isMultiline
                        ? `cbb-multiline !me-0 !w-full transition-all duration-300 ease-in-out`
                        : ""
                    }`}
                    rows={1}
                    style={{
                      color: getTextColor(messageBoxBg),
                      fontSize: "14px !important",
                      overflowY: isMultiline ? "auto" : "hidden",
                      ...(isMultiline
                        ? {
                            paddingInlineStart:
                              rtl.isRTL && hasTextareaScrollbar
                                ? `${TEXTAREA_SCROLLBAR_GAP}px`
                                : "0px",
                            paddingInlineEnd:
                              !rtl.isRTL && hasTextareaScrollbar
                                ? `${TEXTAREA_SCROLLBAR_GAP}px`
                                : "0px",
                            paddingTop: "0px",
                            paddingBottom: "0px",
                          }
                        : pendingImage
                          ? {
                              paddingInlineStart:
                                (botData?.aiBotConfig?.enableImage ?? false)
                                  ? "48px"
                                  : "16px",
                              paddingInlineEnd: "105px",
                            }
                          : {}),
                    }}
                    placeholder={
                      COMPOSE_MESSAGE_PLACEHOLDER[
                        botData?.aiBotConfig?.language?.toLowerCase()
                      ] || COMPOSE_MESSAGE_PLACEHOLDER.english + "..."
                    }
                    autoFocus={
                      !isMobile &&
                      pageLocation().hostname.includes("chatbotbuilder.net")
                    }
                    value={newReply}
                    onChange={handleButtomInput}
                    onKeyDown={handleSendViaEnter}
                    onPaste={handleImagePaste}
                    readOnly={isChatInputDisabled}
                    disabled={isRecording || isTranscribing}
                    onScroll={(e) => {
                      const target = e.currentTarget;
                      setHasUserScrolledTextarea(target.scrollTop > 0);
                      setIsScrolledToBottom(
                        Math.abs(
                          target.scrollHeight -
                            target.scrollTop -
                            target.clientHeight,
                        ) <= 15,
                      );
                    }}
                  />
                  {pendingImage &&
                    isMultiline &&
                    !isScrolledToBottom &&
                    !isTyping &&
                    hasUserScrolledTextarea && (
                      <div
                        className="pointer-events-none absolute left-0 z-10 h-[8px] w-full"
                        style={{
                          bottom: "40px",
                          backdropFilter: "blur(3px)",
                          WebkitBackdropFilter: "blur(3px)",
                          maskImage:
                            "linear-gradient(to bottom, transparent, black)",
                          WebkitMaskImage:
                            "linear-gradient(to bottom, transparent, black)",
                        }}
                      ></div>
                    )}
                  <div
                    className={`flex items-center ${rtl.isRTL ? "gap-[4px]" : "gap-[8px]"} ${isComposerStacked ? "absolute z-10" : "mb-[8px]"}`}
                    style={
                      isComposerStacked
                        ? {
                            [rtl.isRTL ? "left" : "right"]:
                              `${STACKED_COMPOSER_INSET}px`,
                            bottom: "8px",
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`cbb-suggested-cta suggested-cta flex-cnt mb-[7px] h-full ${
                        !botData?.aiStarterQues?.data.length ? "none" : ""
                      } ${
                        message[message.length - 1]?.text?.isMandatory
                          ? "opacity-30"
                          : ""
                      }`}
                    >
                      <div
                        className="cbb-suggested-cta-content"
                        onClick={() => {
                          if (message[message.length - 1]?.isLoader) return;
                          setSuggestedQuesAns(true);
                          setStarterQues(botData?.aiStarterQues?.data);
                        }}
                      >
                        <SuggestedQuesAns />
                      </div>
                    </div>
                    {(botData?.aiBotConfig?.enableVoice ?? false) && (
                      <button
                        onClick={handleMicClick}
                        disabled={isChatInputDisabled || responseLoader}
                        style={
                          {
                            "--mic-color": getTextColor(messageBoxBg),
                            "--mic-hover-bg": deriveShade(messageBoxBg, 0.08),
                            "--mic-hover-color": getTextColor(messageBoxBg),
                          } as React.CSSProperties
                        }
                        className="cbb-mic-icon grid h-[32px] w-[32px] place-items-center rounded-full text-[var(--mic-color)] transition-all duration-200 hover:scale-110 hover:bg-[var(--mic-hover-bg)] hover:text-[var(--mic-hover-color)] disabled:opacity-50"
                      >
                        <Tooltip
                          content="Use microphone"
                          anchorSelector=".cbb-mic-icon"
                          placement="top"
                          className="!-mt-[15px] !-translate-x-[4px]"
                        />
                        <MicIcon fill="currentColor" />
                      </button>
                    )}
                    <div
                      className={`cbb-full-userBtn userBtn hhhh btn-text-area h-full ${isComposerStacked ? "!pe-[0px]" : "pe-[12px]"} `}
                    >
                      <div
                        className="cbb-send-button-wrapper svg-box"
                        style={{
                          background: `${
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "black"
                              : chatConfig?.botBubbleColor === "#F1F1F1"
                                ? ""
                                : chatConfig?.sendBtnBgColor
                          }`,
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          responseLoader
                            ? handleStop()
                            : handleSendReplyButton();
                        }}
                      >
                        <span
                          className="cbb-send-button"
                          style={{
                            color: `${
                              chatConfig?.botBubbleColor === "#F1F1F1"
                                ? "#fff"
                                : getTextColor(chatConfig?.sendBtnBgColor)
                            }`,
                          }}
                        >
                          {responseLoader ? (
                            <svg
                              className="cbb-stop-generation-svg"
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="0.607422"
                                y="0.5"
                                width="14"
                                height="14"
                                rx="4"
                                fill={getTextColor(chatConfig?.sendBtnBgColor)}
                              />
                            </svg>
                          ) : (
                            <svg
                              className="cbb-send-svg"
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="17"
                              viewBox="0 0 16 17"
                              fill="none"
                            >
                              <path
                                d="M1.12457 0.888736L15.5045 8.07234C15.7365 8.18823 15.8318 8.47283 15.7175 8.70801C15.6719 8.80185 15.597 8.87778 15.5045 8.92402L1.12487 16.1075C0.892888 16.2234 0.612165 16.1267 0.497855 15.8915C0.447871 15.7886 0.436284 15.6709 0.465235 15.5601L1.88993 10.1057C1.93705 9.92536 2.08403 9.78969 2.26558 9.759L8.70978 8.66968C8.78873 8.65634 8.85401 8.6035 8.88538 8.53167L8.90224 8.47457C8.92046 8.36373 8.85976 8.25804 8.76172 8.21654L8.70978 8.20141L2.22919 7.10639C2.04757 7.0757 1.90054 6.93995 1.85347 6.75949L0.46491 1.436C0.398763 1.18253 0.547815 0.922697 0.797827 0.855636C0.907113 0.826322 1.02317 0.838079 1.12457 0.888736Z"
                                fill={
                                  botData?.url === "omni001-290" ||
                                  botData?.url === "tarun-4" ||
                                  botData?.url === "tarun-25"
                                    ? "white"
                                    : getTextColor(chatConfig?.sendBtnBgColor)
                                }
                              />
                            </svg>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!isReplyValid && showError && error !== "Field is required" && (
              <span className="cbb-small-error-txt small-txt error check-error no-m-tb mt-10">
                {error}
              </span>
            )}
          </div>
          {botData?.cookieConsent && consent && (
            <div
              className={`cbb-cookie-consent cookieConsent ${chatConfig?.omniBranding ? "!mb-[-10px]" : ""}`}
              style={{
                background: deriveShade(chatConfig?.chatBgColor),
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: botData?.cookieConsentContent,
                }}
                className={`cbb-cookie-consent-inner cookieConsent-inner`}
                style={{
                  color: getTextColor(deriveShade(chatConfig?.chatBgColor)),
                }}
              />
              <div className={`cbb-cookie-btn-wrapper btn bg-transparent`}>
                <button
                  className={`cbb-accept-cookie-btn`}
                  onClick={() => {
                    console.log("=== ACCEPT BUTTON CLICKED ===");

                    // Hide consent popup first
                    setConsent(false);

                    window.localStorage.setItem("privacy_policy", "true");
                    console.log(
                      "localStorage set:",
                      window.localStorage.getItem("privacy_policy"),
                    );

                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=true; expires=${expires.toUTCString()}; path=/`;

                    loadGTMNow();
                  }}
                >
                  {ACCEPT_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ACCEPT_MESSAGE.english}
                </button>
                <button
                  className={`cbb-decline-cookie-btn`}
                  onClick={() => {
                    setConsent(false);
                    deleteCookies("privacy_policy");
                    window.localStorage.removeItem("privacy_policy");
                    setCookie("privacy_policy", "false");
                  }}
                >
                  {DECLINE_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || DECLINE_MESSAGE.english}
                </button>
              </div>
            </div>
          )}
        </div>
        {!COMPARE_MODE && (
          <ProtectedBranding
            chatConfig={chatConfig}
            environmentData={environmentData}
            // BrandingLogoCBB={BrandingLogoCBB}
            BrandingLogoOmni={BrandingLogoOmni}
            fromOutgrow={fromOutgrow}
            contactData={localContactData || contactData || null}
            hasUserSentMessage={hasUserSentMessage}
          />
        )}
      </div>
    </div>
  );
});

export default Preview;

interface Row {
  key: string;
  value: any;
  testValue: string;
}

const BrandingLogoOmni = () => (
  <svg
    width={29}
    height={28}
    viewBox="0 0 29 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_2085_10186)">
      <path
        d="M22.718 10.8422C23.9774 10.2899 25.4734 10.8631 25.7644 12.2072C26.2009 14.2231 26.1325 16.3294 25.5467 18.3341C24.7263 21.1414 22.9439 23.5701 20.512 25.1947C18.0802 26.8192 15.154 27.5357 12.2466 27.2186C9.33926 26.9015 6.63632 25.571 4.61181 23.4605C2.58729 21.3499 1.37036 18.594 1.17446 15.676C0.978559 12.758 1.81617 9.86416 3.54041 7.50188C5.26467 5.13965 7.7655 3.4598 10.6044 2.75699C12.6317 2.25504 14.7391 2.27424 16.7351 2.79422C18.0659 3.14088 18.5764 4.65938 17.9722 5.89478C17.368 7.13012 15.867 7.59383 14.502 7.42712C13.606 7.31773 12.6908 7.37075 11.8012 7.591C10.0979 8.01272 8.59739 9.02061 7.56286 10.438C6.52828 11.8553 6.02575 13.5916 6.14327 15.3424C6.26079 17.0932 6.99094 18.7468 8.20566 20.0131C9.42037 21.2794 11.0421 22.0777 12.7865 22.268C14.531 22.4583 16.2867 22.0283 17.7458 21.0536C19.2049 20.0789 20.2744 18.6216 20.7665 16.9373C21.0236 16.0577 21.1147 15.1455 21.0426 14.2457C20.9328 12.8749 21.4586 11.3944 22.718 10.8422Z"
        fill="#FB5F66"
      />
      <path
        d="M27.2799 6.87081C26.8448 7.21147 25.5525 6.72189 24.1552 6.07874C23.0413 6.50435 22.321 6.26301 23.8277 7.88542C24.9258 9.06768 21.7416 8.92601 19.9035 10.7641C17.5372 13.2177 18.5705 18.7253 15.6631 14.1324C14.5676 12.4018 16.1237 17.6853 15.3855 17.9002C14.4066 18.065 14.5265 16.8718 14.1757 14.0777C14.0084 12.7458 13.8104 13.9541 13.5726 15.4165C14.59 17.8493 14.2542 16.2574 13.5286 17.139L13.3172 16.8999C13.1407 17.8116 12.9453 18.494 12.7283 18.2771C12.2273 17.7759 11.721 15.1833 11.0408 14.3259L10.9492 14.2224C10.9801 14.2533 11.0106 14.2879 11.0408 14.3259L13.3172 16.8999C13.4072 16.4354 13.4922 15.9114 13.5726 15.4165C13.2627 14.6752 12.827 13.5602 12.2385 11.8877C11.6122 9.40722 15.8552 10.8004 17.5817 9.42265C19.3083 8.04494 19.9668 6.25264 19.6963 4.42994C19.7303 3.80576 22.0608 5.11479 24.1552 6.07874C24.5117 5.94249 24.9085 5.73797 25.2856 5.38205C25.5541 5.12852 25.7666 4.85554 25.9041 4.56289L23.5358 0.590332C25.914 2.17858 26.4033 3.50021 25.9041 4.56289L27.2799 6.87081Z"
        fill="#444444"
      />
      <path
        d="M26.8215 1.85207C25.0928 0.123352 22.29 0.123353 20.5613 1.85207C18.8326 3.58079 18.8326 6.38359 20.5613 8.11231C22.29 9.84103 25.0928 9.84103 26.8215 8.11231C28.5502 6.38359 28.5502 3.58079 26.8215 1.85207Z"
        fill="#444444"
      />
      <path
        d="M17.51 11.1642C15.4356 9.08974 12.0722 9.08974 9.99776 11.1642C7.9233 13.2387 7.9233 16.602 9.99776 18.6765C12.0722 20.7509 15.4356 20.7509 17.51 18.6765C19.5845 16.602 19.5845 13.2387 17.51 11.1642Z"
        fill="#444444"
      />
    </g>
    <defs>
      <clipPath id="clip0_2085_10186">
        <rect
          width={27.6862}
          height={27.6862}
          fill="white"
          transform="translate(0.701172)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const BrandingLogoCBB = ({ color }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.2374 7.32417L11.2362 7.34163V7.35914C11.2362 7.89245 11.1281 8.22884 10.9809 8.44996C10.8369 8.6663 10.6214 8.82361 10.3049 8.93664C9.97956 9.05282 9.56787 9.11458 9.06559 9.14387C8.62435 9.1696 8.14337 9.16916 7.61923 9.16868C7.54762 9.16862 7.47522 9.16855 7.40199 9.16855C7.32877 9.16855 7.25636 9.16862 7.18476 9.16868C6.66062 9.16916 6.17964 9.1696 5.7384 9.14387C5.23612 9.11458 4.82442 9.05282 4.49909 8.93664C4.18261 8.82361 3.96708 8.6663 3.82306 8.44996C3.67585 8.22884 3.56776 7.89245 3.56776 7.35914C3.56776 5.2349 5.291 3.48182 7.40199 3.48182C9.55907 3.48182 11.3861 5.13159 11.2374 7.32417Z"
      stroke={color}
      strokeWidth="1.03395"
    />
    <path
      d="M2.75549 3.99891C-0.0153649 8.08562 2.67627 11.0943 4.85986 10.9699C4.91074 10.967 4.95452 11.0067 4.95452 11.0576V12.9632C4.95452 13.0406 5.04861 13.0787 5.10251 13.0232L7.04553 11.0213C7.08165 10.9841 7.13302 10.9652 7.18471 10.9695C9.88113 11.1931 12.0041 10.6999 12.7894 9.15648C13.7737 7.22207 12.8324 4.84835 11.8847 3.81439C9.9029 1.05719 4.95449 0.755614 2.75549 3.99891Z"
      stroke="url(#paint0_linear_2541_12675)"
      strokeWidth="1.20628"
    />
    <ellipse
      cx="5.81734"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="7.44039"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="9.06343"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <defs>
      <linearGradient
        id="paint0_linear_2541_12675"
        x1="3.35457"
        y1="1.91882"
        x2="7.26257"
        y2="13.1757"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FF898E" />
        <stop offset="0.546875" stopColor="#EA0D17" />
        <stop offset="1" stopColor="#FB5F66" />
      </linearGradient>
    </defs>
  </svg>
);

const SuggestedQuesAns = () => (
  <svg
    className="cp"
    xmlns="http://www.w3.org/2000/svg"
    width={30}
    height={30}
    viewBox="0 0 30 30"
    fill="none"
  >
    <path
      d="M7.08984 15.8789C7.49637 15.8789 7.87699 15.7788 8.22117 15.6138C7.88461 15.2699 7.88514 14.7196 8.22621 14.3786C8.56729 14.0375 9.11754 14.037 9.46143 14.3735C9.62648 14.0293 9.72656 13.6487 9.72656 13.2422C9.72656 11.7882 8.54379 10.6055 7.08984 10.6055C5.6359 10.6055 4.45312 11.7882 4.45312 13.2422C4.45312 14.6961 5.6359 15.8789 7.08984 15.8789Z"
      fill="#7A7A7A"
    />
    <path
      d="M2.69531 22.9102H3.57422V23.7891C3.57422 24.274 3.18023 24.668 2.69531 24.668C2.20951 24.668 1.81641 25.0611 1.81641 25.5469C1.81641 26.0327 2.20951 26.4258 2.69531 26.4258H4.45312C6.57574 26.4258 8.35072 24.9134 8.75924 22.9102H11.4844C12.9383 22.9102 14.1211 21.7274 14.1211 20.2734V6.21094C14.1211 4.75699 12.9383 3.57422 11.4844 3.57422H2.69531C1.24137 3.57422 0 4.75699 0 6.21094V20.2734C0 21.7274 1.24137 22.9102 2.69531 22.9102ZM7.08984 8.84766C9.51287 8.84766 11.4844 10.8192 11.4844 13.2422C11.4844 14.1363 11.2129 14.967 10.7522 15.6618L11.2269 16.1364C11.5702 16.4797 11.5702 17.036 11.2269 17.3793C10.8836 17.7226 10.3274 17.7226 9.98408 17.3793L9.50941 16.9046C8.81461 17.3652 7.98398 17.6367 7.08984 17.6367C4.66682 17.6367 2.69531 15.6652 2.69531 13.2422C2.69531 10.8192 4.66682 8.84766 7.08984 8.84766ZM22.1351 13.2422H23.6852L22.9102 11.6921L22.1351 13.2422Z"
      fill="#7A7A7A"
    />
    <path
      d="M27.3047 3.57422H18.5156C17.0617 3.57422 15.8789 4.75699 15.8789 6.21094V20.2734C15.8789 21.7274 17.0617 22.9102 18.5156 22.9102H21.2408C21.6493 24.9134 23.4243 26.4258 25.5469 26.4258H27.3047C27.7905 26.4258 28.1836 26.0327 28.1836 25.5469C28.1836 25.0611 27.7905 24.668 27.3047 24.668C26.8198 24.668 26.4258 24.274 26.4258 23.7891V22.9102H27.3047C28.7586 22.9102 30 21.7274 30 20.2734V6.21094C30 4.75699 28.7586 3.57422 27.3047 3.57422ZM26.8189 17.544C26.6103 17.6481 26.369 17.6652 26.1479 17.5915C25.9268 17.5178 25.7439 17.3593 25.6396 17.1509L24.5641 15H21.2562L20.1808 17.1509C19.9637 17.5843 19.4341 17.7612 19.0015 17.544C18.5672 17.3269 18.3912 16.799 18.6084 16.3647L22.124 9.33346C22.4227 8.73779 23.3977 8.73779 23.6964 9.33346L27.2121 16.3647C27.4291 16.799 27.2532 17.3269 26.8189 17.544Z"
      fill="#7A7A7A"
    />
  </svg>
);

const GoToBottomIcon = ({ fill }) => (
  <svg
    className="cbb-go-to-bottom-icon"
    width={16}
    height={10}
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99997 9.12047C7.71322 9.12047 7.4265 9.01098 7.20788 8.79246L0.328227 1.91274C-0.109409 1.4751 -0.109409 0.765553 0.328227 0.328094C0.765686 -0.109365 1.4751 -0.109365 1.91277 0.328094L7.99997 6.41565L14.0872 0.328307C14.5249 -0.109152 15.2342 -0.109152 15.6716 0.328307C16.1095 0.765765 16.1095 1.47532 15.6716 1.91295L8.79207 8.79268C8.57334 9.01123 8.28662 9.12047 7.99997 9.12047Z"
      fill={fill}
    />
  </svg>
);

const MicIcon = ({ fill }) => (
  <svg
    className="cbb-mic"
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.5 5C12.5 3.61929 11.3807 2.5 10 2.5C8.61929 2.5 7.5 3.61929 7.5 5V9.16667C7.5 10.5474 8.61929 11.6667 10 11.6667C11.3807 11.6667 12.5 10.5474 12.5 9.16667V5Z"
      stroke={fill}
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.8333 9.16666C15.8333 12.3883 13.2217 15 10 15C6.77835 15 4.16667 12.3883 4.16667 9.16666"
      stroke={fill}
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 15V17.5"
      stroke={fill}
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ImageUploadIcon = ({ fill }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_277_4533)">
      <path
        d="M5.99998 14.6667H9.99998C13.3333 14.6667 14.6666 13.3333 14.6666 10V6.00001C14.6666 2.66668 13.3333 1.33334 9.99998 1.33334H5.99998C2.66665 1.33334 1.33331 2.66668 1.33331 6.00001V10C1.33331 13.3333 2.66665 14.6667 5.99998 14.6667Z"
        stroke={fill}
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.00002 6.66667C6.73642 6.66667 7.33335 6.06971 7.33335 5.33333C7.33335 4.59695 6.73642 4 6.00002 4C5.26364 4 4.66669 4.59695 4.66669 5.33333C4.66669 6.06971 5.26364 6.66667 6.00002 6.66667Z"
        stroke={fill}
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.77997 12.6334L5.06663 10.4267C5.5933 10.0734 6.3533 10.1134 6.82662 10.5201L7.04662 10.7134C7.56662 11.1601 8.40662 11.1601 8.92662 10.7134L11.7 8.33341C12.22 7.88675 13.06 7.88675 13.58 8.33341L14.6666 9.26675"
        stroke={fill}
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_277_4533">
        <rect width={16} height={16} fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const DeleteIcon = ({ fill }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M13 3.66666L12.5869 10.3501C12.4813 12.0576 12.4285 12.9114 12.0005 13.5253C11.7889 13.8287 11.5165 14.0849 11.2005 14.2773C10.5614 14.6667 9.706 14.6667 7.99513 14.6667C6.28208 14.6667 5.42553 14.6667 4.78603 14.2766C4.46987 14.0838 4.19733 13.8272 3.98579 13.5232C3.55792 12.9084 3.5063 12.0534 3.40307 10.3435L3 3.66666"
      stroke="#FB5F66"
      strokeLinecap="round"
    />
    <path
      d="M2 3.66668H14M10.7038 3.66668L10.2487 2.72783C9.9464 2.10418 9.7952 1.79236 9.53447 1.59788C9.47667 1.55474 9.4154 1.51637 9.35133 1.48314C9.0626 1.33334 8.71607 1.33334 8.023 1.33334C7.31253 1.33334 6.95733 1.33334 6.66379 1.48942C6.59873 1.52402 6.53665 1.56394 6.47819 1.60879C6.21443 1.81114 6.06709 2.13438 5.77241 2.78085L5.36861 3.66668"
      stroke="#FB5F66"
      strokeLinecap="round"
    />
    <path d="M6.33337 11V7" stroke="#FB5F66" strokeLinecap="round" />
    <path d="M9.66663 11V7" stroke="#FB5F66" strokeLinecap="round" />
  </svg>
);
